import { Content } from 'pdfmake/interfaces';
import { DynamicTemplateConfig } from '../../models/dynamic-template-config.interface';
import { InvoiceData } from '../../models/invoice-data.interface';
import { getItemUnitName, getSelectedMappingLines } from '../helpers/item-display.helper';
import { getInvoiceName } from '../helpers/invoice-name.helper';
import { isCapitalFormEnabledFor } from '../../../../app/config/feature-flags';

/**
 * Helper class to build PDF sections dynamically based on JSON configuration
 */
export class DynamicSectionBuilder {
  /** Tamil Unicode range regex: U+0B80–U+0BFF */
  private static TAMIL_REGEX = /[\u0B80-\u0BFF]/;

  /** Font scale factor for footer sections (1.0 = normal, <1.0 = compressed) */
  private footerFontScale = 1.0;

  constructor(
    private config: DynamicTemplateConfig,
    private invoiceData: InvoiceData,
  ) {
    // MANUAL OVERRIDE: Uncomment the line below to force spacing regardless of UI settings
    this.config.layoutSettings.sectionSpacing = 5;
  }

  /**
   * Set the font scale factor for footer sections.
   * Used when footer content is too tall and needs compression to avoid overlapping items table.
   * @param scale 1.0 = normal, <1.0 = compressed (min 0.65)
   */
  setFooterFontScale(scale: number): void {
    this.footerFontScale = Math.max(0.65, Math.min(1.0, scale));
  }

  /**
   * Apply footer font scale to a font size value.
   * Returns the scaled font size (minimum 5pt to keep text readable).
   */
  private scaledFontSize(baseFontSize: number): number {
    if (this.footerFontScale >= 1.0) return baseFontSize;
    return Math.max(5, Math.round(baseFontSize * this.footerFontScale));
  }

  /**
   * A4 "Capital Form" layout option (Mobile industry only): returns a copy of a business /
   * party / shipping object with its name and address fields in upper case. Other industries,
   * other page sizes, and A4 with the option off get the original object back unchanged.
   */
  private applyCapitalForm<T>(source: T): T {
    // Industry is the one IndustryService stored at login
    const isA4CapitalForm = this.config.pageSettings?.pageSize === 'A4'
      && this.config.layoutSettings?.capitalForm === true
      && isCapitalFormEnabledFor(localStorage.getItem('pos_industry'));
    if (!isA4CapitalForm || !source) return source;

    const result: any = { ...source };
    ['name', 'address', 'city', 'state', 'pincode'].forEach(key => {
      if (typeof result[key] === 'string') result[key] = result[key].toUpperCase();
    });
    return result;
  }

  /**
   * Apply footer font scale to a margin/spacing value.
   * Returns the scaled value (minimum 0).
   */
  private scaledMargin(baseMargin: number): number {
    if (this.footerFontScale >= 1.0) return baseMargin;
    return Math.max(0, Math.round(baseMargin * this.footerFontScale));
  }

  /**
   * Recursively scale all fontSize and margin/spacing values in a pdfmake content tree.
   * Used to compress footer sections when they would overflow the available space.
   * Only applies when footerFontScale < 1.0.
   */
  private applyFooterScaling(content: any): any {
    if (this.footerFontScale >= 1.0 || !content) return content;

    if (Array.isArray(content)) {
      return content.map(item => this.applyFooterScaling(item));
    }

    if (typeof content !== 'object') return content;

    const scaled = { ...content };

    // Scale fontSize
    if (typeof scaled.fontSize === 'number') {
      scaled.fontSize = this.scaledFontSize(scaled.fontSize);
    }

    if (Array.isArray(scaled.margin)) {
      scaled.margin = scaled.margin.map((m: number) => this.scaledMargin(m));
    }

    if (Array.isArray(scaled.fit)) {
      scaled.fit = scaled.fit.map((f: number) => Math.max(10, Math.round(f * this.footerFontScale)));
    }

    if (scaled.stack) scaled.stack = this.applyFooterScaling(scaled.stack);
    if (scaled.columns) scaled.columns = this.applyFooterScaling(scaled.columns);
    if (scaled.table && scaled.table.body) {
      scaled.table = { ...scaled.table };
      scaled.table.body = scaled.table.body.map((row: any[]) =>
        row.map((cell: any) => this.applyFooterScaling(cell))
      );
    }
    if (scaled.text && Array.isArray(scaled.text)) {
      scaled.text = scaled.text.map((t: any) =>
        typeof t === 'object' ? this.applyFooterScaling(t) : t
      );
    }
    if (scaled.ol) scaled.ol = this.applyFooterScaling(scaled.ol);
    if (scaled.ul) scaled.ul = this.applyFooterScaling(scaled.ul);

    return scaled;
  }

  private getFontForText(text: string): string {
    if (text && DynamicSectionBuilder.TAMIL_REGEX.test(text)) {
      return 'NotoSansTamil';
    }
    return this.config.themeSettings.fontFamily || 'Roboto';
  }


  private getVisibleColumnsBasedOnData(tableConfig: any): any[] {
    const allPossibleColumns = [
      {
        key: 'sno',
        label: 'S.No',
        width: 'auto',
        align: 'center',
        dataKey: null,
        alwaysShow: true,
      },
      {
        key: 'name',
        label: 'Description',
        width: '*',
        align: 'left',
        dataKey: 'name',
        alwaysShow: true,
      },
      {
        key: 'hsn',
        label: 'HSN/SAC',
        width: 'auto',
        align: 'center',
        dataKey: 'hsn',
      },
      {
        key: 'batch',
        label: 'Batch',
        width: 'auto',
        align: 'center',
        dataKey: 'batch',
      },
      {
        key: 'quantity',
        label: 'Qty',
        width: 'auto',
        align: 'center',
        dataKey: 'quantity',
        alwaysShow: true,
      },
      {
        key: 'unit',
        label: 'Unit',
        width: 'auto',
        align: 'center',
        dataKey: 'unit',
      },
      {
        key: 'mrp',
        label: 'Mrp',
        width: 'auto',
        align: 'right',
        dataKey: 'mrp',
        alwaysShow: true,
      },
      {
        key: 'price',
        label: 'Rate',
        width: 'auto',
        align: 'right',
        dataKey: 'price',
        alwaysShow: true,
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 'auto',
        align: 'right',
        dataKey: 'amount',
      },
      {
        key: 'discount',
        label: 'Discount Amt',
        width: 'auto',
        align: 'right',
        dataKey: 'discount',
      },
      {
        key: 'discountPercent',
        label: 'Discount %',
        width: 'auto',
        align: 'center',
        dataKey: 'discountPercent',
      },
      {
        key: 'taxableAmount',
        label: 'Taxable Amount',
        width: 'auto',
        align: 'right',
        dataKey: 'taxableAmount',
      },
      {
        key: 'taxPercent',
        label: 'Tax %',
        width: 'auto',
        align: 'center',
        dataKey: 'taxPercent',
      },
      {
        key: 'taxAmount',
        label: 'Tax Amt',
        width: 'auto',
        align: 'right',
        dataKey: 'taxAmount',
      },
      {
        key: 'total',
        label: 'Total Amount',
        width: 'auto',
        align: 'right',
        dataKey: 'total',
        alwaysShow: true,
      },
    ];

    if (!this.invoiceData.items || this.invoiceData.items.length === 0) {
      return allPossibleColumns.filter((col) => col.alwaysShow);
    }

    const visibleColumns: any[] = [];

    for (const colDef of allPossibleColumns) {
      if (colDef.alwaysShow) {
        visibleColumns.push(colDef);
        continue;
      }
      const hasData = this.invoiceData.items.some((item: any) => {
        if (!colDef.dataKey) return false;

        let value = item[colDef.dataKey];

        if (value === null || value === undefined) {
          switch (colDef.dataKey) {
            case 'hsn':
              value = item.hsncode;
              break;
            case 'discount':
            case 'discountAmount':
            case 'discountPercent':
              value = item.discount || item.discountAmount || item.discountamt || item.discountPercentage || item.discountPercent;
              break;
            case 'taxPercent':
              value = item.taxRate || item.tax;
              break;
            case 'taxAmount':
              value = item.taxamount;
              break;
            case 'unit':
              // unit may only be available via product.units.name
              value = getItemUnitName(item);
              break;
          }
        }


        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (
          typeof value === 'number' &&
          value === 0 &&
          !['discount', 'discountPercent', 'discountAmount'].includes(
            colDef.dataKey,
          )
        )
          return false;

        return true;
      });

      if (hasData) {
        visibleColumns.push(colDef);
      }
    }

    if (tableConfig.columns && tableConfig.columns.length > 0) {
      const configuredColumns = tableConfig.columns.filter(
        (col: any) => col.show,
      );

      const mergedColumns: any[] = [];

      for (const configCol of configuredColumns) {
        const hasData = visibleColumns.some((vc) => vc.key === configCol.key);

        // Always include columns that the config says show: true
        // The config is the source of truth — if show is true, include it
        // itemNote is not a standalone column — it renders below item name
        if (configCol.key === 'itemNote') {
          continue;
        }
        if (configCol.key === 'discount' || configCol.key === 'discountAmount' || configCol.key === 'discountPercent' || configCol.key === 'discount%') {
          // Include the discount column as configured — config is source of truth
          mergedColumns.push({
            ...configCol,
            label: configCol.label || (configCol.key.includes('Percent') || configCol.key.includes('%') ? 'Discount %' : 'Discount'),
          });
        } else {
          mergedColumns.push({
            ...configCol,
            label:
              configCol.label ||
              visibleColumns.find((vc) => vc.key === configCol.key)?.label ||
              configCol.key,
          });
        }
      }

      if (mergedColumns.length > 0) {
        return mergedColumns;
      }
    }

    return visibleColumns;
  }

  private buildUnifiedHsnAndTotalsTable(summaryRows: any[][], summaryLayout: any): any {
    const hsnConfig = this.config.hsnBreakup;
    const currencySymbol = this.config.additionalSettings?.currencySymbol || '₹';
    const theme = this.config.themeSettings;

    if (!hsnConfig) return { text: '' };
    const hsnHeader1: any[] = [];
    const hsnHeader2: any[] = [];
    const hsnWidths: any[] = [];
    if (hsnConfig.showHSNSAC !== false) {
      hsnHeader1.push({ text: hsnConfig.hsnSACLabel || 'HSN/SAC', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center', rowSpan: 2 });
      hsnHeader2.push({});
      hsnWidths.push('auto');
    }
    if (hsnConfig.showTaxableValue !== false) {
      hsnHeader1.push({ text: hsnConfig.taxableValueLabel || 'Taxable Value', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center', rowSpan: 2 });
      hsnHeader2.push({});
      hsnWidths.push('auto');
    }
    if (hsnConfig.showCGST !== false) {
      hsnHeader1.push({ text: hsnConfig.cgstLabel || 'CGST', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center', colSpan: 2 });
      hsnHeader1.push({});
      hsnHeader2.push({ text: '%', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center' });
      hsnHeader2.push({ text: 'Amt', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center' });
      hsnWidths.push('auto'); hsnWidths.push('auto');
    }
    if (hsnConfig.showSGST !== false) {
      hsnHeader1.push({ text: hsnConfig.sgstLabel || 'SGST', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center', colSpan: 2 });
      hsnHeader1.push({});
      hsnHeader2.push({ text: '%', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center' });
      hsnHeader2.push({ text: 'Amt', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center' });
      hsnWidths.push('auto'); hsnWidths.push('auto');
    }
    if (hsnConfig.showIGST !== false) {
      hsnHeader1.push({ text: hsnConfig.igstLabel || 'IGST', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center', colSpan: 2 });
      hsnHeader1.push({});
      hsnHeader2.push({ text: '%', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center' });
      hsnHeader2.push({ text: 'Amt', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center' });
      hsnWidths.push('auto'); hsnWidths.push('auto');
    }
    if (hsnConfig.showTotalTax !== false) {
      hsnHeader1.push({ text: hsnConfig.totalTaxLabel || 'Total Tax', fillColor: hsnConfig.headerStyle?.backgroundColor, color: hsnConfig.headerStyle?.textColor, fontSize: 7, bold: true, alignment: 'center', rowSpan: 2 });
      hsnHeader2.push({});
      hsnWidths.push('auto');
    }

    const breakupData = this.invoiceData?.hsnBreakup || [];
    // console.log(this.invoiceData)
    const hsnDataRows = breakupData.map((hsn: any) => {
      const row: any[] = [];
      if (hsnConfig.showHSNSAC !== false) row.push({ text: hsn.hsnCode || '', fontSize: 7, alignment: 'center' });
      if (hsnConfig.showTaxableValue !== false) {
        const displayTaxable = this.invoiceData.discaftertax
          ? this.invoiceData.items.reduce((sum: number, item: any) => {
            const itemHsn = item.hsncode || item.hsn;
            if (itemHsn === hsn.hsnCode) {
              const itemPrice = item.price || 0;
              const itemQty = item.quantity || 0;
              return sum + itemPrice * itemQty;
            } return sum;
          }, 0)
          : (hsn.taxableValue || 0);
        row.push({ text: this.formatCurrency(displayTaxable, currencySymbol), fontSize: 7, alignment: 'right' });
      }
      // if (hsnConfig.showTaxableValue !== false) row.push({ text: this.formatCurrency(hsn.taxableValue || 0, currencySymbol), fontSize: 7, alignment: 'right' });
      if (hsnConfig.showCGST !== false) {
        row.push({ text: `${hsn.cgstPercent || 0}%`, fontSize: 7, alignment: 'center' });
        row.push({ text: this.formatCurrency(hsn.cgstAmount || 0, currencySymbol), fontSize: 7, alignment: 'right' });
      }
      if (hsnConfig.showSGST !== false) {
        row.push({ text: `${hsn.sgstPercent || 0}%`, fontSize: 7, alignment: 'center' });
        row.push({ text: this.formatCurrency(hsn.sgstAmount || 0, currencySymbol), fontSize: 7, alignment: 'right' });
      }
      if (hsnConfig.showIGST !== false) {
        row.push({ text: `${hsn.igstPercent || 0}%`, fontSize: 7, alignment: 'center' });
        row.push({ text: this.formatCurrency(hsn.igstAmount || 0, currencySymbol), fontSize: 7, alignment: 'right' });
      }
      if (hsnConfig.showTotalTax !== false) row.push({ text: this.formatCurrency(hsn.totalTax || 0, currencySymbol), fontSize: 7, alignment: 'right' });
      return row;
    });
    const hsnTotalRow: any[] = [];
    // const totalTaxableValue = breakupData.reduce((sum: number, hsn: any) => sum + (hsn.taxableValue || 0), 0);
    const totalTaxableValue = breakupData.reduce((sum: number, hsn: any) =>
      sum + (this.invoiceData.discaftertax ?
        this.invoiceData.items.reduce((itemSum: number, item: any) => {
          const itemHsn = item.hsncode || item.hsn;
          if (itemHsn === hsn.hsnCode) {
            const itemPrice = item.price || 0;
            const itemQty = item.quantity || 0;
            return itemSum + itemPrice * itemQty;
          }
          return itemSum;
        }, 0) : (hsn.taxableValue || 0)), 0
    );
    const totalCGST = breakupData.reduce((sum: number, hsn: any) => sum + (hsn.cgstAmount || 0), 0);
    const totalSGST = breakupData.reduce((sum: number, hsn: any) => sum + (hsn.sgstAmount || 0), 0);
    const totalIGST = breakupData.reduce((sum: number, hsn: any) => sum + (hsn.igstAmount || 0), 0);
    const totalTax = breakupData.reduce((sum: number, hsn: any) => sum + (hsn.totalTax || 0), 0);

    if (hsnConfig.showHSNSAC !== false) hsnTotalRow.push({ text: 'Total', fontSize: 7, bold: true, alignment: 'center' });
    if (hsnConfig.showTaxableValue !== false) hsnTotalRow.push({ text: this.formatCurrency(totalTaxableValue, currencySymbol), fontSize: 7, bold: true, alignment: 'right' });
    if (hsnConfig.showCGST !== false) {
      hsnTotalRow.push({ text: '', fontSize: 7 });
      hsnTotalRow.push({ text: this.formatCurrency(totalCGST, currencySymbol), fontSize: 7, bold: true, alignment: 'right' });
    }
    if (hsnConfig.showSGST !== false) {
      hsnTotalRow.push({ text: '', fontSize: 7 });
      hsnTotalRow.push({ text: this.formatCurrency(totalSGST, currencySymbol), fontSize: 7, bold: true, alignment: 'right' });
    }
    if (hsnConfig.showIGST !== false) {
      hsnTotalRow.push({ text: '', fontSize: 7 });
      hsnTotalRow.push({ text: this.formatCurrency(totalIGST, currencySymbol), fontSize: 7, bold: true, alignment: 'right' });
    }
    if (hsnConfig.showTotalTax !== false) hsnTotalRow.push({ text: this.formatCurrency(totalTax, currencySymbol), fontSize: 7, bold: true, alignment: 'right' });
    const hsnColCount = hsnWidths.length;
    const allHsnRows = [hsnHeader1, hsnHeader2, ...hsnDataRows, hsnTotalRow];
    const totalUnifiedRows = Math.max(allHsnRows.length, summaryRows.length);
    const finalBody: any[][] = [];

    for (let i = 0; i < totalUnifiedRows; i++) {
      const hsnRow = allHsnRows[i] || Array(hsnColCount).fill({ text: '', border: [false, false, false, false] });
      const summaryRow = summaryRows[i] || Array(2).fill({ text: '' });

      finalBody.push([...hsnRow, ...summaryRow]);
    }

    return this.applyFooterScaling({
      table: {
        widths: [...hsnWidths, '*', 100],
        body: finalBody,
      },
      layout: {
        hLineWidth: (i: any, node: any) => summaryLayout?.hLineWidth ? summaryLayout.hLineWidth(i, node) : 0.5,
        vLineWidth: (i: any, node: any) => summaryLayout?.vLineWidth ? summaryLayout.vLineWidth(i, node) : 0.5,
        hLineColor: (i: any, node: any) => summaryLayout?.hLineColor ? summaryLayout.hLineColor(i, node) : '#000000',
        vLineColor: (i: any, node: any) => summaryLayout?.vLineColor ? summaryLayout.vLineColor(i, node) : '#000000',
      },
      margin: [0, 0, 0, 0],
    });
  }

  buildBusinessSection(): Content {
    const businessConfig = this.config.businessDetails;
    // console.log(this.invoiceData, 'businessConfig')
    const theme = this.config.themeSettings;
    const pageSettings = this.config.pageSettings;
    const headerLayout = this.config.layoutSettings.headerLayout || 'logo-left';

    // console.log(this.invoiceData.business, 'invoiceData.business')
    const business = this.applyCapitalForm(this.invoiceData.business || {
      name: this.invoiceData.businessName || '-',
      address: (this.invoiceData.businessAddress) || '-',
      phone: this.invoiceData.businessPhone || '-',
      email: this.invoiceData.businessEmail || '-',
      gstin: this.invoiceData.businessGSTIN || '-',
      website: '-',

    });

    let logoElement: any = null;
    if (businessConfig.showLogo) {
      const logoData = (business.logo !== undefined && business.logo !== null) ? business.logo : businessConfig.logoBase64;
      if (logoData && typeof logoData === 'string' && logoData.trim() !== '') {
        logoElement = {
          image: logoData,
          width: 50,
          alignment: businessConfig.logoPosition || 'left',
          margin: [0, 0, 0, 0],
        };
      }
    }

    // ═══ A5 LANDSCAPE SPECIFIC LAYOUT ═══
    // Top Row: Title (BILL OF SUPPLY + Original for receipt) left, Logo right
    // Below Row: All business details centered one by one
    // if (pageSettings.pageSize === 'A5' && pageSettings.orientation === 'landscape') {
    //   return this.buildA5LandscapeBusinessSection(business, businessConfig, theme, logoElement);
    // }

    if (pageSettings.pageSize === 'A5' && pageSettings.orientation === 'portrait') {
      return this.buildA5PortraitBusinessSection(business, businessConfig, theme, logoElement);
    }

    const businessDetailsContent: any[] = [];
    const isThermal = pageSettings.pageSize === 'THERMAL';
    if (businessConfig.showBusinessName) {
      businessDetailsContent.push({
        text: business.name,
        font: this.getFontForText(business.name),
        fontSize: isThermal ? businessConfig.businessNameFontSize + 4 : businessConfig.businessNameFontSize,
        bold: true,
        color: theme.primaryColor,
        margin: [0, 0, 0, 0],
      });
    }

    if (businessConfig.showTagline) {
      const taglineText = businessConfig.tagline || business.tagline;
      if (taglineText) {
        businessDetailsContent.push({
          text: taglineText,
          fontSize: isThermal ? theme.baseFontSize + 1 : theme.baseFontSize - 1,
          color: theme.secondaryColor,
          margin: [0, 0, 0, 2],
        });
      }
    }

    if (businessConfig.showAddress) {
      let addressText = business.address || '-';

      if (business.city || business.state || business.pincode) {
        const addressParts = [
          business.address,
          business.city,
          business.state,
          business.pincode,
        ].filter((part) => part && part.trim());
        addressText = addressParts.join(', ');
      }

      businessDetailsContent.push({
        text: addressText,
        font: this.getFontForText(addressText),
        fontSize: isThermal ? theme.baseFontSize + 1 : theme.baseFontSize - 1,
        color: theme.textColor,
        margin: [0, 0, 0, 2],
      });
    }
    const contactRow: any[] = [];

    if (businessConfig.showPhone) {
      const phoneLabel = 'Ph:';
      const phoneValue = business.phone || '-';
      contactRow.push({ text: phoneValue != '-' ? `${phoneLabel} ${phoneValue}` : '', width: 'auto' });
    }

    if (businessConfig.showEmail) {
      const emailLabel = businessConfig.emailLabel || 'E-Mail:';
      const emailValue = business.email || '';
      contactRow.push({ text: emailValue != '-' ? `${emailLabel} ${emailValue}` : '', width: 'auto' });
    }

    if (businessConfig.showWebsite) {
      const websiteValue = business.website || '-';
      contactRow.push({ text: websiteValue != '-' ? `Website: ${websiteValue}` : '', width: 'auto' });
    }


    if (contactRow.length > 0) {
      const isThermal = pageSettings.pageSize === 'THERMAL';
      const isCenteredLayout = !logoElement ||
        headerLayout === 'logo-center' ||
        headerLayout === 'logo-left-text-center' ||
        headerLayout === 'logo-right-text-center';
      if (isThermal) {
        contactRow.forEach((item) => {
          businessDetailsContent.push({
            text: item.text,
            fontSize: theme.baseFontSize + 1,
            color: theme.textColor,
            margin: [0, 0, 0, 2],
          });
        });
      } else {
        businessDetailsContent.push({
          columns: contactRow.map((item) => ({
            ...item,
            ...(isCenteredLayout ? { width: '*', alignment: 'center' } : {}),
          })),
          columnGap: 5,
          fontSize: theme.baseFontSize - 1,
          color: theme.textColor,
          margin: [0, 0, 0, 2],
        });
      }
    }

    // Show MSME No and MSME Type below contact row — A4/A5 custom templates
    const isA4OrA5 =
      pageSettings.pageSize === 'A4' || pageSettings.pageSize === 'A5';
    if (isA4OrA5) {
      const clientSaleData = (() => {
        try {
          return JSON.parse(localStorage.getItem('client_sale_data') || '{}');
        } catch {
          return {};
        }
      })();

      const msmeNoValue =
        business.msmeNo ||
        (business as any).msmeno ||
        (business as any).msme_no ||
        this.invoiceData.business?.msmeNo ||
        (this.invoiceData.business as any)?.msmeno ||
        (this.invoiceData.business as any)?.msme_no ||
        clientSaleData?.msmeno ||
        clientSaleData?.msme_no ||
        clientSaleData?.msmeNo ||
        localStorage.getItem('msmeno') ||
        '';

      const msmeTypeValue =
        business.msmeType ||
        (business as any).msmetype ||
        (business as any).msme_type ||
        this.invoiceData.business?.msmeType ||
        (this.invoiceData.business as any)?.msmetype ||
        (this.invoiceData.business as any)?.msme_type ||
        clientSaleData?.msmetype ||
        clientSaleData?.msme_type ||
        clientSaleData?.msmeType ||
        localStorage.getItem('msmetype') ||
        '';

      const msmeRow: string[] = [];
      if (businessConfig.showMsmeNo !== false && msmeNoValue && msmeNoValue !== '-') {
        msmeRow.push(`Msme No: ${msmeNoValue}`);
      }
      if (businessConfig.showMsmeType !== false && msmeTypeValue && msmeTypeValue !== '-') {
        msmeRow.push(`Msme Type: ${msmeTypeValue}`);
      }
      if (msmeRow.length > 0) {
        businessDetailsContent.push({
          text: msmeRow.join(' | '),
          fontSize: theme.baseFontSize - 1,
          color: theme.textColor,
          margin: [0, 0, 0, 2],
        });
      }
    }

    const taxInfo: string[] = [];
    if (businessConfig.showGSTIN) {
      taxInfo.push(`GSTIN: ${business.gstin || '-'}`);
    }
    if (businessConfig.showPan) {
      const panValue = this.invoiceData.business?.pan;
      if (panValue && panValue.trim() !== '') {  // ✅ only push when PAN actually has a value
        taxInfo.push(`PAN: ${panValue}`);
      }
    }

    if (taxInfo.length > 0) {
      const isThermalTax = pageSettings.pageSize === 'THERMAL';
      if (isThermalTax) {
        taxInfo.forEach((info) => {
          businessDetailsContent.push({
            text: info,
            fontSize: isThermal ? theme.baseFontSize + 1 : theme.baseFontSize - 1,
            color: theme.textColor,
            margin: [0, 0, 0, 2],
          });
        });
      } else {
        businessDetailsContent.push({
          text: taxInfo.filter(t => t.trim() !== '').join(' | '),
          fontSize: theme.baseFontSize - 1,
          color: theme.textColor,
          margin: [0, 0, 0, 2],
        });
      }
    }
    let finalContent: any;
    if (pageSettings.pageSize !== 'THERMAL') {
      // console.log('logo left', headerLayout)
      if (headerLayout == 'logo-left' && logoElement) {
        // console.log('logo left')
        finalContent = {
          columns: [
            { ...logoElement, width: businessConfig.logoWidth || 80 },
            {
              stack: businessDetailsContent,
              width: '*',
            },
          ],
          columnGap: 10,
        };
      } else if (headerLayout === 'logo-right' && logoElement) {
        finalContent = {
          columns: [
            {
              stack: businessDetailsContent,
              width: '*',
            },
            { ...logoElement, width: businessConfig.logoWidth || 80 },
          ],
          columnGap: 10,
        };
      } else if (headerLayout == 'logo-center' && logoElement) {

        finalContent = {
          stack: [
            { ...logoElement, alignment: 'center', margin: [0, 0, 0, 10] },
            {
              stack: businessDetailsContent.map((item) => ({
                ...item,
                alignment: 'center',
              })),
            },
          ],
        };
      } else if (headerLayout === 'logo-left-text-center' && logoElement) {
        finalContent = {
          columns: [
            { ...logoElement, width: businessConfig.logoWidth || 80 },
            {
              stack: businessDetailsContent.map((item) => ({
                ...item,
                alignment: 'center',
              })),
              width: '*',
            },
          ],
          columnGap: 10,
        };
      } else if (headerLayout === 'logo-right-text-center' && logoElement) {
        finalContent = {
          columns: [
            {
              stack: businessDetailsContent.map((item) => ({
                ...item,
                alignment: 'center',
              })),
              width: '*',
            },
            { ...logoElement, width: businessConfig.logoWidth || 80 },
          ],
          columnGap: 10,
        };
      } else if (headerLayout === 'compact' && logoElement) {
        const leftStack: any[] = [];
        const rightStack: any[] = [];

        // ✅ Logo + Business Name + Phone + Email side by side
        const nameAndDetails: any[] = [];

        if (businessConfig.showBusinessName) {
          nameAndDetails.push({
            text: business.name,
            font: this.getFontForText(business.name),
            fontSize: businessConfig.businessNameFontSize,
            bold: true,
            color: theme.primaryColor,
            margin: [0, 0, 0, 2],
          });
        }

        if (businessConfig.showTagline) {
          const taglineText = businessConfig.tagline || business.tagline;
          if (taglineText) {
            nameAndDetails.push({
              text: taglineText,
              fontSize: theme.baseFontSize - 1,
              color: theme.secondaryColor,
              margin: [0, 0, 0, 2],
            });
          }
        }

        if (businessConfig.showPhone) {
          const phoneLabel = 'Ph:';
          const phoneValue = business.phone || '-';
          if (phoneValue !== '-') {
            nameAndDetails.push({
              text: `${phoneLabel} ${phoneValue}`,
              fontSize: theme.baseFontSize - 1,
              color: theme.textColor,
              margin: [0, 0, 0, 2],
            });
          }
        }

        if (businessConfig.showEmail) {
          const emailLabel = businessConfig.emailLabel || 'E-Mail:';
          const emailValue = business.email || '';
          if (emailValue && emailValue !== '-') {
            nameAndDetails.push({
              text: `${emailLabel} ${emailValue}`,
              fontSize: theme.baseFontSize - 1,
              color: theme.textColor,
              margin: [0, 0, 0, 2],
            });
          }
        }

        // ✅ Logo beside nameAndDetails — if no logo, nameAndDetails fills the left stack directly
        if (logoElement) {
          leftStack.push({
            columns: [
              { ...logoElement, width: businessConfig.logoWidth || 60, alignment: 'left' },
              { stack: nameAndDetails, width: '*', margin: [6, 0, 0, 0] },
            ],
            columnGap: 0,
          });
        } else {
          leftStack.push(...nameAndDetails);
        }

        // ✅ Right: GSTIN → Address → Website → PAN
        if (businessConfig.showGSTIN) {
          rightStack.push({
            text: `GSTIN: ${business.gstin || '-'}`,
            fontSize: theme.baseFontSize - 1,
            color: theme.textColor,
            alignment: 'right',
            margin: [0, 0, 0, 2],
          });
        }

        if (businessConfig.showAddress) {
          let addressText = business.address || '-';
          if (business.city || business.state || business.pincode) {
            const addressParts = [
              business.address,
              business.city,
              business.state,
              business.pincode,
            ].filter((part) => part && part.trim());
            addressText = addressParts.join(', ');
          }
          rightStack.push({
            text: addressText,
            font: this.getFontForText(addressText),
            fontSize: theme.baseFontSize - 1,
            color: theme.textColor,
            alignment: 'right',
            margin: [0, 0, 0, 2],
          });
        }

        if (businessConfig.showWebsite) {
          const websiteValue = business.website || '-';
          if (websiteValue !== '-') {
            rightStack.push({
              text: `Website: ${websiteValue}`,
              fontSize: theme.baseFontSize - 1,
              color: theme.textColor,
              alignment: 'right',
              margin: [0, 0, 0, 2],
            });
          }
        }

        if (businessConfig.showPan) {
          const panValue = this.invoiceData.business?.pan;
          if (panValue && panValue.trim() !== '') {
            rightStack.push({
              text: `PAN: ${panValue}`,
              fontSize: theme.baseFontSize - 1,
              color: theme.textColor,
              alignment: 'right',
              margin: [0, 0, 0, 2],
            });
          }
        }

        finalContent = {
          columns: [
            { stack: leftStack, width: '*' },
            { stack: rightStack, width: '*' },
          ],
          columnGap: 10,
        };
      }
    } else {
      const content: any[] = [];
      // console.log('not go any condtion')
      if (logoElement) {
        content.push(logoElement);
      }
      content.push(...businessDetailsContent);
      finalContent = {
        stack: content,
        alignment: 'center',
        margin: [5, 0, 5, 0],  // ← ADD THIS
      };
    }
    // console.log(finalContent, 'finalContent')
    if (!finalContent) {
      finalContent = {
        stack: businessDetailsContent.map((item) => ({ ...item, alignment: 'center' })),
      };
    }
    if (businessConfig.showBorder) {
      return {
        table: {
          widths: ['*'],
          body: [[{
            stack: [finalContent],  // ← wrap in stack instead of spreading
            margin: [5, 1, 5, 0],
          }]],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          hLineColor: () => businessConfig.borderColor || theme.borderColor || '#000000',
          vLineColor: () => businessConfig.borderColor || theme.borderColor || '#000000',
        },
        margin: [0, 0, 0, 0],//this.config.layoutSettings.sectionSpacing
      };
    }

    return {
      stack: [finalContent],  // ← same here
      margin: [0, 0, 0, 0],
    };
  }

  /**
   * A5 Landscape specific business section layout:
   * Top Row: Invoice title (Bold, underline) + "Original for receipt" on left, Logo on right
   * Below Row: Business name, GST, Address, Phone, Email, PAN - all centered one by one
   */
  private buildA5LandscapeBusinessSection(business: any, businessConfig: any, theme: any, logoElement: any): Content {
    const invoiceConfig = this.config.invoiceDetails;
    const invoiceTitle = 'BILL OF SUPPLY';
    const fontSize = theme.baseFontSize - 1;

    // ═══ TOP ROW: Title left + Logo right ═══
    const titleContent: any = {
      columns: [
        {
          text: invoiceTitle,
          bold: true,
          decoration: 'underline',
          fontSize: invoiceConfig?.titleFontSize || 11,
          color: theme.primaryColor,
          width: 'auto',
        },
        {
          text: '  Original for receipt',
          fontSize: invoiceConfig?.titleFontSize || 11,
          color: theme.textColor,
          width: 'auto',
          margin: [0, 0, 0, 0],
        }
      ],
      columnGap: 4,
      width: '*',
      alignment: 'left',
    };

    // Logo box: show image if available, otherwise show empty bordered rectangle as placeholder
    const logoWidth = businessConfig.logoWidth || 60;
    const logoBoxContent: any = logoElement && logoElement.image
      ? { image: logoElement.image, width: logoWidth, alignment: 'center' }
      : {
        canvas: [{
          type: 'rect',
          x: 0,
          y: 0,
          w: logoWidth,
          h: logoWidth * 0.75,
          lineWidth: 0,
          lineColor: '#fff',
        }],
        width: logoWidth,
        alignment: 'center',
      };

    const logoBox: any = {
      table: {
        widths: ['auto'],
        body: [[{
          stack: [logoBoxContent],
          margin: [3, 4, 4, 4],
        }]],
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        hLineColor: () => '#000000',
        vLineColor: () => '#000000',
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
      width: 'auto',
      alignment: 'right',
    };

    const topRow: any = {
      columns: [
        titleContent,
        logoBox,
      ],
      columnGap: 10,
      margin: [0, 0, 0, 1],
    };

    // ═══ BELOW ROW: All business details centered one by one ═══
    const centeredDetails: any[] = [];

    if (businessConfig.showBusinessName) {
      centeredDetails.push({
        text: business.name,
        font: this.getFontForText(business.name),
        fontSize: businessConfig.businessNameFontSize,
        bold: true,
        color: theme.primaryColor,
        alignment: 'center',
        margin: [0, 0, 0, 2],
      });
    }
    if (businessConfig.showTagline) {
      const taglineText = businessConfig.tagline || business.tagline;
      if (taglineText) {
        centeredDetails.push({
          text: taglineText,
          fontSize: fontSize,
          color: theme.textColor,
          alignment: 'center',
          margin: [0, 0, 0, 2],
        });
      }
    }

    if (businessConfig.showAddress) {
      // A5 Landscape: Show each address part only if it has a truthy value
      const addressParts = [
        business.address,
        business.city,
        business.state,
        business.pincode,
      ].filter((part: string) => part && part.trim());

      if (addressParts.length > 0) {
        const addressText = addressParts.join(', ');
        centeredDetails.push({
          text: addressText,
          font: this.getFontForText(addressText),
          fontSize: fontSize,
          color: theme.textColor,
          alignment: 'center',
          margin: [0, 0, 0, 2],
        });
      }
    }

    // Phone and Email with pipe separator
    const contactParts: string[] = [];
    if (businessConfig.showPhone) {
      const phoneLabel = businessConfig.phoneLabel || 'Tel:';
      const phoneValue = business.phone || '-';
      contactParts.push(`${phoneLabel} ${phoneValue}`);
    }
    if (businessConfig.showEmail) {
      const emailLabel = businessConfig.emailLabel || 'E-Mail:';
      const emailValue = business.email || '-';
      contactParts.push(`${emailLabel} ${emailValue}`);
    }
    if (businessConfig.showWebsite) {
      const websiteValue = business.website || '-';
      contactParts.push(`Website: ${websiteValue}`);
    }
    if (contactParts.length > 0) {
      centeredDetails.push({
        text: contactParts.join(' | '),
        fontSize: fontSize,
        color: theme.textColor,
        alignment: 'center',
        margin: [0, 0, 0, 2],
      });
    }

    // GSTIN and PAN with pipe separator
    const taxParts: string[] = [];
    if (businessConfig.showGSTIN) {
      taxParts.push(`GSTIN: ${business.gstin || '-'}`);
    }
    if (businessConfig.showPan) {
      const panLabel = businessConfig.panLabel || 'PAN:';
      taxParts.push(`${panLabel} ${this.invoiceData.business?.pan || '-'}`);
    }

    if (taxParts.length > 0) {
      centeredDetails.push({
        text: taxParts.join(' | '),
        fontSize: fontSize,
        color: theme.textColor,
        alignment: 'center',
        margin: [0, 0, 0, 0],
      });
    }

    // MSME details with pipe separator (A5 landscape custom template)
    const msmeParts: string[] = [];
    const clientSaleDataL = (() => {
      try {
        return JSON.parse(localStorage.getItem('client_sale_data') || '{}');
      } catch {
        return {};
      }
    })();

    const a5LMsmeNo =
      business.msmeNo ||
      (business as any).msmeno ||
      (business as any).msme_no ||
      this.invoiceData.business?.msmeNo ||
      (this.invoiceData.business as any)?.msmeno ||
      (this.invoiceData.business as any)?.msme_no ||
      clientSaleDataL?.msmeno ||
      clientSaleDataL?.msme_no ||
      clientSaleDataL?.msmeNo ||
      localStorage.getItem('msmeno') ||
      '';

    const a5LMsmeType =
      business.msmeType ||
      (business as any).msmetype ||
      (business as any).msme_type ||
      this.invoiceData.business?.msmeType ||
      (this.invoiceData.business as any)?.msmetype ||
      (this.invoiceData.business as any)?.msme_type ||
      clientSaleDataL?.msmetype ||
      clientSaleDataL?.msme_type ||
      clientSaleDataL?.msmeType ||
      localStorage.getItem('msmetype') ||
      '';

    if (businessConfig.showMsmeNo !== false && a5LMsmeNo && a5LMsmeNo !== '-') {
      msmeParts.push(`Msme No: ${a5LMsmeNo}`);
    }
    if (businessConfig.showMsmeType !== false && a5LMsmeType && a5LMsmeType !== '-') {
      msmeParts.push(`Msme Type: ${a5LMsmeType}`);
    }
    if (msmeParts.length > 0) {
      centeredDetails.push({
        text: msmeParts.join(' | '),
        fontSize: fontSize,
        color: theme.textColor,
        alignment: 'center',
        margin: [0, 0, 0, 2],
      });
    }
    // Invisible spacer: ensures the business section always occupies a fixed height
    // even when all optional fields (PAN, GST, phone, email, etc.) are hidden.
    // The canvas height is chosen so that topRow (~20pt) + spacer >= 80pt total.
    const spacerHeight = Math.max(0, 80 - (centeredDetails.length * 14) - 20);
    const finalContent: any = {
      stack: [
        topRow,
        ...centeredDetails,
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 0, y2: spacerHeight, lineWidth: 0, lineColor: '#ffffff' }] },
      ],
    };

    if (businessConfig.showBorder) {
      return {
        table: {
          widths: ['*'],
          body: [[{
            ...finalContent,
            margin: [5, 1, 5, 0],
          }]],
          heights: () => 80, // fixed minimum height — section never shrinks below this
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          hLineColor: () => businessConfig.borderColor || theme.borderColor || '#000000',
          vLineColor: () => businessConfig.borderColor || theme.borderColor || '#000000',
        },
        margin: [0, 0, 0, 0],
      } as any;
    }

    return {
      table: {
        widths: ['*'],
        body: [[{ ...finalContent, margin: [0, 0, 0, 0] }]],
        heights: () => 80, // fixed minimum height — section never shrinks below this
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        hLineColor: () => theme.borderColor || '#000000',
        vLineColor: () => theme.borderColor || '#000000',
      },
      margin: [0, 0, 0, 0],
    } as any;
  }

  private buildA5PortraitBusinessSection(business: any, businessConfig: any, theme: any, logoElement: any): Content {
    const invoiceConfig = this.config.invoiceDetails;
    const fontSize = theme.baseFontSize - 1;

    // ─── LEFT: Logo ───
    const logoWidth = businessConfig.logoWidth || 60;
    let leftContent: any;
    if (businessConfig.showLogo && logoElement && logoElement.image) {
      leftContent = {
        image: logoElement.image,
        width: logoWidth,
        alignment: 'left',
        margin: [0, 0, 0, 0],
      };
    } else {
      // Empty placeholder so columns keep their shape
      leftContent = {
        canvas: [{
          type: 'rect', x: 0, y: 0,
          w: logoWidth, h: logoWidth * 0.6,
          lineWidth: 0, lineColor: '#ffffff',
        }],
        width: logoWidth,
      };
    }
    // ─── CENTER: Business details, each item centered ───
    const centerStack: any[] = [];

    if (businessConfig.showBusinessName) {
      centerStack.push({
        text: business.name,
        font: this.getFontForText(business.name),
        fontSize: businessConfig.businessNameFontSize || (theme.baseFontSize + 2),
        bold: true,
        color: theme.primaryColor,
        alignment: 'center',
        margin: [0, 0, 0, 2],
      });
    }

    if (businessConfig.showTagline) {
      const taglineText = businessConfig.tagline || business.tagline;
      if (taglineText) {
        centerStack.push({
          text: taglineText,
          fontSize: fontSize,
          color: theme.secondaryColor,
          alignment: 'center',
          margin: [0, 0, 0, 2],
        });
      }
    }

    if (businessConfig.showAddress) {
      const addressParts = [
        business.address,
        business.city,
        business.state,
        business.pincode,
      ].filter((p: string) => p && p.trim());
      if (addressParts.length > 0) {
        const addressText = addressParts.join(', ');
        centerStack.push({
          text: addressText,
          font: this.getFontForText(addressText),
          fontSize: fontSize,
          color: theme.textColor,
          alignment: 'center',
          margin: [0, 0, 0, 2],
        });
      }
    }

    const contactParts: string[] = [];
    if (businessConfig.showPhone) {
      const phoneLabel = businessConfig.phoneLabel || 'Tel:';
      contactParts.push(`${phoneLabel} ${business.phone || '-'}`);
    }
    if (businessConfig.showEmail) {
      const emailLabel = businessConfig.emailLabel || 'E-Mail:';
      contactParts.push(`${emailLabel} ${business.email || '-'}`);
    }
    if (businessConfig.showWebsite && business.website) {
      contactParts.push(`Website: ${business.website}`);
    }
    if (contactParts.length > 0) {
      centerStack.push({
        text: contactParts.join(' | '),
        fontSize: fontSize,
        color: theme.textColor,
        alignment: 'center',
        margin: [0, 0, 0, 2],
      });
    }

    const taxParts: string[] = [];
    if (businessConfig.showGSTIN) {
      taxParts.push(`GSTIN: ${business.gstin || '-'}`);
    }
    if (businessConfig.showPan) {
      const panLabel = businessConfig.panLabel || 'PAN:';
      taxParts.push(`${panLabel} ${this.invoiceData.business?.pan || '-'}`);
    }
    if (taxParts.length > 0) {
      centerStack.push({
        text: taxParts.join(' | '),
        fontSize: fontSize,
        color: theme.textColor,
        alignment: 'center',
        margin: [0, 0, 0, 0],
      });
    }

    // MSME details with pipe separator (A5 portrait custom template)
    const a5PMsmeParts: string[] = [];
    const clientSaleDataP = (() => {
      try {
        return JSON.parse(localStorage.getItem('client_sale_data') || '{}');
      } catch {
        return {};
      }
    })();

    const a5PMsmeNo =
      business.msmeNo ||
      (business as any).msmeno ||
      (business as any).msme_no ||
      this.invoiceData.business?.msmeNo ||
      (this.invoiceData.business as any)?.msmeno ||
      (this.invoiceData.business as any)?.msme_no ||
      clientSaleDataP?.msmeno ||
      clientSaleDataP?.msme_no ||
      clientSaleDataP?.msmeNo ||
      localStorage.getItem('msmeno') ||
      '';

    const a5PMsmeType =
      business.msmeType ||
      (business as any).msmetype ||
      (business as any).msme_type ||
      this.invoiceData.business?.msmeType ||
      (this.invoiceData.business as any)?.msmetype ||
      (this.invoiceData.business as any)?.msme_type ||
      clientSaleDataP?.msmetype ||
      clientSaleDataP?.msme_type ||
      clientSaleDataP?.msmeType ||
      localStorage.getItem('msmetype') ||
      '';

    if (businessConfig.showMsmeNo !== false && a5PMsmeNo && a5PMsmeNo !== '-') {
      a5PMsmeParts.push(`Msme No: ${a5PMsmeNo}`);
    }
    if (businessConfig.showMsmeType !== false && a5PMsmeType && a5PMsmeType !== '-') {
      a5PMsmeParts.push(`Msme Type: ${a5PMsmeType}`);
    }
    if (a5PMsmeParts.length > 0) {
      centerStack.push({
        text: a5PMsmeParts.join(' | '),
        fontSize: fontSize,
        color: theme.textColor,
        alignment: 'center',
        margin: [0, 0, 0, 2],
      });
    }

    const invoiceTitle = 'BILL OF SUPPLY';
    const rightStack: any[] = [
      {
        text: invoiceTitle,
        fontSize: invoiceConfig?.titleFontSize || (theme.baseFontSize + 1),
        bold: true,
        decoration: 'underline',
        color: theme.primaryColor,
        alignment: 'right',
        margin: [0, 0, 0, 2],
      },
    ];

    // ─── Assemble three-column layout ───
    const threeCol: any = {
      columns: [
        { width: 'auto', stack: rightStack },
        { width: '*', stack: centerStack },
        { width: logoWidth + 4, stack: [leftContent] },
      ],
      columnGap: 6,
    };

    if (businessConfig.showBorder) {
      return {
        table: {
          widths: ['*'],
          body: [[{ ...threeCol, margin: [5, 3, 5, 3] }]],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          hLineColor: () => businessConfig.borderColor || theme.borderColor || '#000000',
          vLineColor: () => businessConfig.borderColor || theme.borderColor || '#000000',
        },
        margin: [0, 0, 0, 0],
      } as any;
    }

    return { ...threeCol, margin: [0, 0, 0, 0] } as any;
  }

  /**
   * A5 Landscape specific combined Party + Invoice Details layout:
   * Left side: Customer Name (bold), Address, Mobile | Email | GSTIN | Age | Gender
   * Right side: Table with Invoice No, Date, Time, Mode of Pay, Due Date, DC No,
   *             Doctor Name, Doctor Id, E-way Bill No, Vehicle No
   */
  buildA5LandscapePartyAndInvoiceSection(): Content {
    // console.log('[A5Landscape] buildA5LandscapePartyAndInvoiceSection called');
    const partyConfig = this.config.partyDetails;
    const invoiceConfig = this.config.invoiceDetails;
    const theme = this.config.themeSettings;
    const fontSize = theme.baseFontSize - 2;
    const party = this.invoiceData.party || {
      name: this.invoiceData.customerName || '-',
      address: this.invoiceData.customerAddress?.line1 || '-',
      city: this.invoiceData.customerAddress?.city || '',
      state: this.invoiceData.customerAddress?.state || '',
      pincode: this.invoiceData.customerAddress?.pincode || '',
      phone: this.invoiceData.customerMobile || '-',
      mobile: this.invoiceData.customerMobile || '-',
      gstin: this.invoiceData.customerGSTIN || '-',
    };

    const showParty = this.config.layoutSettings?.showPartyDetails !== false &&
      this.config.layoutSettings?.sectionOrder?.find(s => s.id === 'party')?.visible !== false &&
      partyConfig?.showBillTo !== false;
    const showInvoice = this.config.layoutSettings?.showInvoiceDetails !== false &&
      this.config.layoutSettings?.sectionOrder?.find(s => s.id === 'invoiceDetails')?.visible !== false;

    // ═══ LEFT SIDE: Party Details ═══
    const leftContent: any[] = [];

    if (showParty) {
      // Customer Name (bold)
      const customerName = party.name || '-';
      leftContent.push({
        text: customerName,
        font: this.getFontForText(customerName),
        fontSize: theme.baseFontSize,
        bold: true,
        color: theme.textColor,
        margin: [2, 2, 0, 2],
      });

      // Address
      const addressStack: any[] = [];
      const addressParts = [
        party.address,
        party.city,
        party.state,
        party.pincode,
      ].filter((part) => !!part && part.trim());

      if (addressParts.length > 0) {
        const addressText = addressParts.join(', ');
        addressStack.push({
          columns: [
            { text: 'Address:', width: 25, color: theme.textColor },
            { text: addressText, font: this.getFontForText(addressText), width: '*' }
          ]
        });
      }

      // Contact info line: Mobile, Email, GSTIN, Age, Gender
      const contactStack: any[] = [];
      const mobile = party.phone || party.mobile || '';
      if (mobile && mobile !== '-') {
        contactStack.push({ columns: [{ text: 'Mobile:', width: 40, color: theme.textColor }, { text: mobile, width: '*' }], margin: [0, 0, 0, 1] });
      }
      const email = party.email || '';
      if (email) {
        contactStack.push({ columns: [{ text: 'Email:', width: 40, color: theme.textColor }, { text: email, width: '*' }], margin: [0, 0, 0, 1] });
      }
      const gstin = party.gstin || '';
      if (gstin && gstin !== '-') {
        contactStack.push({ columns: [{ text: 'GSTIN:', width: 40, color: theme.textColor }, { text: gstin, width: '*' }], margin: [0, 0, 0, 1] });
      }
      const age = (this.invoiceData as any).age || (this.invoiceData.customFields?.['age']) || '';
      if (age || (partyConfig as any).showAge) {
        contactStack.push({ columns: [{ text: 'Age:', width: 40, color: theme.textColor }, { text: age || '-', width: '*' }], margin: [0, 0, 0, 1] });
      }
      const gender = (this.invoiceData as any).gender || (this.invoiceData.customFields?.['gender']) || '';
      if (gender || (partyConfig as any).showGender) {
        contactStack.push({ columns: [{ text: 'Gender:', width: 40, color: theme.textColor }, { text: gender || '-', width: '*' }], margin: [0, 0, 0, 1] });
      }

      const columnsLayout: any[] = [];
      if (addressStack.length > 0) {
        columnsLayout.push({ stack: addressStack, width: contactStack.length > 0 ? '45%' : '100%', fontSize: fontSize, color: theme.textColor });
      }
      if (contactStack.length > 0) {
        columnsLayout.push({ stack: contactStack, width: addressStack.length > 0 ? '55%' : '100%', fontSize: fontSize, color: theme.textColor });
      }

      if (columnsLayout.length > 0) {
        leftContent.push({
          columns: columnsLayout,
          columnGap: 5,
          margin: [2, 0, 0, 2]
        });
      }
    }

    // ═══ RIGHT SIDE: Invoice Details Table ═══
    const rightTableBody: any[][] = [];

    if (showInvoice) {
      const invoiceNumber = this.invoiceData.invoiceNumber || '-';
      const invoiceDate = this.invoiceData.invoiceDate
        ? typeof this.invoiceData.invoiceDate === 'string'
          ? this.invoiceData.invoiceDate
          : this.invoiceData.invoiceDate.toLocaleDateString()
        : '-';
      const invoiceTime = this.invoiceData.invoiceTime || '';
      const paymentMethod = (this.invoiceData as any).paymentMethod || '';
      const dueDate = this.invoiceData.dueDate || '';
      const dcNo = this.invoiceData.sodcNo || (this.invoiceData as any).dcNo || '';
      const doctorName = (this.invoiceData as any).doctorName || (this.invoiceData.customFields?.['doctorName']) || '';
      const doctorId = (this.invoiceData as any).doctorId || (this.invoiceData.customFields?.['doctorId']) || '';
      const ewayBillNo = this.invoiceData.ewayBillNumber || '';
      const vehicleNo = this.invoiceData.vehicleNumber || '';

      const fields: { label: string, value: string }[] = [];

      if (invoiceConfig.showInvoiceNumber !== false) {
        fields.push({ label: 'Invoice No.', value: invoiceNumber });
      }
      if (invoiceConfig.showInvoiceDate !== false) {
        fields.push({ label: 'Invoice Date', value: invoiceDate });
      }
      if (invoiceConfig.showTime !== false && invoiceTime) {
        fields.push({ label: 'Time', value: invoiceTime });
      }
      if ((invoiceConfig as any).showModeOfPayment !== false && paymentMethod) {
        fields.push({ label: 'Mode of Pay', value: paymentMethod });
      }
      if ((invoiceConfig as any).showDueDate && dueDate) {
        fields.push({ label: 'Due Date', value: dueDate });
      }
      if ((invoiceConfig as any).showSODCNo && dcNo) {
        fields.push({ label: 'DC No.', value: dcNo });
      }

      const pharmaConfig = (partyConfig as any);
      const showPharma = pharmaConfig?.showPharmaSection;
      const pharmaFields = pharmaConfig?.pharmaFields || {};

      if (showPharma && pharmaFields.showDoctorName && doctorName) {
        fields.push({ label: 'Doctor Name', value: doctorName });
      }
      if (showPharma && pharmaFields.showDoctorId && doctorId) {
        fields.push({ label: 'Doctor ID', value: doctorId });
      }
      if ((invoiceConfig as any).showEwayBillNumber && ewayBillNo) {
        fields.push({ label: 'E-way Bill', value: ewayBillNo });
      }
      if ((invoiceConfig as any).showVehicleNumber && vehicleNo) {
        fields.push({ label: 'Vehicle No.', value: vehicleNo });
      }

      // Chunk into rows of 3
      for (let i = 0; i < fields.length; i += 3) {
        const row = fields.slice(i, i + 3).map(f => ({
          stack: [
            { text: f.label, bold: true, fontSize: fontSize, margin: [0, 0, 0, 2] },
            { text: f.value, fontSize: fontSize }
          ]
        }));
        rightTableBody.push(row);
      }

      // Normalize rows to 3 columns
      const MAX_COLUMNS = 3;
      for (const row of rightTableBody) {
        while (row.length < MAX_COLUMNS) {
          row.push({ text: '', fontSize: fontSize });
        }
      }
    }

    const hasLeft = leftContent.length > 0;
    const hasRight = rightTableBody.length > 0;

    if (!hasLeft && !hasRight) {
      return { text: '', margin: [0, 0, 0, 0] } as any;
    }

    const borderColor = theme.borderColor || '#000000';
    const borderWidth = partyConfig?.borderWidth || 0.5;

    const rightColumnWidths: any[] = [];
    if (hasRight) {
      const maxCols = rightTableBody[0]?.length || 0;
      for (let i = 0; i < maxCols; i++) {
        rightColumnWidths.push('*');
      }
    }

    let tableWidths: any[] = [];
    const tableBodyRow: any[] = [];

    if (hasLeft && hasRight) {
      tableWidths = ['45%', '*'];
      tableBodyRow.push({
        stack: leftContent,
        margin: [0, 0, 0, 0],
      });
      tableBodyRow.push({
        table: {
          widths: rightColumnWidths,
          body: rightTableBody,
        },
        layout: {
          hLineWidth: (i: number) => i === 0 ? 0 : 0.5, // Remove top border
          vLineWidth: (i: number, node: any) => {
            if (i === 0) return 0; // No left border
            return 0.5; // All other vertical lines including right border
          },
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
        margin: [0, 0, 0, 0],
      });

      // Remove right border from last row of invoice details
      if (rightTableBody.length > 0) {
        const lastRow = rightTableBody[rightTableBody.length - 1];
        const lastCell = lastRow[lastRow.length - 1];
        if (typeof lastCell === 'object') {
          lastCell.border = [false, false, false, false];
        }
      }
    } else if (hasLeft && !hasRight) {
      tableWidths = ['*'];
      tableBodyRow.push({
        stack: leftContent,
        margin: [0, 0, 0, 0],
      });
    } else if (!hasLeft && hasRight) {
      tableWidths = ['*'];
      tableBodyRow.push({
        table: {
          widths: rightColumnWidths,
          body: rightTableBody,
        },
        layout: {
          hLineWidth: (i: number) => i === 0 ? 0 : 0.5, // Remove top border
          vLineWidth: (i: number, node: any) => {
            if (i === 0) return 0; // No left border
            return 0.5; // All other vertical lines including right border
          },
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
        margin: [0, 0, 0, 0],
      });

      // Remove right border from last row of invoice details
      if (rightTableBody.length > 0) {
        const lastRow = rightTableBody[rightTableBody.length - 1];
        const lastCell = lastRow[lastRow.length - 1];
        if (typeof lastCell === 'object') {
          lastCell.border = [false, false, false, false];
        }
      }
    }

    return {
      table: {
        widths: tableWidths,
        body: [tableBodyRow],
        heights: () => 47, // fixed minimum height — section never shrinks below this
      },
      layout: {
        hLineWidth: (i: number) => borderWidth, // Top and bottom border
        vLineWidth: (i: number, node: any) => {
          if (i === 0) return 0; // Remove left side border
          if (i === node.table.widths.length) return 0; // Remove right side border
          return borderWidth; // Middle divider = party right border
        },
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
      margin: [0, 0, 0, 0],
    } as any;
  }
  /**
   * A5 Portrait party section — matches screenshot layout exactly.
   * Fixed height: hidden fields render as empty space, not collapsed.
   *
   * Layout (left column of the party+invoice row):
   *   Customer Name  (bold, large)
   *   Address: ...
   *   Mobile: ... | GSTIN: ...
   *   Email: ...
   *   Gender: ... | Age: ...
   *
   * Always occupies a fixed minimum height so invoice details table aligns.
   */
  private buildA5PortraitPartySection(partyConfig: any, theme: any): Content {
    const billToFields = partyConfig?.billToFields;
    const fontSize = theme.baseFontSize - 1;
    const labelColor = theme.textColor;
    const borderColor = partyConfig?.borderColor || theme.borderColor || '#000000';
    const borderWidth = 0.3;

    const party = this.invoiceData.party || {
      name: this.invoiceData.customerName || '',
      address: this.invoiceData.customerAddress?.line1 || '',
      city: this.invoiceData.customerAddress?.city || '',
      state: this.invoiceData.customerAddress?.state || '',
      pincode: this.invoiceData.customerAddress?.pincode || '',
      phone: this.invoiceData.customerMobile || '',
      mobile: this.invoiceData.customerMobile || '',
      gstin: this.invoiceData.customerGSTIN || '',
      email: '',
    };

    // ── ROW 1: Customer Name ──────────────────────────────────────────────────
    const nameText = (billToFields?.showCustomerName !== false && party.name)
      ? party.name : '';
    const nameRow: any = {
      text: nameText
        ? [{ text: 'Customer Name', bold: true, fontSize: theme.baseFontSize, color: labelColor }]
        : ' ',
      margin: [3, 3, 3, 1],
    };
    // When name exists, show it as the cell text (bold heading style)
    const nameCell: any = nameText
      ? {
        text: nameText,
        font: this.getFontForText(nameText),
        bold: true,
        fontSize: theme.baseFontSize,
        color: labelColor,
        margin: [3, 3, 3, 1],
      }
      : { text: ' ', fontSize: theme.baseFontSize, margin: [3, 3, 3, 1] };

    // ── ROW 2: Address ────────────────────────────────────────────────────────
    const showAddress = billToFields?.showCustomerAddress !== false;
    let addressText = '';
    if (showAddress) {
      const addressParts = [
        party.address,
        party.city,
        party.state,
        party.pincode,
      ].filter((p) => p && p.trim());
      addressText = addressParts.join(', ');
    }
    const addressCell: any = showAddress
      ? {
        text: addressText
          ? [
            { text: 'Address:', bold: true, fontSize: fontSize, color: labelColor },
            { text: addressText, font: this.getFontForText(addressText), fontSize: fontSize, color: labelColor },
          ]
          : ' ',
        margin: [3, 1, 3, 1],
      }
      : { text: ' ', fontSize: fontSize, margin: [3, 1, 3, 1] };

    // ── ROW 3: Mobile | GSTIN ─────────────────────────────────────────────────
    const showPhone = billToFields?.showCustomerPhone !== false;
    const showGstin = billToFields?.showCustomerGSTIN !== false;
    const mobileVal = party.phone || party.mobile || '';
    const gstinVal = party.gstin || '';

    const mobilePart = showPhone
      ? [
        { text: 'Mobile: ', bold: true, fontSize: fontSize, color: labelColor },
        { text: mobileVal || '-', fontSize: fontSize, color: labelColor },
      ]
      : [];
    const gstinPart = showGstin
      ? [
        { text: (showPhone ? ' | ' : '') + 'GSTIN: ', bold: true, fontSize: fontSize, color: labelColor },
        { text: gstinVal || '-', fontSize: fontSize, color: labelColor },
      ]
      : [];
    const mobileGstinCell: any = (showPhone || showGstin)
      ? {
        text: [...mobilePart, ...gstinPart].length > 0
          ? [...mobilePart, ...gstinPart]
          : ' ',
        margin: [3, 1, 3, 1],
      }
      : { text: ' ', fontSize: fontSize, margin: [3, 1, 3, 1] };

    // ── ROW 4: Email ──────────────────────────────────────────────────────────
    const showEmail = billToFields?.showCustomerEmail !== false;
    const emailVal = party.email || '';
    const emailCell: any = showEmail
      ? {
        text: emailVal
          ? [
            { text: 'Email: ', bold: true, fontSize: fontSize, color: labelColor },
            { text: emailVal, fontSize: fontSize, color: labelColor },
          ]
          : ' ',
        margin: [3, 1, 3, 1],
      }
      : { text: ' ', fontSize: fontSize, margin: [3, 1, 3, 1] };

    // ── ROW 5: Gender | Age ───────────────────────────────────────────────────
    const showGender = (partyConfig as any)?.showGender || (partyConfig as any)?.pharmaFields?.showGender;
    const showAge = (partyConfig as any)?.showAge || (partyConfig as any)?.pharmaFields?.showAge;
    const genderVal = (this.invoiceData as any).gender
      || this.invoiceData.customFields?.['gender'] || '';
    const ageVal = (this.invoiceData as any).age
      || this.invoiceData.customFields?.['age'] || '';

    const genderPart = showGender
      ? [
        { text: 'Gender: ', bold: true, fontSize: fontSize, color: labelColor },
        { text: genderVal || '-', fontSize: fontSize, color: labelColor },
      ]
      : [];
    const agePart = showAge
      ? [
        { text: (showGender ? ' | ' : '') + 'Age: ', bold: true, fontSize: fontSize, color: labelColor },
        { text: ageVal ? ageVal.toString() : '-', fontSize: fontSize, color: labelColor },
      ]
      : [];
    const genderAgeCell: any = (showGender || showAge)
      ? {
        text: [...genderPart, ...agePart].length > 0
          ? [...genderPart, ...agePart]
          : ' ',
        margin: [3, 1, 3, 3],
      }
      : { text: ' ', fontSize: fontSize, margin: [3, 1, 3, 3] };

    // ── Assemble single-column table with fixed row heights ───────────────────
    // Fixed row heights ensure layout doesn't collapse when fields are hidden.
    const ROW_HEIGHTS = [18, 18, 16, 16, 16]; // pts — adjust to taste

    return {
      table: {
        widths: ['*'],
        body: [
          [nameCell],
          [addressCell],
          [mobileGstinCell],
          [emailCell],
          [genderAgeCell],
        ],
        heights: (rowIndex: number) => ROW_HEIGHTS[rowIndex] ?? 16,
      },
      layout: {
        // hLineWidth: (i: number, node: any) => {
        //   // Remove top border (i === 0), keep only bottom border
        //   if (i === node.table.body.length) return borderWidth;
        //   return 0; // no top, no internal lines
        // },
        vLineWidth: (i: number, node: any) => {
          if (i === 0) return 0; // no left outer border
          if (i === node.table.widths.length) {
            // ✅ show right border only when party section is visible
            return this.config.layoutSettings?.showInvoiceDetails ? borderWidth : 0;
          }
          return borderWidth; // keep internal column dividers
        }, // ✅ remove left and right borders
        hLineWidth: () => 0,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
      margin: [0, 0, 0, 0],
    } as any;
  }
  /**
   * A5 Portrait invoice details — matches screenshot right-side table.
   * Fixed row heights: hidden fields show as empty cells, not collapsed.
   *
   * Layout (3 columns per row, chunked):
   *   Invoice No.  | Invoice Date | Time
   *   Due Date     | DC No.       | Mode of Pay
   *   Doctor Name  | Doctor ID    | E-Way Bill
   *   Vehicle No.  |              |
   */
  private buildA5PortraitInvoiceDetails(invoiceConfig: any, theme: any): Content {
    const fontSize = theme.baseFontSize - 2;
    const borderColor = invoiceConfig?.borderColor || theme.borderColor || '#000000';
    const borderWidth = 0.3;

    // Helper to build a label+value stack cell with fixed structure
    const cell = (label: string, value: string, show: boolean): any => ({
      stack: show
        ? [
          { text: label, bold: true, fontSize: fontSize, color: theme.textColor, margin: [0, 0, 0, 1] },
          { text: value || '-', fontSize: fontSize, color: theme.textColor },
        ]
        : [
          { text: ' ', fontSize: fontSize },  // empty but occupies space
          { text: ' ', fontSize: fontSize },
        ],
      margin: [3, 2, 3, 2],
    });

    // Gather all values
    const invoiceNumber = this.invoiceData.invoiceNumber || '-';
    const invoiceDate = this.invoiceData.invoiceDate
      ? (typeof this.invoiceData.invoiceDate === 'string'
        ? this.invoiceData.invoiceDate
        : (this.invoiceData.invoiceDate as Date).toLocaleDateString())
      : '-';
    const invoiceTime = this.invoiceData.invoiceTime || '-';
    const dueDate = this.invoiceData.dueDate || '-';
    const dcNo = this.invoiceData.sodcNo || (this.invoiceData as any).dcNo || '-';
    const paymentMethod = (this.invoiceData as any).paymentMethod || '-';
    const doctorName = (this.invoiceData as any).doctorName
      || this.invoiceData.customFields?.['doctorName'] || '-';
    const doctorId = (this.invoiceData as any).doctorId
      || this.invoiceData.customFields?.['doctorId'] || '-';
    const ewayBill = this.invoiceData.ewayBillNumber || '-';
    const vehicleNo = this.invoiceData.vehicleNumber || '-';

    const pharmaConfig = invoiceConfig as any;
    const pharmaFields = (this.config.partyDetails as any)?.pharmaFields || {};
    const showPharma = (this.config.partyDetails as any)?.showPharmaSection;

    // Each row = 3 cells. Always render all 4 rows with fixed height.
    const rows: any[][] = [
      // Row 1
      [
        cell('Invoice No.', invoiceNumber, invoiceConfig.showInvoiceNumber !== false),
        cell('Invoice Date', invoiceDate, invoiceConfig.showInvoiceDate !== false),
        cell('Time', invoiceTime, invoiceConfig.showTime !== false),
      ],
      // Row 2
      [
        cell('Due Date', dueDate, !!(invoiceConfig as any).showDueDate),
        cell('DC No.', dcNo, !!(invoiceConfig as any).showSODCNo),
        cell('Mode of Pay', paymentMethod, invoiceConfig.showModeOfPayment !== false),
      ],
      // Row 3
      [
        cell('Doctor Name', doctorName, showPharma && pharmaFields.showDoctorName),
        cell('Doctor ID', doctorId, showPharma && pharmaFields.showDoctorId),
        cell('E-Way Bill', ewayBill, !!(invoiceConfig as any).showEwayBillNumber),
      ],
      // Row 4
      [
        cell('Vehicle No.', vehicleNo, !!(invoiceConfig as any).showVehicleNumber),
        { text: ' ', margin: [3, 2, 3, 2] },   // always empty
        { text: ' ', margin: [3, 2, 3, 2] },   // always empty
      ],
    ];

    // Fixed row height — same for all rows so party section heights match
    const ROW_HEIGHT = 18; // pts — match ROW_HEIGHTS in buildA5PortraitPartySection

    return {
      table: {
        widths: ['*', '*', '*'],
        body: rows,
        heights: () => ROW_HEIGHT,
      },
      layout: {
        hLineWidth: (i: number, node: any) => {
          // Remove top border (i === 0), keep internal and bottom lines
          if (i === 0) return 0; // ✅ no top border
          return borderWidth;
        },
        vLineWidth: (i: number, node: any) => {
          // Remove outer left (i === 0) and outer right (i === widths.length) borders
          if (i === 0 || i === node.table.widths.length) return 0; // ✅ no side borders
          return borderWidth; // keep internal column dividers
        },
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
      margin: [0, 0, 0, 0],
    } as any;
  }


  // @param inlineWithInvoiceDetails when true (party + invoiceDetails in same row):
  buildPartySection(inlineWithInvoiceDetails = false): Content {
    const partyConfig = this.config.partyDetails;
    const theme = this.config.themeSettings;
    const pageSettings = this.config.pageSettings;

    // console.log(theme, 'Bill to font sizze')
    // THERMAL layout: simple text with dashed lines, no tables
    if (pageSettings.pageSize === 'THERMAL') {
      return this.buildThermalPartySection(partyConfig, theme);
    }

    // ═══ A5 PORTRAIT: Fixed-height party section matching screenshot layout ═══
    if (pageSettings.pageSize === 'A5' && pageSettings.orientation !== 'landscape') {
      return this.buildA5PortraitPartySection(partyConfig, theme);
    }
    const columns: any[] = [];

    const party = this.applyCapitalForm(this.invoiceData.party || {
      name: this.invoiceData.customerName || '-',
      address: this.invoiceData.customerAddress?.line1 || '-',
      city: this.invoiceData.customerAddress?.city || '',
      state: this.invoiceData.customerAddress?.state || '',
      pincode: this.invoiceData.customerAddress?.pincode || '',
      phone: this.invoiceData.customerMobile || '-',
      mobile: this.invoiceData.customerMobile || '-',
      gstin: this.invoiceData.customerGSTIN || '-',
    });

    const showingThreeColumns =
      partyConfig.showBillTo &&
      partyConfig.showShipTo &&
      partyConfig.showDispatchFrom;
    const showingTwoColumns =
      (partyConfig.showBillTo &&
        (partyConfig.showShipTo || partyConfig.showDispatchFrom)) ||
      (partyConfig.showShipTo && partyConfig.showDispatchFrom);
    const columnWidth = showingThreeColumns
      ? '33.33%'
      : showingTwoColumns
        ? '50%'
        : '*';
    // console.log(partyConfig, 'partyConfig');
    if (partyConfig.showBillTo) {
      const billToContent: any[] = [];
      const billToFields = partyConfig.billToFields;

      const billToPrefix = 'Bill To';
      const billToName = party.name || '-';

      if (billToFields?.showCustomerName !== false) {
        billToContent.push({
          text: [
            { text: `${billToPrefix} : `, fontSize: theme.baseFontSize - 1, bold: true, color: theme.textColor },
            { text: billToName, font: this.getFontForText(billToName), fontSize: theme.baseFontSize, bold: true, color: theme.textColor }
          ],
          margin: [0, 0, 0, 3],
        });
      }

      {
        const addressParts: string[] = [];
        if (billToFields?.showCustomerAddress !== false && party.address) addressParts.push(party.address);
        if (billToFields?.showCustomerCity !== false && party.city) addressParts.push(party.city);
        if (billToFields?.showCustomerState !== false && party.state) addressParts.push(party.state);
        if (billToFields?.showCustomerPin !== false && party.pincode) addressParts.push(party.pincode);

        if (addressParts.length > 0) {
          const addressText = addressParts.join(', ');
          billToContent.push({
            text: addressText,
            font: this.getFontForText(addressText),
            fontSize: theme.baseFontSize - 3,
            color: theme.textColor,
            margin: [0, 0, 0, 1],
          });
        }
      }

      const contactRow: any[] = [];

      if (billToFields?.showCustomerPhone !== false) {
        const customerPhone = party.phone || party.mobile || '-';
        contactRow.push({ text: `Phone: ${customerPhone}`, width: 'auto' });
      }

      if (billToFields?.showCustomerEmail !== false && party.email) {
        contactRow.push({ text: `Email: ${party.email}`, width: 'auto' });
      }

      if (contactRow.length > 0) {
        billToContent.push({
          columns: contactRow,
          columnGap: 15,
          fontSize: theme.baseFontSize - 2,
          color: theme.textColor,
          margin: [0, 0, 0, 1],
        });
      }

      if (billToFields?.showCustomerGSTIN !== false) {
        const gstinValue = party.gstin || '-';
        billToContent.push({
          text: `GSTIN: ${gstinValue}`,
          fontSize: theme.baseFontSize - 2,
          color: theme.textColor,
          margin: [0, 0, 0, 1],
        });
      }

      columns.push({
        width: columnWidth,
        stack: billToContent,
      });
    }
    if (partyConfig.showShipTo) {
      const shipToContent: any[] = [];
      const shipToFields = partyConfig.shipToFields;

      const shipToPrefix = 'Ship To';
      const shippingData = this.applyCapitalForm(this.invoiceData.shipping);
      const shipToName = shippingData?.name || '-';

      if (shipToFields?.showCustomerName !== false) {
        shipToContent.push({
          text: [
            { text: `${shipToPrefix} : `, fontSize: theme.baseFontSize - 1, bold: true, color: theme.textColor },
            { text: shipToName, font: this.getFontForText(shipToName), fontSize: theme.baseFontSize, bold: true, color: theme.textColor }
          ],
          margin: [0, 0, 0, 3],
        });
      }

      if (shippingData) {

        {
          const addressParts: string[] = [];
          if (shipToFields?.showCustomerAddress !== false && shippingData.address) addressParts.push(shippingData.address);
          if (shipToFields?.showCustomerCity !== false && shippingData.city) addressParts.push(shippingData.city);
          if (shipToFields?.showCustomerState !== false && shippingData.state) addressParts.push(shippingData.state);
          if (shipToFields?.showCustomerPin !== false && shippingData.pincode) addressParts.push(shippingData.pincode);

          if (addressParts.length > 0) {
            const addressText = addressParts.join(', ');
            shipToContent.push({
              text: addressText,
              font: this.getFontForText(addressText),
              fontSize: theme.baseFontSize - 3,
              color: theme.textColor,
              margin: [0, 0, 0, 1],
            });
          }
        }

        const shipToContactRow: any[] = [];

        if (shipToFields?.showCustomerPhone !== false && shippingData.phone) {
          shipToContactRow.push({ text: `Phone: ${shippingData.phone}`, width: 'auto' });
        }

        if (shipToFields?.showCustomerEmail !== false && shippingData.email) {
          shipToContactRow.push({ text: `Email: ${shippingData.email}`, width: 'auto' });
        }

        if (shipToContactRow.length > 0) {
          shipToContent.push({
            columns: shipToContactRow,
            columnGap: 15,
            fontSize: theme.baseFontSize - 2,
            color: theme.textColor,
            margin: [0, 0, 0, 1],
          });
        }

        if (shipToFields?.showCustomerGSTIN !== false && shippingData.gstin) {
          shipToContent.push({
            text: `GSTIN: ${shippingData.gstin}`,
            fontSize: theme.baseFontSize - 2,
            color: theme.textColor,
            margin: [0, 0, 0, 1],
          });
        }
      } else {
        shipToContent.push({
          text: '-',
          fontSize: theme.baseFontSize - 2,
          color: theme.textColor,
          margin: [0, 0, 0, 1],
        });
      }

      columns.push({
        width: columnWidth,
        stack: shipToContent,
      });
    }
    if (partyConfig.showDispatchFrom) {
      const dispatchContent: any[] = [];

      const dispatchPrefix = partyConfig.dispatchFromPrefix || 'Dispatch From';
      const dispatchData = this.invoiceData.dispatchFrom;
      const dispatchName = dispatchData?.name || '-';

      dispatchContent.push({
        text: [
          { text: `${dispatchPrefix} : `, fontSize: theme.baseFontSize - 1, bold: true, color: theme.textColor },
          { text: dispatchName, font: this.getFontForText(dispatchName), fontSize: theme.baseFontSize, bold: true, color: theme.textColor }
        ],
        margin: [0, 0, 0, 3],
      });

      if (dispatchData) {

        {
          const dispatchFromFields = partyConfig.dispatchFromFields;
          const addressParts: string[] = [];
          if (dispatchFromFields?.showCustomerAddress !== false && dispatchData.address) addressParts.push(dispatchData.address);
          if (dispatchFromFields?.showCustomerCity !== false && dispatchData.city) addressParts.push(dispatchData.city);
          if (dispatchFromFields?.showCustomerState !== false && dispatchData.state) addressParts.push(dispatchData.state);
          if (dispatchFromFields?.showCustomerPin !== false && dispatchData.pincode) addressParts.push(dispatchData.pincode);
          const addressText = addressParts.length > 0 ? addressParts.join(', ') : '';

          if (addressText) {
            dispatchContent.push({
              text: addressText,
              font: this.getFontForText(addressText),
              fontSize: theme.baseFontSize - 1,
              color: theme.textColor,
              margin: [0, 0, 0, 2],
            });
          }
        }

        const dispatchContactRow: any[] = [];

        if (dispatchData.phone) {
          dispatchContactRow.push({ text: `Phone: ${dispatchData.phone}`, width: 'auto' });
        }
        const showDispatchEmail =
          partyConfig.dispatchFromFields?.showCustomerEmail !== false ||
          (partyConfig as any).showCustomerEmail !== false;
        if (showDispatchEmail && dispatchData.email) {
          dispatchContactRow.push({ text: `Email: ${dispatchData.email}`, width: 'auto' });
        }

        if (dispatchContactRow.length > 0) {
          dispatchContent.push({
            columns: dispatchContactRow,
            columnGap: 15,
            fontSize: theme.baseFontSize - 1,
            color: theme.textColor,
            margin: [0, 0, 0, 2],
          });
        }

        if (dispatchData.gstin) {
          dispatchContent.push({
            text: `GSTIN: ${dispatchData.gstin}`,
            fontSize: theme.baseFontSize - 1,
            color: theme.textColor,
            margin: [0, 0, 0, 2],
          });
        }
      } else {
        dispatchContent.push({
          text: '-',
          fontSize: theme.baseFontSize - 1,
          color: theme.textColor,
          margin: [0, 0, 0, 2],
        });
      }

      columns.push({
        width: columnWidth,
        stack: dispatchContent,
      });
    }
    if (partyConfig.showBorder) {
      let tableWidths: any[];
      if (inlineWithInvoiceDetails) {
        tableWidths = Array(columns.length).fill('*');
      } else if (columns.length === 1) {
        tableWidths = ['*'];
      } else if (columns.length === 2) {
        tableWidths = ['50%', '50%'];
      } else if (columns.length === 3) {
        tableWidths = ['*', 'auto', '*'];
      } else {
        tableWidths = Array(columns.length).fill('*');
      }

      return {
        table: {
          widths: tableWidths,
          body: [
            [
              ...columns.map((col) => ({
                stack: col.stack,
                margin: [3, 0, 3, 0],
              })),
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: (i: number, node: any) => {
            if (inlineWithInvoiceDetails && (i === 0 || i === node.table.widths.length)) return 0;
            return partyConfig.borderWidth || 0.5;
          },
          hLineColor: () => partyConfig.borderColor || theme.borderColor || '#000000',
          vLineColor: () => partyConfig.borderColor || theme.borderColor || '#000000',
        },
        margin: [0, 0, 0, 0],
      };
    }

    return {
      columns: columns,
      columnGap: 5,
      margin: [0, 0, 0, 0], //this.config.layoutSettings.sectionSpacing
    };
  }

  buildThermalPartySection(config: any, theme: any): Content {
    if (!config.showBillTo) {
      return { text: '', margin: [0, 0, 0, 0] };
    }

    const billToFields = config.billToFields;
    const party = this.invoiceData.party || {
      name: this.invoiceData.customerName || '-',
      phone: this.invoiceData.customerMobile || '-',
      mobile: this.invoiceData.customerMobile || '-',
    };

    const fontSize = theme.baseFontSize + 1;
    const customerName = (billToFields?.showCustomerName !== false) ? (party.name || '-') : '';
    const customerPhone = (billToFields?.showCustomerPhone !== false) ? (party.phone || party.mobile || '-') : '';

    if (!customerName && !customerPhone) {
      return { text: '', margin: [0, 0, 0, 0] };
    }
    // console.log(customerName, customerPhone, 'customerName, customerPhone')
    // Old layout: Bill To (left) | Ph (right) in one row
    // return {
    //   columns: [
    //     { text: customerName ? `Bill To: ${customerName}` : '', fontSize, color: theme.textColor, width: '*', bold: true },
    //     { text: (customerPhone != "" && customerPhone != '-') ? `Ph: ${customerPhone}` : '', fontSize, color: theme.textColor, width: 'auto', alignment: 'right', bold: true },
    //   ],
    //   margin: [0, 2, 0, 2] //[0, 2, 0, 2],
    // };

    // Bill To | Mobile in one row — same 2-column grid as thermal invoice details
    const showName = !!customerName;
    const showPhone = !!customerPhone && customerPhone !== '-';
    if (!showName && !showPhone) {
      return { text: '', margin: [0, 0, 0, 0] };
    }
    const colon = (show: boolean) => ({ text: show ? ':' : '', bold: true, fontSize, color: theme.textColor, noWrap: true });
    const partyRows: any[][] = [[
      showName
        ? { text: 'Bill To', bold: true, fontSize, color: theme.textColor, noWrap: true }
        : { text: '' },
      colon(showName),
      showName
        ? { text: customerName, bold: true, fontSize, color: theme.textColor, font: this.getFontForText(customerName) }
        : { text: '' },
      showPhone
        ? { text: 'Mobile', bold: true, fontSize, color: theme.textColor, noWrap: true }
        : { text: '' },
      colon(showPhone),
      showPhone
        ? { text: customerPhone, bold: true, fontSize, color: theme.textColor, noWrap: true }
        : { text: '' },
    ]];

    return {
      table: {
        // left-label | : | left-value | right-label | : | right-value
        widths: ['auto', 'auto', '*', 'auto', 'auto', 'auto'],
        body: partyRows,
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: (i: number) => i === 0 ? 0 : 2,
        paddingRight: (i: number) => i === 1 || i === 4 ? 1 : 2, // tight around colons
        paddingTop: () => 1,
        paddingBottom: () => 1,
      },
      margin: [0, 1, 0, 1],
    };
  }

  private buildThermalItemsTable(itemsOverride?: any[], showSummary: boolean = true): Content {
    const tableConfig = this.config.itemTable;
    const theme = this.config.themeSettings;
    const currencySymbol = this.config.additionalSettings.currencySymbol;
    const totalsConfig = this.config.totalsSection;
    const borderColor = tableConfig.bodyStyle.borderColor || theme.borderColor || '#000000';
    const fontSize = theme.baseFontSize;
    const headerFontSize = theme.baseFontSize;
    const itemsTableLayout = this.config.layoutSettings.itemsTableLayout || 'standard';
    const lineColor = theme.textColor || '#000000';
    // console.log(this.config, 'settings')
    const allThermalColumns = [
      { key: 'sno', label: 'S.No', align: 'center' },
      { key: 'name', label: 'Description', align: 'left' },
      { key: 'mrp', label: 'MRP', align: 'center' },
      { key: 'quantity', label: 'Qty', align: 'center' },
      { key: 'unit', label: 'Unit', align: 'center' },
      { key: 'price', label: 'Rate', align: 'center' },
      { key: 'total', label: 'Amount', align: 'center ' },
    ];
    // console.log(tableConfig.columns, 'tableConfig.columns')

    // Filter columns based on API config (show: true/false)
    const thermalColumns = allThermalColumns.filter((col) => {
      if (tableConfig.columns && tableConfig.columns.length > 0) {
        const apiCol = tableConfig.columns.find((c: any) => c.key === col.key);
        // If column exists in API config, respect its show flag
        if (apiCol) return apiCol.show;
      }
      // Columns not in API config (e.g. mrp) — show by default
      return true;
    });

    // console.log(thermalColumns, 'thermalColumns')
    const headerRow = thermalColumns.map((col) => ({
      text: col.label,
      bold: true,
      alignment: ['mrp', 'quantity', 'price', 'total'].includes(col.key)
        ? 'right'
        : col.align,
      fontSize: headerFontSize + 2,
      fillColor: tableConfig.headerStyle.backgroundColor,
      color: tableConfig.headerStyle.textColor,
    }));
    console.log(itemsOverride, this.invoiceData)
    // console.log(headerRow, 'headerRow')
    const itemsToRender = itemsOverride || this.invoiceData.items;
    // console.log(itemsToRender, 'itemsToRender')
    const bodyRows = itemsToRender.map((item, index) => {
      return thermalColumns.map((col) => {
        let value = '';
        switch (col.key) {
          case 'sno':
            value = (index + 1).toString();
            break;
          case 'name':
            const nameParts = [];
            const isFreeItem = Number(item.is_free) === 1;
            console.log('isFreeItem:', isFreeItem, item);
            if (item.name && (tableConfig.showProductName || (tableConfig.showProductName === undefined && tableConfig.showRefName === undefined))) {
              nameParts.push({ text: item.name, bold: true, font: this.getFontForText(item.name) });
            }
            if (isFreeItem) {
              nameParts.push({ text: ' [FREE]', bold: true, font: this.getFontForText('FREE') });
            }
            if (tableConfig.showRefName && item.product?.reference_name) {
              nameParts.push({ text: item.product.reference_name, bold: true, font: this.getFontForText(item.product.reference_name) });
            }
            // Selected mapping (variants / parameters) — one per line, small font
            getSelectedMappingLines(item).forEach((line) => {
              nameParts.push({ text: '\n' + line, bold: false, fontSize: 7, font: this.getFontForText(line) });
            });
            if (tableConfig.showItemDescription && item.description) {
              nameParts.push({ text: '\n' + item.description, bold: true, font: this.getFontForText(item.description) });
            }

            if ((tableConfig as any).showItemNote !== false) {
              const itemNote = item.itemNote || item.note || '';
              if (itemNote) {
                nameParts.push({ text: '\n' + itemNote, bold: false, font: this.getFontForText(itemNote) });
              }
            }
            return {
              text: nameParts.length > 0 ? nameParts : [{ text: '-', font: this.getFontForText('-') }],
              alignment: col.align,
              fontSize: fontSize + 1,
              color: theme.textColor,
            };
          case 'mrp':
            value = item.mrp ? this.formatCurrency(item.mrp, '') : this.formatCurrency(item.price, '');
            break;
          case 'quantity':
            value = (item.quantity || 0).toString();
            break;
          case 'unit':
            // value = item.unit?.name || '-';
            value = getItemUnitName(item) || '-';
            break;
          case 'price':
            if (totalsConfig.showInclusiveTax) {
              const qty = item.quantity || 1;

              const taxPerUnit =
                (item.taxamount || item.taxAmount || 0) / qty;

              value = this.formatCurrency(Number(item.price || 0) + taxPerUnit, '');
            } else {
              value = this.formatCurrency(Number(item.price || 0), '');
            }
            break;

          case 'total':
            if (Number(item.is_free) === 1) {
              value = this.formatCurrency(0, '');
            } else if (totalsConfig.showInclusiveTax) {
              const qty = item.quantity || 1;

              const taxPerUnit =
                (item.taxamount || item.taxAmount || 0) / qty;

              const inclusiveRate = Number(item.price || 0) + taxPerUnit;

              value = this.formatCurrency(inclusiveRate * qty, '');
            } else {
              value = this.formatCurrency(Number(item.total || 0), '');
            }
            break;
          // case 'price':
          //   if (totalsConfig.showInclusiveTax) {
          //     const qty = item.quantity || 1;
          //     const taxPerUnit = (item.taxamount || item.taxAmount || 0) / qty;
          //     value = this.formatCurrency(item.price + taxPerUnit, currencySymbol);
          //   } else {
          //     value = this.formatCurrency(item.price, currencySymbol);
          //   }
          //   break;
          // case 'total':
          //   console.log(totalsConfig.showInclusiveTax, 'totalsConfig.showInclusiveTax')
          //   if (totalsConfig.showInclusiveTax) {
          //     const qty = item.quantity || 1;
          //     const taxPerUnit = (item.taxamount || item.taxAmount || 0) / qty;
          //     const inclusiveRate = item.price + taxPerUnit;
          //     value = this.formatCurrency(inclusiveRate * qty, currencySymbol);
          //   } else {
          //     value = this.formatCurrency(item.total || 0, currencySymbol);
          //   }
          //   break;
        }

        return {
          text: value,
          font: this.getFontForText(value),
          alignment: col.align,
          fontSize: fontSize + 1,
          color: theme.textColor,
          bold: true
        };
      });
    });

    const allRows: any[][] = [headerRow, ...bodyRows];

    let layout: any;
    if (itemsTableLayout === 'bordered') {
      layout = {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        fillColor: (rowIndex: number) => {
          if (rowIndex === 0) return tableConfig.headerStyle.backgroundColor;
          return rowIndex % 2 === 0 ? tableConfig.bodyStyle.alternateRowColor : null;
        },
        paddingLeft: () => 2,
        paddingRight: () => 2,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      };
    } else if (itemsTableLayout === 'compact') {
      layout = {
        hLineWidth: (i: number) => (i === 0 || i === 1) ? 0.5 : 0,
        vLineWidth: () => 0,
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        paddingLeft: () => 2,
        paddingRight: () => 2,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      };
    } else {
      layout = {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0,
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        fillColor: (rowIndex: number) => {
          if (rowIndex === 0) return tableConfig.headerStyle.backgroundColor;
          return rowIndex % 2 === 0 ? tableConfig.bodyStyle.alternateRowColor : null;
        },
        paddingLeft: () => 2,
        paddingRight: () => 2,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      };
    }

    const content: any[] = [];

    content.push({
      table: {
        headerRows: 1,
        widths: thermalColumns.map((col) => col.key === 'name' ? '*' : 'auto'),
        body: allRows,
      },
      layout,
      margin: [0, 0, 0, 0],
    });

    // Summary row: Details, T.Qty and Total without background, then dashed line below
    if (showSummary && totalsConfig.showGrandTotal) {
      const totalQuantity = itemsToRender.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0);
      const grandTotal = this.invoiceData.totalAmount || this.invoiceData.grandTotal || 0;
      const loyaltyAmount = Number(this.invoiceData.loyaltyAmount) || 0;
      const finalTotal = grandTotal - loyaltyAmount;

      const dashedLineDef = {
        table: {
          widths: ['*'],
          body: [[{ text: '', border: [false, true, false, false] }]],
        },
        layout: {
          hLineWidth: (i: number) => (i === 0 ? 0.5 : 0),
          vLineWidth: () => 0,
          hLineColor: () => lineColor,
          hLineStyle: () => ({ dash: { length: 3, space: 2 } }),
          paddingTop: () => 2,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 2] as number[],
      };

      // Add ITEM QTY and T QTY line
      const itemQty = itemsToRender.length;

      // Replace the content.push({ text: [...] }) block with this:

      const summaryRow: any[] = [
        {
          text: [
            { text: 'ITEM QTY : ', bold: true, fontSize },
            { text: itemQty.toString(), bold: true, fontSize },
          ],
          colSpan: 2,
          alignment: 'left',
          fontSize,
          border: [false, false, false, false],
        },
        {}, // colSpan placeholder
        {
          text: [
            { text: 'T QTY : ', bold: true, fontSize },
            { text: totalQuantity.toString(), bold: true, fontSize },
          ],
          colSpan: 2,
          alignment: 'right',
          fontSize,
          border: [false, false, false, false],
        },
        {}, // placeholder
        { text: '' }, // placeholder
        { text: '' }, // placeholder
      ];

      content.push({
        table: {
          widths: ['*', '*', '*', '*', '*', '*',],
          body: [summaryRow],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 2,
          paddingRight: () => 0,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
        margin: [0, 2, 0, 2],
      });

      // Dashed line below summary row
      const simpleDashedLine = {
        table: {
          widths: ['*'],
          dontBreakRows: true,
          keepWithHeaderRows: 1,
          body: [[{ text: '', border: [false, true, false, false] }]],
        },
        layout: {
          hLineWidth: (i: number) => (i === 0 ? 0.5 : 0),
          vLineWidth: () => 0,
          hLineColor: () => lineColor,
          vLineColor: () => lineColor,
          hLineStyle: () => ({ dash: { length: 3, space: 2 } }),
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 1,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 1] as number[],
      };
      content.push(simpleDashedLine);
    }

    return {
      stack: content,
      margin: [0, 0, 0, 0],
    };
  }

  private buildA5PortraitItemsTable(
    itemsOverride?: any[],
    showSummary: boolean = true,
    fillerHeight: number = 0,
    isExpanded: boolean = false,
  ): Content {
    const tableConfig = this.config.itemTable;
    const theme = this.config.themeSettings;
    const currencySymbol = this.config.additionalSettings.currencySymbol;
    const totalsConfig = this.config.totalsSection;
    const borderColor = tableConfig.bodyStyle.borderColor || theme.borderColor || '#000000';
    const itemsToRender = itemsOverride || this.invoiceData.items;

    // ✅ Read layout from config (same as buildItemsTable)
    const itemsTableLayout = (
      this.config.layoutSettings.itemsTableLayout || 'standard'
    ).toLowerCase();

    // ✅ Full column definitions including batch/mfg/exp
    const allBaseColumns = [
      { key: 'sno', label: 'S.NO.', width: 22, align: 'center', configKey: 'showSerialNumber' },
      { key: 'name', label: 'ITEMS', width: '*', align: 'left', configKey: 'showItemDescription' },
      { key: 'hsn', label: 'HSN', width: 'auto', align: 'center', configKey: 'showHSN' },
      { key: 'batch', label: 'BATCH', width: 'auto', align: 'center', configKey: 'showBatchNo' },
      { key: 'mfgDate', label: 'MFG', width: 'auto', align: 'center', configKey: 'showMfgDate' },
      { key: 'expDate', label: 'EXP', width: 'auto', align: 'center', configKey: 'showExpDate' },
      { key: 'quantity', label: 'QTY', width: 'auto', align: 'center', configKey: 'showQuantity' },
      { key: 'unit', label: 'UNIT', width: 'auto', align: 'center', configKey: 'showUnit' },
      { key: 'mrp', label: 'MRP', width: 'auto', align: 'right', configKey: 'showMRP' },
      { key: 'price', label: 'RATE', width: 'auto', align: 'right', configKey: 'showPrice' },
      { key: 'discount', label: 'DISC(%)', width: 'auto', align: 'right', configKey: 'showDiscountAmount' },
      { key: 'taxableAmount', label: 'TAXABLE', width: 'auto', align: 'right', configKey: 'showTaxableAmount' },
      { key: 'taxAmount', label: 'TAX(%)', width: 'auto', align: 'right', configKey: 'showTaxAmount' },
      { key: 'total', label: 'AMOUNT', width: 'auto', align: 'right', configKey: 'showAmount' },
    ];

    // ✅ Filter columns based on tableConfig show* flags
    const visibleColumns = allBaseColumns.filter(col => {
      if (tableConfig.columns && tableConfig.columns.length > 0) {
        const configCol = tableConfig.columns.find((c: any) => c.key === col.key);
        if (configCol) return configCol.show;
      }
      // Fallback to show* boolean flags on tableConfig
      const flagKey = col.configKey as keyof typeof tableConfig;
      if (flagKey in tableConfig) {
        return !!(tableConfig as any)[flagKey];
      }
      // Always show core columns if no config
      return ['sno', 'name', 'quantity', 'price', 'total'].includes(col.key);
    });

    const columnWidths = visibleColumns.map(c => c.width);
    const colCount = visibleColumns.length;

    const baseFontSize = tableConfig.bodyStyle?.fontSize || 8;
    const responsiveFontSize = colCount >= 10
      ? Math.max(baseFontSize - 2, 6)
      : colCount >= 8
        ? Math.max(baseFontSize - 1, 7)
        : baseFontSize;
    const headerFontSize = Math.min(8, responsiveFontSize);
    const cellPadding = colCount >= 10 ? 2 : colCount >= 8 ? 3 : 4;

    // ── Header row ─────────────────────────────────────────────────────────────
    const headerRow = visibleColumns.map(col => ({
      text: col.label,
      alignment: col.align,
      fillColor: tableConfig.headerStyle.backgroundColor,
      color: tableConfig.headerStyle.textColor,
      fontSize: headerFontSize,
      bold: true,
      noWrap: true,
    }));

    // ── Body rows ──────────────────────────────────────────────────────────────
    const bodyRows = itemsToRender.map((item: any, index: number) => {
      return visibleColumns.map(col => {
        switch (col.key) {

          case 'sno':
            return {
              text: (index + 1).toString(),
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };

          case 'name': {
            const nameStack: any[] = [];
            if (item.name && (tableConfig.showProductName || (tableConfig.showProductName === undefined && tableConfig.showRefName === undefined))) {
              let mainName = item.name || '-';
              const isFreeItem = Number(item.is_free) === 1;
              if (isFreeItem) {
                mainName += ' [Free]';
              }
              nameStack.push({
                text: mainName,
                font: this.getFontForText(mainName),
                fontSize: responsiveFontSize,
                color: theme.textColor,
              });
            }
            if (tableConfig.showRefName && item.product?.reference_name) {
              const mainName = item.product?.reference_name || '-';
              nameStack.push({
                text: mainName,
                font: this.getFontForText(mainName),
                fontSize: responsiveFontSize,
                color: theme.textColor,
              });
            }
            // Selected mapping (variants / parameters) — joined into ONE line (fixed row heights)
            const mappingLines = getSelectedMappingLines(item);
            if (mappingLines.length > 0) {
              const mappingText = mappingLines.join(', ');
              nameStack.push({
                text: mappingText,
                font: this.getFontForText(mappingText),
                fontSize: Math.max(responsiveFontSize - 1, 5),
                color: theme.textColor,
              });
            }
            if (tableConfig.showItemDescription && item.description) {
              nameStack.push({
                text: item.description,
                font: this.getFontForText(item.description),
                fontSize: Math.max(responsiveFontSize - 1, 5),
                color: theme.textColor,
                italics: true,
              });
            }
            const itemNote = item.itemNote || item.note || '';
            if ((tableConfig as any).showItemNote !== false && itemNote) {
              nameStack.push({
                text: itemNote,
                fontSize: Math.max(responsiveFontSize - 1, 5),
                color: theme.textColor,
                italics: true,
              });
            }
            return { stack: nameStack, alignment: col.align, noWrap: false };
          }

          case 'hsn':
            return {
              text: item.hsn?.toString() || item.hsncode?.toString() || '-',
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };

          // ✅ New columns
          case 'batch':
            return {
              text: item.batch || item.batchNo || '-',
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };

          case 'mfgDate':
            return {
              text: item.mfgDate || '-',
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };

          case 'expDate':
            return {
              text: item.expDate || '-',
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };

          case 'quantity':
            return {
              text: (item.quantity || 0).toString(),
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };

          case 'unit':
            return {
              // text: item.unit?.name || '-',
              text: getItemUnitName(item) || '-',
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };

          case 'mrp':
            return {
              text: item.mrp
                ? this.formatCurrency(item.mrp, currencySymbol)
                : this.formatCurrency(item.price || 0, currencySymbol),
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };

          case 'price': {
            const qty = item.quantity || 1;
            const taxPerUnit = (item.taxamount || item.taxAmount || 0) / qty;
            const showInclusive = !!totalsConfig.showInclusiveTax;
            return {
              text: this.formatCurrency(
                showInclusive ? item.price + taxPerUnit : item.price || 0,
                currencySymbol,
              ),
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };
          }

          case 'discount': {
            // let dAmt = item.discountamt || item.discountAmount || 0;
            // let dPct = typeof item.discountPercent === 'string'
            //   ? parseFloat(item.discountPercent)
            //   : (item.discountPercent || 0);
            // Free item → no discount (same as A5 landscape)
            const isFreeDisc = Number(item.is_free) === 1;
            let dAmt = isFreeDisc ? 0 : (item.discountamt || item.discountAmount || 0);
            let dPct = isFreeDisc ? 0 : (typeof item.discountPercent === 'string'
              ? parseFloat(item.discountPercent)
              : (item.discountPercent || 0));
            if (dAmt > 0 && item.quantity) dAmt = dAmt * item.quantity;
            const dPctStr = dPct % 1 === 0 ? dPct.toFixed(0) : dPct.toFixed(1);
            return {
              text: `${this.formatCurrency(dAmt, currencySymbol)} (${dPctStr}%)`,
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };
          }

          case 'taxableAmount': {
            const taxableAmt = item.discaftertax
              ? (item.price * item.quantity)
              : (item.price * item.quantity) - ((item.discountamt || 0) * item.quantity);
            // Free item → taxable amount is always 0 (same as A5 landscape)
            return {
              text: Number(item.is_free) === 1
                ? this.formatCurrency(0, currencySymbol)
                : taxableAmt
                ? this.formatCurrency(taxableAmt, currencySymbol)
                : '-',
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };
          }

          case 'taxAmount': {
            // const tAmt = item.taxAmount || item.taxamount || 0;
            // const tPct = item.taxRate || item.taxPercent || item.tax || 0;
            // Free item → no tax (same as A5 landscape)
            const isFreeTax = Number(item.is_free) === 1;
            const tAmt = isFreeTax ? 0 : (item.taxAmount || item.taxamount || 0);
            const tPct = isFreeTax ? 0 : (item.taxRate || item.taxPercent || item.tax || 0);
            return {
              text: `${this.formatCurrency(tAmt, currencySymbol)} (${tPct}%)`,
              alignment: col.align,
              fontSize: responsiveFontSize,
              color: theme.textColor,
            };
          }

          case 'total': {
            const qty = item.quantity || 1;
            const taxPerUnit = (item.taxamount || item.taxAmount || 0) / qty;
            const showInclusive = !!totalsConfig.showInclusiveTax;
            const inclusiveTotal = (item.price + taxPerUnit) * qty;
            const taxableBase = item.discaftertax
              ? (item.price * qty)
              : (item.total || 0);
            const taxAmt2 = item.taxamount || item.taxAmount || 0;
            const totalValue = showInclusive
              ? inclusiveTotal
              : (item.netValue || (taxableBase + taxAmt2) || 0);
            // Free item → amount is always 0 (same as buildItemsTable / A5 landscape)
            return {
              text: this.formatCurrency(Number(item.is_free) === 1 ? 0 : totalValue, currencySymbol),
              alignment: col.align,
              fontSize: responsiveFontSize,
              bold: true,
              color: theme.textColor,
            };
          }

          default:
            return { text: '', alignment: col.align, fontSize: responsiveFontSize };
        }
      });
    });

    // const allRows = [headerRow, ...bodyRows];
    // Filler row: extends vertical border lines down to footer top border
    const hasFiller = fillerHeight > 0;
    // Filler cells keep their left/right borders so vertical column lines
    // continue through the empty space and touch the TOTAL row top border.
    // The TOTAL row (separate pdfmake table) draws its own top border (0.5)
    // which acts as the natural stopping line — no bleed into Section 2.
    const fillerRow: any[] | null = hasFiller
      ? visibleColumns.map(() => ({ text: '' }))
      : null;

    const allRows = [headerRow, ...bodyRows, ...(fillerRow ? [fillerRow] : [])];

    // ✅ Layout respects itemsTableLayout config — same as buildItemsTable
    let layout: any;
    if (itemsTableLayout === 'bordered') {
      layout = {
        hLineWidth: (i: number, node: any) => {
          const fillerRowIndex = bodyRows.length + 1;
          if (hasFiller && i === fillerRowIndex + 1) return 0; // filler bottom → hidden
          if (hasFiller && i === fillerRowIndex) return 0.3;   // items real bottom → draw
          // Bottom border: draw only when filler present (non-last page in multipage)
          if (i === node.table.body.length) return hasFiller ? 0.3 : 0;
          return 0.3;
        },
        vLineWidth: (i: number, node: any) => {
          if (i === 0 || i === node.table.widths.length) return 0;
          return 0.3;
        },
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        fillColor: (rowIndex: number) =>
          rowIndex === 0
            ? tableConfig.headerStyle.backgroundColor
            : hasFiller && rowIndex === bodyRows.length + 1
              ? null
              : rowIndex % 2 === 0
                ? tableConfig.bodyStyle.alternateRowColor
                : null,
        paddingLeft: () => cellPadding,
        paddingRight: () => cellPadding,
        paddingTop: () => cellPadding,
        paddingBottom: () => cellPadding,
      };
    } else if (itemsTableLayout === 'compact') {
      layout = {
        hLineWidth: (i: number, node: any) => {
          if (i === node.table.body.length) return 0;
          return (i === 0 || i === 1) ? 0.3 : 0;
        },
        vLineWidth: () => 0,
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        fillColor: (rowIndex: number) =>
          rowIndex === 0 ? tableConfig.headerStyle.backgroundColor : null,
        paddingLeft: () => cellPadding - 1,
        paddingRight: () => cellPadding - 1,
        paddingTop: () => cellPadding - 1,
        paddingBottom: () => cellPadding - 1,
      };
    } else {
      // standard
      layout = {
        hLineWidth: (i: number, node: any) => {
          const fillerRowIndex = bodyRows.length + 1;
          if (hasFiller && i === fillerRowIndex + 1) return 0;
          if (hasFiller && i === fillerRowIndex) return 0.3;
          if (i === node.table.body.length) return 0;
          return 0.3;
        },
        vLineWidth: () => 0,
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        fillColor: (rowIndex: number) =>
          rowIndex === 0
            ? tableConfig.headerStyle.backgroundColor
            : hasFiller && rowIndex === bodyRows.length + 1
              ? null
              : rowIndex % 2 === 0
                ? tableConfig.bodyStyle.alternateRowColor
                : null,
        paddingLeft: () => cellPadding,
        paddingRight: () => cellPadding,
        paddingTop: () => cellPadding,
        paddingBottom: () => cellPadding,
      };
    }
    const isExpandablePage =
      (this.config.pageSettings.pageSize === 'A4' && this.config.pageSettings.orientation !== 'landscape') ||
      (this.config.pageSettings.pageSize === 'A5' && this.config.pageSettings.orientation !== 'landscape');

    const tableRowHeight = isExpandablePage && isExpanded
      ? (tableConfig.bodyStyle.rowHeight || 20) + 16
      : (tableConfig.bodyStyle.rowHeight || 16);
    return {
      table: {
        headerRows: 1,
        widths: columnWidths,
        body: allRows,
        heights: (rowIndex: number) => {
          if (rowIndex === 0) return 'auto';
          // Filler row stretches to fill gap between items table and footer
          if (hasFiller && rowIndex === bodyRows.length + 1) return fillerHeight;
          return tableRowHeight;
        },
      },
      layout,
      margin: [0, 0, 0, 0],
    };
  }

  buildA5PortraitFooterSection(): Content | null {
    const tableConfig = this.config.itemTable;
    const totalsConfig = this.config.totalsSection;
    const footerConfig = this.config.footer;
    const theme = this.config.themeSettings;
    const currencySymbol = this.config.additionalSettings.currencySymbol;
    const borderColor = tableConfig.bodyStyle.borderColor || theme.borderColor || '#000000';
    const fontSize = theme.baseFontSize + 1;

    const visibleColumns = this.getVisibleColumnsBasedOnData(tableConfig);
    const columnWidths = this.calculateResponsiveWidths(visibleColumns, visibleColumns.length);
    const itemsTableLayout = 'bordered';

    const content: any[] = [];

    // ══════════════════════════════════════════════════════════════════════
    // SECTION 1 — TOTAL summary row (same widths as items table)
    // ══════════════════════════════════════════════════════════════════════
    const totalQuantity = this.invoiceData.items.reduce(
      (sum, item) => sum + (item.quantity || 0), 0);
    const totalDiscount =
      (this.invoiceData.itemDiscount || 0) +
      (this.invoiceData.overallDiscount || 0) ||
      this.invoiceData.discount || 0;

    // Calculate total tax amount across all items
    const totalTaxAmount = this.invoiceData.items.reduce(
      (sum, item) => sum + (item.taxamount || item.taxAmount || 0), 0);

    const grandTotal = this.invoiceData.totalAmount || this.invoiceData.grandTotal || 0;
    const loyaltyAmount = Number(this.invoiceData.loyaltyAmount) || 0;
    const finalTotal = grandTotal - loyaltyAmount;

    // Exclude free items from subtotal (free item amount shows 0 in the table row)
    const freeItemsAmount = this.invoiceData.items.reduce(
      (sum, item: any) => Number(item.is_free) === 1
        ? sum + Number(item.amount || (item.price || 0) * (item.quantity || 0))
        : sum, 0);
    const subtotalExclFree = Math.max(0, Number(this.invoiceData.subtotal || 0) - freeItemsAmount);
    // / Build only the non-empty summary cells as a compact right-aligned row
    const summaryTableBody: any[][] = [[
      {
        text: 'TOTAL',
        alignment: 'left',
        fontSize,
        bold: true,
        color: theme.textColor,
      },
      {
        text: `T.Qty: ${totalQuantity}`,
        alignment: 'center',
        fontSize,
        bold: true,
        color: theme.textColor,
      },
      {
        // subtotal value aligned right
        text: totalsConfig.showSubtotal
          // ? this.formatCurrency(this.invoiceData.subtotal || 0, currencySymbol)
          ? this.formatCurrency(subtotalExclFree, currencySymbol)
          : '',
        alignment: 'right',
        fontSize,
        bold: true,
        color: theme.textColor,
      },
    ]];

    content.push(this.applyFooterScaling({
      table: {
        widths: ['*', 'auto', 80],  // label | qty | amount — no stretching
        body: summaryTableBody,
      },
      heights: () => 20,
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0,         // no vertical lines — keeps it clean
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        paddingLeft: () => 3,
        paddingRight: () => 3,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
      margin: [0, 0, 0, 0],
    }));


    // ══════════════════════════════════════════════════════════════════════
    // SECTION 2 — Payment info (left) + Tax breakdown + Grand Total (right)
    // ══════════════════════════════════════════════════════════════════════
    const receivedAmount = this.invoiceData.paidamount || 0;
    const previousBalance = this.invoiceData.previousBalance || 0;
    const currentBalance = this.invoiceData.currentBalance || 0;

    // Left: payment rows
    const paymentRows: any[] = [];
    if (totalsConfig.showReceivedAmount !== false) {
      paymentRows.push({
        columns: [
          { text: 'Received Amount:', bold: true, fontSize, color: theme.textColor, width: '*' },
          { text: this.formatCurrency(receivedAmount, currencySymbol), fontSize, color: theme.textColor, width: 60, alignment: 'right' },
        ],
        margin: [3, 2, 3, 1],
      });
    }
    if (totalsConfig.showPreviousBalance !== false) {
      paymentRows.push({
        columns: [
          { text: 'Previous Balance:', bold: true, fontSize, color: theme.textColor, width: '*' },
          { text: this.formatCurrency(previousBalance, currencySymbol), fontSize, color: theme.textColor, width: 60, alignment: 'right' },
        ],
        margin: [3, 1, 3, 1],
      });
    }
    if (totalsConfig.showCurrentBalance !== false) {
      paymentRows.push({
        columns: [
          { text: 'Current Balance:', bold: true, fontSize, color: theme.textColor, width: '*' },
          { text: this.formatCurrency(currentBalance, currencySymbol), fontSize, color: theme.textColor, width: 60, alignment: 'right' },
        ],
        margin: [3, 1, 3, 3],
      });
    }

    // Right: tax breakdown + grand total
    const taxRows: any[] = [];
    if (totalsConfig.showTaxBreakup !== false) {
      if (totalsConfig.showCGST !== false && this.invoiceData.cgst) {
        taxRows.push({
          columns: [
            { text: 'CGST', bold: true, fontSize, color: theme.textColor, width: '*' },
            { text: ':', fontSize, color: theme.textColor, width: 8, alignment: 'center' },
            { text: this.formatCurrency(this.invoiceData.cgst, currencySymbol), fontSize, color: theme.textColor, width: 70, alignment: 'right' },
          ],
          margin: [3, 2, 3, 1],
        });
      }
      if (totalsConfig.showSGST !== false && this.invoiceData.sgst) {
        taxRows.push({
          columns: [
            { text: 'SGST', bold: true, fontSize, color: theme.textColor, width: '*' },
            { text: ':', fontSize, color: theme.textColor, width: 8, alignment: 'center' },
            { text: this.formatCurrency(this.invoiceData.sgst, currencySymbol), fontSize, color: theme.textColor, width: 70, alignment: 'right' },
          ],
          margin: [3, 1, 3, 1],
        });
      }
      if (totalsConfig.showIGST !== false && this.invoiceData.igst) {
        taxRows.push({
          columns: [
            { text: 'IGST', bold: true, fontSize, color: theme.textColor, width: '*' },
            { text: ':', fontSize, color: theme.textColor, width: 8, alignment: 'center' },
            { text: this.formatCurrency(this.invoiceData.igst, currencySymbol), fontSize, color: theme.textColor, width: 70, alignment: 'right' },
          ],
          margin: [3, 1, 3, 1],
        });
      }
    }
    if (totalsConfig.showGrandTotal !== false) {
      taxRows.push({
        columns: [
          { text: 'Grand Total', bold: true, fontSize: fontSize + 1, color: theme.primaryColor, width: '*' },
          { text: ':', fontSize: fontSize + 1, color: theme.primaryColor, width: 8, alignment: 'center' },
          { text: this.formatCurrency(finalTotal, currencySymbol), bold: true, fontSize: fontSize + 1, color: theme.primaryColor, width: 70, alignment: 'right' },
        ],
        margin: [3, 2, 3, 3],
      });
    }

    // Combine left + right into a 2-column bordered table row
    if (paymentRows.length > 0 || taxRows.length > 0) {
      content.push(this.applyFooterScaling({
        table: {
          widths: ['*', '45%'],
          body: [[
            { stack: paymentRows.length > 0 ? paymentRows : [{ text: '', margin: [3, 2, 3, 2] }] },
            { stack: taxRows.length > 0 ? taxRows : [{ text: '', margin: [3, 2, 3, 2] }] },
          ]],
        },
        heights: () => 50,
        layout: {
          hLineWidth: (i: number, node: any) => 0,
          vLineWidth: (i: number, node: any) => 0,
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0],
      }));
    }

    // ══════════════════════════════════════════════════════════════════════
    // SECTION 3 — Amount in Words (full width)
    // ══════════════════════════════════════════════════════════════════════
    if (totalsConfig.showAmountInWords) {
      const amountInWords = this.numberToWords(finalTotal);
      const prefix = totalsConfig.amountInWordsPrefix || 'Rupees: ';
      const suffix = totalsConfig.amountInWordsSuffix || ' Only';
      const fullText = `Amount Chargeable (in words):  ${prefix}${amountInWords}${suffix}`;

      content.push(this.applyFooterScaling({
        table: {
          widths: ['*'],
          body: [[{
            text: fullText,
            font: this.getFontForText(fullText),
            fontSize: fontSize - 1,
            italics: totalsConfig.amountInWordsItalic !== undefined
              ? totalsConfig.amountInWordsItalic : false,
            bold: false,
            color: theme.textColor,
            margin: [3, 3, 3, 3],
          }]],
        },
        heights: () => 20,
        layout: {
          hLineWidth: (i: number, node: any) => {
            return i === 0 ? 0.3 : 0; // Only top border
          },
          vLineWidth: () => 0, // Removes left & right vertical borders
          // hLineColor: () => '#cccccc',
          hLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0],
      }));
    }

    // ══════════════════════════════════════════════════════════════════════
    // SECTION 4 — 3-column footer: Bank Details | Terms | Authorised Signatory
    // ══════════════════════════════════════════════════════════════════════
    const businessName = this.invoiceData.business?.name
      || this.invoiceData.businessName || '';

    const col1: any[] = []; // Bank Details
    const col2: any[] = []; // Terms & Conditions
    const col3: any[] = []; // Authorised Signatory

    // ── Column 1: Bank Details + QR ──────────────────────────────────────
    if (footerConfig.showBankDetails) {
      col1.push({
        text: 'BANK DETAILS',
        fontSize: fontSize - 1,
        bold: true,
        color: theme.primaryColor,
        margin: [0, 0, 0, 3],
      });
      const bankDetails = this.invoiceData.bankDetails;
      if (bankDetails) {
        if (bankDetails.bankName)
          col1.push({ text: `Name: ${bankDetails.bankName}`, fontSize: fontSize - 1, color: theme.textColor });
        if (bankDetails.ifscCode)
          col1.push({ text: `IFSC: ${bankDetails.ifscCode}`, fontSize: fontSize - 1, color: theme.textColor });
        if (bankDetails.accountNumber)
          col1.push({ text: `Acc No: ${bankDetails.accountNumber}`, fontSize: fontSize - 1, color: theme.textColor });
        if (bankDetails.upiId)
          col1.push({ text: `UPI: ${bankDetails.upiId}`, fontSize: fontSize - 1, color: theme.textColor });
      }

      // QR Code
      const qrEnabled = (footerConfig as any).showQrCode ?? footerConfig.showQRCode ?? false;
      if (qrEnabled && this.invoiceData.qrcode) {
        col1.push({
          image: this.invoiceData.qrcode,
          fit: [35, 35],
          alignment: 'left',
          margin: [0, 4, 0, 0],
        });
      }
    }

    // ── Column 2: Terms & Conditions ─────────────────────────────────────
    if (footerConfig.showTermsAndConditions) {
      col2.push({
        text: 'TERMS AND CONDITIONS',
        fontSize: fontSize - 1,
        bold: true,
        color: theme.primaryColor,
        margin: [0, 0, 0, 3],
      });
      const termsText = footerConfig.termsText
        || this.invoiceData.termsAndConditions
        || this.invoiceData.terms || '';
      if (termsText) {
        const points = termsText
          .split('\n')
          .filter((p: string) => p.trim() !== '');
        points.forEach((p: string, i: number) => {
          const clean = p.trim().replace(/^\d+[\.\)]\s*/, '');
          col2.push({
            text: `${i + 1}. ${clean}`,
            fontSize: fontSize - 1,
            color: theme.textColor,
            margin: [0, 1, 0, 0],
          });
        });
      }
    }

    // ── Column 3: Authorised Signatory ───────────────────────────────────
    if (footerConfig.showSignature) {
      col3.push({
        text: 'AUTHORISED SIGNATORY FOR',
        fontSize: fontSize - 1,
        bold: true,
        color: theme.primaryColor,
        alignment: 'center',
        margin: [0, 0, 0, 3],
      });

      const signatureData = this.invoiceData.signature
        || (this.invoiceData.business as any)?.signature
        || footerConfig.signature;
      if (signatureData) {
        col3.push({
          image: signatureData,
          fit: [50, 30],
          alignment: 'center',
          margin: [0, 4, 0, 4],
        });
      } else {
        col3.push({ text: '', margin: [0, 20, 0, 0] });
      }

      if (footerConfig.showForBusinessName && businessName) {
        col3.push({
          text: businessName,
          fontSize: fontSize - 1,
          bold: true,
          alignment: 'center',
          color: theme.textColor,
          margin: [0, 4, 0, 0],
        });
      }
    }

    const hasFooter3Col = footerConfig.showBankDetails
      || footerConfig.showTermsAndConditions
      || footerConfig.showSignature;


    if (hasFooter3Col) {

      // ─────────────────────────────────────────────
      // COLUMN 1 → BANK DETAILS + QR SIDE BY SIDE
      // ─────────────────────────────────────────────
      const bankLeft: any[] = [];
      const bankRight: any[] = [];

      if (footerConfig.showBankDetails) {

        bankLeft.push({
          text: 'BANK DETAILS',
          fontSize: fontSize - 1,
          bold: true,
          color: theme.primaryColor,
          margin: [0, 0, 0, 3],
        });

        const bankDetails = this.invoiceData.bankDetails;

        if (bankDetails) {

          if (bankDetails.bankName) {
            bankLeft.push({
              text: `Name: ${bankDetails.bankName}`,
              fontSize: fontSize - 1,
              color: theme.textColor,
              margin: [0, 1, 0, 0],
            });
          }

          if (bankDetails.ifscCode) {
            bankLeft.push({
              text: `IFSC: ${bankDetails.ifscCode}`,
              fontSize: fontSize - 1,
              color: theme.textColor,
              margin: [0, 1, 0, 0],
            });
          }

          if (bankDetails.accountNumber) {
            bankLeft.push({
              text: `Acc No: ${bankDetails.accountNumber}`,
              fontSize: fontSize - 1,
              color: theme.textColor,
              margin: [0, 1, 0, 0],
            });
          }

          if (bankDetails.upiId) {
            bankLeft.push({
              text: `UPI: ${bankDetails.upiId}`,
              fontSize: fontSize - 1,
              color: theme.textColor,
              margin: [0, 1, 0, 0],
            });
          }
        }

        // QR / Logo right side
        const qrEnabled =
          (footerConfig as any).showQrCode ??
          footerConfig.showQRCode ??
          false;

        if (qrEnabled) {
          if (this.invoiceData.qrcode) {
            bankRight.push({
              image: this.invoiceData.qrcode,
              fit: [55, 55],
              alignment: 'right',
              margin: [0, 2, 0, 0],
            });
          } else {
            // Black placeholder box same size as QR image
            bankRight.push({
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: 50,
                  h: 30,
                  color: '#fff',
                }
              ],
              alignment: 'right',
              // margin: [0, 2, 0, 0],
            });
          }
        }
      }

      // ─────────────────────────────────────────────
      // COLUMN 3 → SIGNATURE IMAGE + BUSINESS NAME
      // ─────────────────────────────────────────────
      const col3Fixed: any[] = [];

      if (footerConfig.showSignature) {

        col3Fixed.push({
          text: 'AUTHORISED SIGNATORY',
          fontSize: fontSize - 1,
          bold: true,
          color: theme.primaryColor,
          alignment: 'center',
          margin: [0, 0, 0, 4],
        });

        const signatureData =
          this.invoiceData.signature
          || (this.invoiceData.business as any)?.signature
          || footerConfig.signature;

        if (signatureData) {
          col3Fixed.push({
            image: signatureData,
            fit: [70, 40],
            alignment: 'center',
            margin: [0, 4, 0, 4],
          });
        } else {
          col3Fixed.push({
            canvas: [
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: 65,
                h: 40,
                color: '#fff',
              }
            ],
            alignment: 'center',
            // margin: [0, 4, 0, 4],
          });
        }

        if (footerConfig.showForBusinessName && businessName) {
          col3Fixed.push({
            text: businessName,
            fontSize: fontSize - 1,
            bold: true,
            alignment: 'center',
            color: theme.textColor,
            margin: [0, 2, 0, 0],
          });
        }
      }

      // ─────────────────────────────────────────────
      // FINAL TABLE
      // ─────────────────────────────────────────────

      content.push(this.applyFooterScaling({
        table: {
          widths: ['*', '*', '*'],
          body: [[

            // COLUMN 1
            {
              table: {
                widths: ['*', 'auto'],
                body: [[
                  {
                    stack: bankLeft.length > 0
                      ? bankLeft
                      : [{ text: ' ' }],
                    border: [false, false, false, false],
                  },
                  {
                    stack: bankRight.length > 0
                      ? bankRight
                      : [{ text: '' }],
                    border: [false, false, false, false],
                  }
                ]]
              },
              layout: 'noBorders',
              margin: [3, 3, 3, 3],
            },

            // COLUMN 2
            {
              stack: col2.length > 0
                ? col2
                : [{ text: ' ' }],
              margin: [3, 3, 3, 3],
            },

            // COLUMN 3
            {
              stack: col3Fixed.length > 0
                ? col3Fixed
                : [{ text: ' ' }],
              margin: [3, 3, 3, 3],
            },

          ]],

          // MINIMUM HEIGHT
          heights: () => {
            const itemCount = this.invoiceData.items?.length || 0;
            if (itemCount >= 1 && itemCount <= 3) {
              return 139; // Stretch footer more for few items
            } else if (itemCount >= 4 && itemCount <= 6) {
              return 120; // Stretch footer moderately
            } else {
              return 115;  // Default for 7-8 items
            }
          },
        },

        layout: {

          hLineWidth: (i: number, node: any) => {
            if (i === node.table.body.length) return 0;
            return 0.5;
          },

          vLineWidth: (i: number, node: any) => {

            if (i === 0) return 0;

            if (i === node.table.widths.length) return 0;

            return 0.5;
          },

          hLineColor: () => borderColor,
          vLineColor: () => borderColor,

          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },

        margin: [0, 0, 0, 0],
      }));
    }

    return content.length > 0
      ? ({ stack: content, margin: [0, 0, 0, 0] } as any)
      : null;
  }

  /** Text shown in an items-table cell; shared by the table rows and A4 column-width measuring */
  private getItemCellText(item: any, index: number, col: any, tableConfig: any, currencySymbol: string): string {
    let value = '';

    switch (col.key) {
      case 'sno':
        value = (index + 1).toString();
        break;
      case 'name':
        const nameParts = [];
        const isFreeItem = Number(item.is_free) === 1;
        if (item.name && (tableConfig.showProductName || (tableConfig.showProductName === undefined && tableConfig.showRefName === undefined))) {
          nameParts.push(item.name);
        }
        if (isFreeItem) {
          nameParts.push('[Free]');
        }
        if (item.product?.reference_name && tableConfig.showRefName) {
          nameParts.push(item.product?.reference_name);
        }
        // Selected mapping (variants / parameters) — one per line
        nameParts.push(...getSelectedMappingLines(item));
        if (tableConfig.showItemDescription && item.description) {
          nameParts.push(item.description);
        }
        if ((tableConfig as any).showItemNote !== false) {
          const itemNote = item.itemNote || item.note || '';
          if (itemNote) {
            nameParts.push(itemNote);
          }
        }
        value = nameParts.length > 0 ? nameParts.join('\n') : '-';
        break;
      case 'hsn':
      case 'hsncode':
        value = item.hsn?.toString() || item.hsncode?.toString() || '-';
        break;
      case 'batch':
        value = item.batch || '-';
        break;
      case 'expDate':
        value = item.expDate || '-';
        break;
      case 'mfgDate':
        value = item.mfgDate || '-';
        break;
      case 'qty':
      case 'quantity':
        value = (item.quantity || 0).toString();
        break;
      case 'unit':
        // value = item.unit?.name || '-';
        value = getItemUnitName(item) || '-';
        break;
      case 'mrp':
        value = this.formatCurrency(item.mrp, currencySymbol);
        break;
      case 'price':
      case 'rate':
        value = this.formatCurrency(item.price, currencySymbol);
        break;
      case 'amount':
        const amountVal = item.amount || item.price * item.quantity;
        value = Number(item.is_free) === 1
          ? this.formatCurrency(0, currencySymbol)
          : amountVal
          ? this.formatCurrency(amountVal, currencySymbol)
          : '-';
        break;
      case 'discount':
      case 'discountAmount':
        let dAmt = item.discountamt;
        // console.log(dAmt, item.price, item.quantity, 'discount calculation');
        if ((dAmt !== null || dAmt !== undefined || dAmt !== 0) && item.price && item.quantity) {
          const dPct = typeof item.discountPercent === 'string'
            ? parseFloat(item.discountPercent)
            : item.discountPercent || 0;
          // console.log((item.price * item.quantity) * (dPct / 100), 'discount calculation from percent')
          if (dPct > 0) dAmt = item.discountamt * item.quantity
          // (item.price * item.quantity) * (dPct / 100);
        }

        // value = dAmt && dAmt > 0 ? this.formatCurrency(dAmt, currencySymbol) : '-';
        // Free item → no discount (same as A5 landscape)
        value = Number(item.is_free) === 1 ? '-' : (dAmt && dAmt > 0 ? this.formatCurrency(dAmt, currencySymbol) : '-');
        break;
      case 'discountPercent':
      case 'discount%':
        let dPct = typeof item.discountPercent === 'string'
          ? parseFloat(item.discountPercent)
          : item.discountPercent || 0;
        if ((dPct === null || dPct === undefined || dPct === 0) && item.price && item.quantity) {
          const dAmt = item.discountamt;
          if (dAmt > 0) dPct = (dAmt / (item.price * item.quantity)) * 100;
        }
        // value = dPct && dPct > 0 ? `${dPct.toFixed(dPct % 1 === 0 ? 0 : 2)}%` : '-';
        value = Number(item.is_free) === 1 ? '-' : (dPct && dPct > 0 ? `${dPct.toFixed(dPct % 1 === 0 ? 0 : 2)}%` : '-');
        break;
      case 'taxableAmount':
      case 'taxable':
        // const taxableAmt =  (item.price * item.quantity) - (item.discountamt * item.quantity);
        // value = taxableAmt
        //   ? this.formatCurrency(taxableAmt, currencySymbol)
        //   : '-';
        const taxableAmt = item.discaftertax
          ? (item.price * item.quantity)
          : (item.price * item.quantity) - ((item.discountamt || 0) * item.quantity);
        // Free item → taxable amount is always 0 (same as A5 landscape)
        value = Number(item.is_free) === 1
          ? this.formatCurrency(0, currencySymbol)
          : taxableAmt
          ? this.formatCurrency(taxableAmt, currencySymbol)
          : '-';
        break;
      case 'tax':
      case 'taxRate':
      case 'taxPercent':
      case 'tax%':
        const taxPct = item.taxRate || item.taxPercent || item.tax || 0;
        // value = taxPct ? `${taxPct}%` : '-';
        // Free item → no tax (same as A5 landscape)
        value = Number(item.is_free) === 1 ? '0%' : (taxPct ? `${taxPct}%` : '-');
        break;
      case 'taxAmount':
      case 'taxAmt':
        const taxAmt = item.taxAmount || item.taxamount || 0;
        // value = taxAmt ? this.formatCurrency(taxAmt, currencySymbol) : '-';
        value = Number(item.is_free) === 1
          ? this.formatCurrency(0, currencySymbol)
          : (taxAmt ? this.formatCurrency(taxAmt, currencySymbol) : '-');
        break;
      case 'total':
      case 'totalAmount':
        // const totalValue = item.netValue || item.total || 0;
        // value = this.formatCurrency(totalValue, currencySymbol);
        const taxableBase = item.discaftertax
          ? (item.price * item.quantity)
          : (item.total || 0);
        const taxAmt2 = item.taxamount || item.taxAmount || 0;
        const totalValue = item.netValue || (taxableBase + taxAmt2) || 0;
        value = this.formatCurrency(Number(item.is_free) === 1 ? 0 : totalValue, currencySymbol);
        break;
      // case 'mrp':
      //   value = item.mrp
      //     ? this.formatCurrency(item.mrp, currencySymbol)
      //     : '-';
      //   break;
      default:
        value = '';
    }
    return value;
  }

  buildItemsTable(itemsOverride?: any[], showSummary: boolean = true, isExpanded: boolean = false, hideBottomBorder: boolean = false, fillerHeight: number = 0): Content {
    const tableConfig = this.config.itemTable;
    const theme = this.config.themeSettings;
    const currencySymbol = this.config.additionalSettings.currencySymbol;

    // THERMAL layout: fixed columns — S.No, Description, Qty, MRP, Rate, Amount
    if (this.config.pageSettings.pageSize === 'THERMAL') {
      return this.buildThermalItemsTable(itemsOverride, showSummary);
    }

    // A5 PORTRAIT: custom items table layout
    if (this.config.pageSettings.pageSize === 'A5' &&
      this.config.pageSettings.orientation !== 'landscape') {
      return this.buildA5PortraitItemsTable(itemsOverride, showSummary, fillerHeight, isExpanded);
    }

    // console.log(tableConfig, 'tableConfig')
    const borderColor = tableConfig.bodyStyle.borderColor || theme.borderColor || '#000000';
    const visibleColumns = this.getVisibleColumnsBasedOnData(tableConfig);
    const columnCount = visibleColumns.length;
    let responsiveFontSize = this.getResponsiveFontSize(
      columnCount,
      tableConfig.bodyStyle.fontSize,
    );
    let responsiveHeaderFontSize = this.getResponsiveFontSize(
      columnCount,
      tableConfig.headerStyle.fontSize,
    );
    if (isExpanded) {
      // Intentionally omitting horizontal expansion (font size) to prevent table width overflow.
      // responsiveFontSize += 2;
    }

    // console.log('Header Font Size (th):', Math.min(8, responsiveHeaderFontSize));
    // console.log('Body Font Size (td):', responsiveFontSize);

    const headerRow = visibleColumns.map((col) => ({
      text: col.label,
      style: 'tableHeader',
      alignment: col.align,
      fillColor: tableConfig.headerStyle.backgroundColor,
      color: tableConfig.headerStyle.textColor,
      fontSize: 11,
      bold: true, // Always bold — config value may be false/undefined
      noWrap: false,
    }));
    // console.log(this.invoiceData.items, 'Table items')
    const itemsToRender = itemsOverride || this.invoiceData.items;
    // console.log(itemsToRender, 'itemsToRender')
    const bodyRows = itemsToRender.map((item, index) => {
      return visibleColumns.map((col) => {
        const value = this.getItemCellText(item, index, col, tableConfig, currencySymbol);

        const isDescriptionColumn = col.key === 'name' || col.key === 'description';
        const isTotalColumn = col.key === 'total' || col.key === 'totalAmount';
        const isA4 = this.config.pageSettings.pageSize === 'A4';

        let finalFontSize = isDescriptionColumn ? Math.max(responsiveFontSize - 1, 5) : responsiveFontSize;
        let isBold = false;

        if (isA4 && (isDescriptionColumn || isTotalColumn)) {
          finalFontSize = 10;
          isBold = false;
        }

        return {
          text: value,
          font: this.getFontForText(value),
          alignment: col.align,
          fontSize: 10,
          bold: isBold,
          color: theme.textColor,
          noWrap: false,
        };
      });
    });

    // A5 Landscape: hide summary rows below items table (footer section handles totals) - COMMENTED OUT
    // const isA5Landscape = this.config.pageSettings.pageSize === 'A5' && this.config.pageSettings.orientation === 'landscape';
    const summaryRows = showSummary ? this.buildSummaryRows(
      visibleColumns,
      tableConfig,
      isExpanded ? responsiveFontSize - 1 : responsiveFontSize,
      currencySymbol,
    ) : [];

    const columnWidths = this.getItemsTableColumnWidths(
      visibleColumns,
      columnCount,
    );

    let cellPadding = this.getResponsivePadding(columnCount);
    let verticalPadding = cellPadding;
    if (isExpanded) {
      verticalPadding += 4; // Only stretch vertically to avoid horizontal overflow limit
    }
    const discountIdx = visibleColumns.findIndex(c => c.key === 'discount' || c.key === 'discountAmount');
    const discountPctIdx = visibleColumns.findIndex(c => c.key === 'discountPercent' || c.key === 'discount%');
    const isDiscountSplit = discountIdx !== -1 && discountPctIdx !== -1 && Math.abs(discountIdx - discountPctIdx) === 1;

    const hasFiller = fillerHeight > 0;
    const headerRowCount = isDiscountSplit ? 2 : 1;
    const fillerRow: any[] | null = hasFiller
      ? visibleColumns.map(() => ({ text: '' })) // empty cells — borders handled by layout callbacks
      : null;
    const fillerStartRowIndex = headerRowCount + bodyRows.length;
    let allRows: any[] = [];
    if (isDiscountSplit) {
      const headerRow1: any[] = [];
      const headerRow2: any[] = [];
      const firstIdx = Math.min(discountIdx, discountPctIdx);
      const secondIdx = Math.max(discountIdx, discountPctIdx);

      visibleColumns.forEach((col, i) => {
        if (i === firstIdx) {
          headerRow1.push({
            text: 'Discount',
            style: 'tableHeader',
            alignment: 'center',
            fillColor: tableConfig.headerStyle.backgroundColor,
            color: tableConfig.headerStyle.textColor,
            fontSize: Math.min(8, responsiveHeaderFontSize),
            bold: true,
            colSpan: 2,
          });
          headerRow1.push({}); // Empty cell for colSpan

          const label1 = visibleColumns[firstIdx].key.includes('Percent') || visibleColumns[firstIdx].key.includes('%') ? '%' : 'Amt';
          const label2 = visibleColumns[secondIdx].key.includes('Percent') || visibleColumns[secondIdx].key.includes('%') ? '%' : 'Amt';

          headerRow2.push({
            text: label1,
            style: 'tableHeader',
            alignment: 'center',
            fillColor: tableConfig.headerStyle.backgroundColor,
            color: tableConfig.headerStyle.textColor,
            fontSize: Math.min(8, responsiveHeaderFontSize),
            bold: true,
          });
          headerRow2.push({
            text: label2,
            style: 'tableHeader',
            alignment: 'center',
            fillColor: tableConfig.headerStyle.backgroundColor,
            color: tableConfig.headerStyle.textColor,
            fontSize: Math.min(8, responsiveHeaderFontSize),
            bold: true,
          });
        } else if (i === secondIdx) {
        } else {
          headerRow1.push({
            text: col.label,
            style: 'tableHeader',
            alignment: col.align,
            fillColor: tableConfig.headerStyle.backgroundColor,
            color: tableConfig.headerStyle.textColor,
            fontSize: Math.min(8, responsiveHeaderFontSize),
            bold: true,
            rowSpan: 2,
          });
          headerRow2.push({}); // Empty cell for rowSpan
        }
      });
      allRows = [headerRow1, headerRow2, ...bodyRows, ...(fillerRow ? [fillerRow] : []), ...summaryRows];
    } else {
      allRows = [headerRow, ...bodyRows, ...(fillerRow ? [fillerRow] : []), ...summaryRows];
    }
    const itemsTableLayout = (this.config.layoutSettings.itemsTableLayout || 'standard').toLowerCase();
    // console.log('[buildItemsTable] pageSize:', this.config.pageSettings.pageSize, 'orientation:', this.config.pageSettings.orientation, 'itemsTableLayout:', itemsTableLayout);


    let layout: any;

    if (itemsTableLayout === 'bordered') {
      layout = {
        hLineWidth: (i: number, node: any) => {
          // if (isA5Landscape && i === 0) return 0; // Remove top border for A5 landscape
          if (hasFiller && (i === fillerStartRowIndex || i === fillerStartRowIndex + 1)) return 0;
          if (hideBottomBorder && i === node.table.body.length) return 0;
          return 0.5;
        },
        vLineWidth: () => 0.5,
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        fillColor: (rowIndex: number) => {
          if (rowIndex === 0) return tableConfig.headerStyle.backgroundColor;
          if (hasFiller && rowIndex === fillerStartRowIndex) return null; // filler: no fill
          return rowIndex % 2 === 0
            ? tableConfig.bodyStyle.alternateRowColor
            : null;
        },
        paddingLeft: (i: number) => i >= bodyRows.length ? Math.max(cellPadding - 3, 2) : cellPadding,
        paddingRight: (i: number) => i >= bodyRows.length ? Math.max(cellPadding - 3, 2) : cellPadding,
        paddingTop: (i: number) => {
          if (hasFiller && i === fillerStartRowIndex) return 0;
          return i > bodyRows.length ? Math.max(verticalPadding - 2, 2) : verticalPadding;
        },
        paddingBottom: (i: number) => {
          if (hasFiller && i === fillerStartRowIndex) return 0;
          return i > bodyRows.length ? Math.max(verticalPadding - 2, 2) : verticalPadding;
        },
      };
    } else if (itemsTableLayout === 'compact') {
      layout = {
        hLineWidth: (i: number) => {
          // if (isA5Landscape && i === 0) return 0; // Remove top border for A5 landscape
          return (i === 0 || i === 1) ? 0.5 : 0; // Only header borders
        },
        vLineWidth: () => 0,
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        fillColor: (rowIndex: number) => {
          if (rowIndex === 0) return tableConfig.headerStyle.backgroundColor;
          return null;
        },
        paddingLeft: () => cellPadding - 1,
        paddingRight: () => cellPadding - 1,
        paddingTop: () => verticalPadding - 1,
        paddingBottom: () => verticalPadding - 1,
      };
    } else {
      layout = {
        hLineWidth: (i: number, node: any) => {
          // if (isA5Landscape && i === 0) return 0; // Remove top border for A5 landscape
          if (hasFiller && (i === fillerStartRowIndex || i === fillerStartRowIndex + 1)) return 0;
          if (hideBottomBorder && i === node.table.body.length) return 0;
          return 0.5;
        },
        vLineWidth: () => 0,
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        fillColor: (rowIndex: number) => {
          if (rowIndex === 0) return tableConfig.headerStyle.backgroundColor;
          if (hasFiller && rowIndex === fillerStartRowIndex) return null; // filler: no fill
          return rowIndex % 2 === 0
            ? tableConfig.bodyStyle.alternateRowColor
            : null;
        },
        paddingLeft: (i: number) => i >= bodyRows.length ? Math.max(cellPadding - 3, 2) : cellPadding,
        paddingRight: (i: number) => i >= bodyRows.length ? Math.max(cellPadding - 3, 2) : cellPadding,
        paddingTop: (i: number) => {
          if (hasFiller && i === fillerStartRowIndex) return 0;
          return i > bodyRows.length ? Math.max(verticalPadding - 2, 2) : verticalPadding;
        },
        paddingBottom: (i: number) => {
          if (hasFiller && i === fillerStartRowIndex) return 0;
          return i > bodyRows.length ? Math.max(verticalPadding - 2, 2) : verticalPadding;
        },
      };
    }

    const tableRowHeight = isExpanded ? (tableConfig.bodyStyle.rowHeight || 16) + 10 : tableConfig.bodyStyle.rowHeight;

    return {
      table: {
        headerRows: isDiscountSplit ? 2 : 1,
        widths: columnWidths,
        body: allRows,
        heights: (rowIndex: number) => {
          if (rowIndex < headerRowCount) return 'auto';
          if (hasFiller && rowIndex === fillerStartRowIndex) return fillerHeight;
          return tableRowHeight;
        }
      },
      layout: layout,
      margin: [0, 0, 0, 0],
    };
  }

  buildItemsSummaryRow(): Content {
    const tableConfig = this.config.itemTable;
    const theme = this.config.themeSettings;
    const currencySymbol = this.config.additionalSettings.currencySymbol;
    const totalsConfig = this.config.totalsSection;
    const borderColor = tableConfig.bodyStyle.borderColor || theme.borderColor || '#000000';

    const visibleColumns = this.getVisibleColumnsBasedOnData(tableConfig);
    const columnCount = visibleColumns.length;
    const fontSize = this.getResponsiveFontSize(columnCount, tableConfig.bodyStyle.fontSize);
    // Same widths as buildItemsTable so the summary row stays column-aligned with the items
    const columnWidths = this.getItemsTableColumnWidths(visibleColumns, columnCount);

    const totalQuantity = this.invoiceData.items.reduce(
      (sum, item) => sum + (item.quantity || 0), 0
    );
    const totalDiscount =
      (this.invoiceData.itemDiscount || 0) + (this.invoiceData.overallDiscount || 0) || this.invoiceData.discount || 0;
    const grandTotal = this.invoiceData.totalAmount || this.invoiceData.grandTotal || 0;
    const loyaltyAmount = Number(this.invoiceData.loyaltyAmount) || 0;
    const finalTotal = grandTotal - loyaltyAmount;
    const discountIdx = visibleColumns.findIndex(c => c.key === 'discount' || c.key === 'discountAmount');
    const discountPctIdx = visibleColumns.findIndex(c => c.key === 'discountPercent' || c.key === 'discount%');
    const isDiscountSplit = discountIdx !== -1 && discountPctIdx !== -1 && Math.abs(discountIdx - discountPctIdx) === 1;

    const summaryRow = visibleColumns.map((col, index) => {
      let value = '';

      if (col.key === 'qty' || col.key === 'quantity') {
        value = `T.Qty: ${totalQuantity}`;
      } else if (index === visibleColumns.length - 2) {
        value = 'Total:';
      } else if (col.key === 'total' || col.key === 'totalAmount') {
        value = this.formatCurrency(finalTotal, currencySymbol);
      } else if (
        (col.key === 'discount' || col.key === 'discountAmount' || col.key === 'discountPercent' || col.key === 'discount%') &&
        (tableConfig.showDiscountAmount || tableConfig.showDiscountPercent)
      ) {
        if (isDiscountSplit) {
          if (col.key.includes('Percent') || col.key.includes('%')) {
            value = 'Disc:';
          } else {
            value = totalDiscount > 0 ? this.formatCurrency(totalDiscount, currencySymbol) : '0.00';
          }
        } else {
          value = totalDiscount > 0
            ? `Disc: ${this.formatCurrency(totalDiscount, currencySymbol)}`
            : "Disc: 0.00";
        }
      }

      return {
        text: value,
        alignment: col.align,
        fontSize: fontSize,
        bold: true,
        fillColor: totalsConfig.grandTotalBackgroundColor || '#000000',
        color: totalsConfig.grandTotalTextColor || '#FFFFFF',
        noWrap: true
      };
    });

    const itemsTableLayout = this.config.layoutSettings.itemsTableLayout || 'standard';
    const layout: any = {
      hLineWidth: () => 0.5,
      vLineWidth: () => itemsTableLayout === 'bordered' ? 0.5 : 0,
      hLineColor: () => borderColor,
      vLineColor: () => borderColor,
    };

    return this.applyFooterScaling({
      table: {
        widths: columnWidths,
        body: [summaryRow]
      },
      layout: layout,
      margin: [0, 0, 0, 0]
    });
  }

  /**
   * A5 Landscape footer section: summary row + right-aligned totals below items table.
   * Summary row: "Total" in name col, T.Qty in qty col, discount in discount col, subtotal in total col.
   * Below: Round Off, Grand Total (right-aligned, one by one) — condition-based like buildTotalsSection.
   */
  buildA5LandscapeFooterSection(): Content | null {
    const tableConfig = this.config.itemTable;
    const totalsConfig = this.config.totalsSection;
    const theme = this.config.themeSettings;
    const currencySymbol = this.config.additionalSettings.currencySymbol;
    const borderColor = tableConfig.bodyStyle.borderColor || theme.borderColor || '#000000';
    // console.log(this.invoiceData)
    const visibleColumns = this.getVisibleColumnsBasedOnData(tableConfig);
    const columnCount = visibleColumns.length;
    const fontSize = this.getResponsiveFontSize(columnCount, tableConfig.bodyStyle.fontSize);
    const columnWidths = this.calculateResponsiveWidths(visibleColumns, columnCount);
    const itemsTableLayout = (this.config.layoutSettings.itemsTableLayout || 'standard').toLowerCase();

    const totalQuantity = this.invoiceData.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalDiscount = (this.invoiceData.itemDiscount || 0) + (this.invoiceData.overallDiscount || 0) || this.invoiceData.discount || 0;
    const subtotalValue = this.invoiceData.subtotal || 0;
    const grandTotal = this.invoiceData.totalAmount || this.invoiceData.grandTotal || 0;
    const loyaltyAmount = Number(this.invoiceData.loyaltyAmount) || 0;
    const finalTotal = grandTotal - loyaltyAmount;
    const roundOffValue = this.invoiceData.roundOffAmount || 0;

    const content: any[] = [];

    // ═══ SUMMARY ROW (spans full items table width) ═══
    const summaryRow = visibleColumns.map((col) => {
      let value = '';
      if (col.key === 'name' || col.key === 'description') {
        value = 'Total';
      } else if (col.key === 'qty' || col.key === 'quantity') {
        value = totalQuantity.toString();
      } else if (col.key === 'discount' || col.key === 'discountAmount') {
        if (totalsConfig.showDiscount && totalDiscount > 0) {
          value = this.formatCurrency(totalDiscount, currencySymbol);
        }
      }
      // else if (col.key === 'total' || col.key === 'totalAmount') {
      //   if (totalsConfig.showSubtotal) {
      //     value = this.formatCurrency(subtotalValue, currencySymbol);
      //   }
      // }
      else if (col.key === 'total' || col.key === 'totalAmount') {
        if (totalsConfig.showSubtotal) {
          // subtotalValue from invoiceData is already correct (backend-calculated)
          // but taxable display in summary respects discaftertax per item
          value = this.formatCurrency(subtotalValue, currencySymbol);
        }
      }

      return {
        text: value,
        alignment: col.align,
        fontSize: fontSize,
        bold: true,
        color: theme.textColor,
      };
    });

    content.push({
      table: {
        widths: columnWidths,
        body: [summaryRow],
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => itemsTableLayout === 'bordered' ? 0.5 : 0,
        hLineColor: () => borderColor,
        vLineColor: () => borderColor,
        paddingLeft: () => 2,
        paddingRight: () => 2,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
      margin: [0, 0, 0, 0],
    });

    // ═══ BELOW: Right-aligned totals (Round Off, Grand Total) ═══
    const totalRows: any[][] = [];

    if (totalsConfig.showRoundOff && roundOffValue) {
      totalRows.push([
        { text: 'Round Off:', alignment: 'right', fontSize: fontSize, bold: true, color: theme.textColor },
        { text: this.formatCurrency(roundOffValue, currencySymbol), alignment: 'right', fontSize: fontSize, color: theme.textColor },
      ]);
    }

    if (totalsConfig.showGrandTotal) {
      totalRows.push([
        { text: 'Grand Total:', alignment: 'right', fontSize: fontSize + 1, bold: true, color: theme.primaryColor },
        { text: this.formatCurrency(finalTotal, currencySymbol), alignment: 'right', fontSize: fontSize + 1, bold: true, color: theme.primaryColor, margin: [0, 0, 0, 0] },
      ]);
    }

    if (totalRows.length > 0) {
      content.push({
        columns: [
          { width: '*', text: '' },
          {
            width: 'auto',
            table: {
              widths: ['auto', 80],
              body: totalRows,
            },
            layout: 'noBorders',
          },
        ],
        margin: [0, 2, 2, 0],
      });
    }

    // ═══ RECEIVED AMOUNT, PREVIOUS BALANCE, CURRENT BALANCE (side by side, bordered) ═══
    const receivedAmount = this.invoiceData.paidamount || 0;
    const previousBalance = this.invoiceData.previousBalance || 0;
    const currentBalance = this.invoiceData.currentBalance || 0;
    // console.log('Balances:', { receivedAmount, previousBalance, currentBalance }, 'Totals Config:', totalsConfig);
    const paymentCells: any[] = [];
    if (totalsConfig.showReceivedAmount && receivedAmount !== undefined && receivedAmount !== null) {
      paymentCells.push({
        columns: [
          { text: 'Received Amount:', fontSize: fontSize - 1, bold: true, alignment: 'left', color: theme.textColor },
          { text: this.formatCurrency(receivedAmount, currencySymbol), fontSize: fontSize, alignment: 'right', color: theme.textColor, margin: [0, 0, 0, 0] },
        ],
        margin: [3, 3, 2, 2],
      });
    }
    if (totalsConfig.showPreviousBalance && previousBalance !== undefined && previousBalance !== null) {
      paymentCells.push({
        columns: [
          { text: 'Previous Balance:', fontSize: fontSize - 1, bold: true, alignment: 'left', color: theme.textColor },
          { text: this.formatCurrency(previousBalance, currencySymbol), fontSize: fontSize, alignment: 'right', color: theme.textColor, margin: [0, 0, 0, 0] },
        ],
        margin: [3, 3, 2, 2],
      });
    }
    if (totalsConfig.showCurrentBalance && currentBalance !== undefined && currentBalance !== null) {
      paymentCells.push({
        columns: [
          { text: 'Current Balance:', fontSize: fontSize - 1, bold: true, alignment: 'left', color: theme.textColor },
          { text: this.formatCurrency(currentBalance, currencySymbol), fontSize: fontSize, alignment: 'right', color: theme.textColor, margin: [0, 0, 0, 0] },
        ],
        margin: [3, 3, 2, 2],
      });
    }

    if (paymentCells.length > 0) {
      const paymentWidths = paymentCells.map(() => '*');
      const showTopBorder = !!(totalsConfig.showGrandTotal || (totalsConfig.showRoundOff && roundOffValue));
      content.push({
        table: {
          widths: paymentWidths,
          body: [paymentCells],
        },
        layout: {
          hLineWidth: (i: number) => {
            if (i === 0 && !showTopBorder) return 0; // Hide top border when grandTotal/roundOff not shown
            return 0.5;
          },
          vLineWidth: (i: number, node: any) => {
            if (i === 0) return 0; // Remove left border of first cell (Received Amount)
            if (i === node.table.widths.length) return 0; // Remove right border of last cell (Current Balance)
            return 0.5;
          },
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0],
      });
    }

    // ═══ AMOUNT IN WORDS (condition-based) ═══
    if (totalsConfig.showAmountInWords) {
      const amountInWords = this.numberToWords(finalTotal);
      const prefix = totalsConfig.amountInWordsPrefix || '';
      const suffix = totalsConfig.amountInWordsSuffix || '';
      const amountInWordsFullText = `${prefix}${amountInWords}${suffix}`;

      content.push({
        text: amountInWordsFullText,
        font: this.getFontForText(amountInWordsFullText),
        fontSize: theme.baseFontSize - 2,
        italics: totalsConfig.amountInWordsItalic !== undefined ? totalsConfig.amountInWordsItalic : true,
        bold: totalsConfig.amountInWordsBold || false,
        color: theme.textColor,
        margin: [3, 2, 0, 0],
      });
    }

    // ═══ NOTES (with top and bottom border) ═══
    const footerConfig = this.config.footer;
    if ((footerConfig as any).showNotes && (footerConfig as any).notesText) {
      content.push({
        table: {
          widths: ['*'],
          body: [[{
            text: `Notes: ${(footerConfig as any).notesText}`,
            fontSize: fontSize - 1,
            color: theme.textColor,
            margin: [-1, 0, 0, 0],
          }]],
        },
        layout: {
          hLineWidth: (i: number, node: any) => i === node.table.body.length ? 0 : 0.5,
          vLineWidth: () => 0,
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
        },
        margin: [0, 0, 0, 0],
      });
    }

    // ═══ 3-SECTION FOOTER: Bank+QR | Terms & Conditions | Authorized Signatory ═══
    const businessName = this.invoiceData.business?.name || this.invoiceData.businessName || '';
    const footerSections: any[] = [];

    // --- 1st Section: Bank Details + QR Code ---
    if (footerConfig.showBankDetails) {
      const bankDetails = this.invoiceData.bankDetails;
      const bankStack: any[] = [];
      // console.log('Bank Details:', bankDetails);
      bankStack.push({
        text: 'BANK DETAILS',
        fontSize: fontSize - 1,
        bold: true,
        color: theme.primaryColor,
        margin: [0, 0, 0, 2],
      });
      if (bankDetails) {
        if (bankDetails.bankName) bankStack.push({ text: `Name: ${bankDetails.bankName}`, fontSize: fontSize - 1, color: theme.textColor });
        if (bankDetails.ifscCode) bankStack.push({ text: `IFSC: ${bankDetails.ifscCode}`, fontSize: fontSize - 1, color: theme.textColor });
        if (bankDetails.accountNumber) bankStack.push({ text: `Acc No: ${bankDetails.accountNumber}`, fontSize: fontSize - 1, color: theme.textColor });
      }

      let qrElement: any = { text: '' };
      const qrCodeEnabled = (footerConfig as any).showQrCode ?? footerConfig.showQRCode ?? false;
      if (qrCodeEnabled && this.invoiceData.qrcode) {
        const qrContent: any[] = [
          {
            image: this.invoiceData.qrcode,
            fit: [35, 35],
            alignment: 'right',
            margin: [0, 0, 0, 2],
          },
        ];
        if ((footerConfig as any).showQRCodeBorder) {
          qrContent.push({
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 35, y2: 0, lineWidth: 0.5, lineColor: '#000000' }],
            alignment: 'right',
            margin: [0, 0, 0, 0],
          });
        }
        qrElement = { stack: qrContent, alignment: 'right' };
      }

      footerSections.push({
        columns: [
          { width: '*', stack: bankStack },
          { width: 'auto', ...qrElement },
        ],
        margin: [3, 3, 3, 3],
      });
    } else {
      footerSections.push({ text: '', margin: [3, 3, 3, 3] });
    }

    // --- 2nd Section: Terms & Conditions ---
    if (footerConfig.showTermsAndConditions && this.config.pageSettings.pageSize !== 'THERMAL') {
      const termsText = footerConfig.termsText || this.invoiceData.termsAndConditions || this.invoiceData.terms || '';
      const termsStack: any[] = [];
      termsStack.push({
        text: 'Terms & Conditions:',
        fontSize: fontSize - 1,
        bold: true,
        color: theme.primaryColor,
        margin: [0, 0, 0, 2],
      });
      if (termsText) {
        const points = termsText.split('\n').filter((p: string) => p.trim() !== '');
        points.forEach((p: string, i: number) => {
          const cleanText = p.trim().replace(/^\d+[\.\)]\s*/, '');
          termsStack.push({
            text: `${i + 1}. ${cleanText}`,
            fontSize: fontSize - 1,
            color: theme.textColor,
            margin: [2, 1, 0, 0],
          });
        });
      }
      footerSections.push({ stack: termsStack, margin: [3, 3, 3, 3] });
    } else {
      footerSections.push({ text: '', margin: [3, 3, 3, 3] });
    }

    // --- 3rd Section: Authorized Signatory ---
    if (footerConfig.showSignature) {
      const sigStack: any[] = [];
      sigStack.push({
        text: footerConfig.signatureLabel || 'Authorized Signatory',
        fontSize: fontSize - 1,
        bold: true,
        alignment: 'center',
        color: theme.primaryColor,
        margin: [0, 0, 0, 3],
      });
      const signatureData = this.invoiceData.signature || (this.invoiceData.business as any)?.signature || footerConfig.signature;
      if (signatureData) {
        sigStack.push({
          image: signatureData,
          fit: [40, 25],
          alignment: 'center',
          margin: [0, 0, 0, 2],
        });
        if ((footerConfig as any).showSignatureBorder) {
          sigStack.push({
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 60, y2: 0, lineWidth: 0.5, lineColor: '#000000' }],
            alignment: 'center',
            margin: [0, 0, 0, 3],
          });
        }
      } else {
        sigStack.push({ text: '', margin: [0, 15, 0, 0] });
      }
      if (footerConfig.showForBusinessName && businessName) {
        sigStack.push({
          text: businessName,
          fontSize: fontSize - 1,
          bold: true,
          alignment: 'center',
          color: theme.textColor,
        });
      }
      footerSections.push({ stack: sigStack, margin: [3, 3, 3, 3] });
    } else {
      footerSections.push({ text: '', margin: [3, 3, 3, 3] });
    }

    // Only push footer if at least one section has real content
    const hasFooterContent = footerConfig.showBankDetails || footerConfig.showTermsAndConditions || footerConfig.showSignature;
    if (hasFooterContent) {
      const footerMinHeight = 40;
      content.push({
        table: {
          widths: ['*', '*', '*'],
          body: [footerSections],
          heights: [footerMinHeight],
        },
        layout: {
          hLineWidth: (i: number, node: any) => {
            if (i === node.table.body.length) return 0; // Remove bottom border
            return 0.5;
          },
          vLineWidth: (i: number, node: any) => {
            if (i === 0) return 0; // Remove left border (Bank details)
            if (i === node.table.widths.length) return 0; // Remove right border (Authorized Signature)
            return 0.5;
          },
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 3, 0, 0],
      });
    }

    return content.length > 0 ? { stack: content, margin: [0, 0, 0, 0] } as any : null;
  }


  private getResponsivePadding(columnCount: number): number {
    if (columnCount >= 14) return 1;
    if (columnCount >= 12) return 2;
    if (columnCount >= 10) return 2;
    if (columnCount >= 8) return 3;
    return 4; // Default padding
  }

  private buildSummaryRows(
    visibleColumns: any[],
    tableConfig: any,
    fontSize: number,
    currencySymbol: string,
  ): any[][] {
    const summaryRows: any[][] = [];
    const theme = this.config.themeSettings;
    const totalsConfig = this.config.totalsSection;

    const totalQuantity = this.invoiceData.items.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0,
    );

    const totalDiscount =
      (this.invoiceData.itemDiscount || 0) + (this.invoiceData.overallDiscount || 0) || this.invoiceData.discount || 0;

    const grandTotal =
      this.invoiceData.totalAmount || this.invoiceData.grandTotal || 0;

    if (
      (tableConfig.showDiscountAmount || tableConfig.showDiscountPercent)
    ) {
      const hasItemDiscount =
        this.invoiceData.itemDiscount !== undefined &&
        this.invoiceData.itemDiscount > 0;
      const hasOverallDiscount =
        this.invoiceData.overallDiscount !== undefined &&
        this.invoiceData.overallDiscount > 0;

      if (hasItemDiscount && hasOverallDiscount) {
        const itemDiscountRow = visibleColumns.map((col, index) => {
          let value = '';

          if (col.key === 'discount' || col.key === 'discountAmount') {
            value = `Item Discount: ${this.formatCurrency(this.invoiceData.itemDiscount || 0, currencySymbol)}`;
          } else if (index === visibleColumns.length - 2) {
            value = 'Item Discount:';
          } else if (col.key === 'total' || col.key === 'totalAmount') {
            value = this.formatCurrency(
              this.invoiceData.itemDiscount || 0,
              currencySymbol,
            );
          }

          return {
            text: value,
            alignment: col.align,
            fontSize: fontSize,
            bold: true,
            fillColor: totalsConfig.backgroundColor || '#F8F9FA',
            color: totalsConfig.textColor || theme.textColor,
          };
        });

        summaryRows.push(itemDiscountRow);
        // console.log(this.invoiceData, 'discounts ')
        const overallDiscountRow = visibleColumns.map((col, index) => {
          let value = '';

          if (col.key === 'discount' || col.key === 'discountAmount') {
            value = `Overall Discount: ${this.formatCurrency(this.invoiceData.overallDiscount || 0, currencySymbol)}`;
          } else if (index === visibleColumns.length - 2) {
            value = 'Overall Discount:';
          } else if (col.key === 'total' || col.key === 'totalAmount') {
            value = this.formatCurrency(
              this.invoiceData.overallDiscount || 0,
              currencySymbol,
            );
          }

          return {
            text: value,
            alignment: col.align,
            fontSize: fontSize,
            bold: true,
            fillColor: totalsConfig.backgroundColor || '#F8F9FA',
            color: totalsConfig.textColor || theme.textColor,
          };
        });

        summaryRows.push(overallDiscountRow);
      } else {
      }
    }

    const loyaltyAmount = Number(this.invoiceData.loyaltyAmount) || 0;
    const loyaltyPoints = Number(this.invoiceData.loyaltyPoints) || 0;

    if (loyaltyAmount > 0) {
      const loyaltyRow = visibleColumns.map((col, index) => {
        let value = '';

        if (col.key === 'discount' || col.key === 'discountAmount') {
          value = `Loyalty Discount (${loyaltyPoints} pts): ${this.formatCurrency(loyaltyAmount, currencySymbol)}`;
        } else if (index === visibleColumns.length - 2) {
          value = `Loyalty Discount (${loyaltyPoints} pts):`;
        } else if (col.key === 'total' || col.key === 'totalAmount') {
          value = this.formatCurrency(loyaltyAmount, currencySymbol);
        }

        return {
          text: value,
          alignment: col.align,
          fontSize: fontSize,
          bold: true,
          fillColor: totalsConfig.backgroundColor || '#F8F9FA',
          color: totalsConfig.textColor || theme.textColor,
        };
      });

      summaryRows.push(loyaltyRow);
    }

    if (totalsConfig.showGrandTotal) {
      const finalTotal = grandTotal - loyaltyAmount;
      const discountIdx = visibleColumns.findIndex(c => c.key === 'discount' || c.key === 'discountAmount');
      const discountPctIdx = visibleColumns.findIndex(c => c.key === 'discountPercent' || c.key === 'discount%');
      const isDiscountSplit = discountIdx !== -1 && discountPctIdx !== -1 && Math.abs(discountIdx - discountPctIdx) === 1;

      const totalRow = visibleColumns.map((col, index) => {
        let value = '';

        if (index === visibleColumns.length - 2) {
          value = 'Total:';
        } else if (col.key === 'total' || col.key === 'totalAmount') {
          value = this.formatCurrency(finalTotal, currencySymbol);
        } else if (col.key === 'qty' || col.key === 'quantity') {
          value = `T.Qty: ${totalQuantity}`;
        } else if (
          (col.key === 'discount' || col.key === 'discountAmount' || col.key === 'discountPercent' || col.key === 'discount%') &&
          (tableConfig.showDiscountAmount || tableConfig.showDiscountPercent)
        ) {
          if (isDiscountSplit) {
            if (col.key.includes('Percent') || col.key.includes('%')) {
              value = 'Disc:';
            } else {
              value = totalDiscount > 0 ? this.formatCurrency(totalDiscount, currencySymbol) : '0.00';
            }
          } else {
            value = totalDiscount > 0
              ? `Disc: ${this.formatCurrency(totalDiscount, currencySymbol)}`
              : "Disc: 0.00";
          }
        }

        return {
          text: value,
          alignment: col.align,
          fontSize: fontSize,
          bold: true,
          fillColor: totalsConfig.grandTotalBackgroundColor || '#000000',
          color: totalsConfig.grandTotalTextColor || '#FFFFFF',
          noWrap: true
        };
      });

      summaryRows.push(totalRow);
    }

    return summaryRows;
  }

  private getResponsiveFontSize(
    columnCount: number,
    baseFontSize: number,
  ): number {
    if (columnCount <= 6) return baseFontSize;
    if (columnCount <= 8) return Math.max(baseFontSize - 1, 7);
    if (columnCount <= 10) return Math.max(baseFontSize - 2, 6);
    if (columnCount <= 12) return Math.max(baseFontSize - 3, 5);
    return Math.max(baseFontSize - 4, 5);
  }

  private calculateResponsiveWidths(
    columns: any[],
    columnCount: number,
  ): any[] {
    const getFixedWidth = (key: string, count: number): number | null => {
      const widthMap: { [key: string]: { [range: string]: number } } = {
        sno: { '14+': 18, '12-13': 20, '10-11': 22, default: 25 },
        quantity: { '14+': 22, '12-13': 25, '10-11': 28, default: 30 },
        qty: { '14+': 22, '12-13': 25, '10-11': 28, default: 30 },
        unit: { '14+': 22, '12-13': 25, '10-11': 28, default: 30 },
        discountPercent: { '14+': 30, '12-13': 32, '10-11': 35, default: 40 },
        'discount%': { '14+': 30, '12-13': 32, '10-11': 35, default: 40 },
        taxPercent: { '14+': 25, '12-13': 28, '10-11': 30, default: 35 },
        'tax%': { '14+': 25, '12-13': 28, '10-11': 30, default: 35 },
        hsn: { '14+': 38, '12-13': 42, '10-11': 48, default: 55 },
        hsncode: { '14+': 38, '12-13': 42, '10-11': 48, default: 55 },
        batch: { '14+': 30, '12-13': 35, '10-11': 40, default: 45 },
      };

      if (!widthMap[key]) return null;

      const range =
        count >= 14
          ? '14+'
          : count >= 12
            ? '12-13'
            : count >= 10
              ? '10-11'
              : 'default';
      return widthMap[key][range];
    };

    if (columnCount >= 14) {
      return columns.map((col) => {
        if (col.key === 'name') return '*'; // Description takes remaining space
        if (typeof col.width === 'number') return col.width; // Respect explicit numeric width from config
        const fixedWidth = getFixedWidth(col.key, columnCount);
        if (fixedWidth) return fixedWidth;
        if (col.align === 'right') return 38; // Monetary columns
        if (col.align === 'center') return 25; // Center-aligned columns
        return 'auto';
      });
    } else if (columnCount >= 12) {
      return columns.map((col) => {
        if (col.key === 'name') return '*';
        if (typeof col.width === 'number') return col.width;
        const fixedWidth = getFixedWidth(col.key, columnCount);
        if (fixedWidth) return fixedWidth;
        if (col.align === 'right') return 42;
        if (col.align === 'center') return 28;
        return 'auto';
      });
    } else if (columnCount >= 10) {
      return columns.map((col) => {
        if (col.key === 'name') return '*';
        if (typeof col.width === 'number') return col.width;
        const fixedWidth = getFixedWidth(col.key, columnCount);
        if (fixedWidth) return fixedWidth;
        if (col.align === 'right') return 48;
        if (col.align === 'center') return 32;
        return 'auto';
      });
    } else if (columnCount >= 8) {
      return columns.map((col) => {
        if (col.key === 'name') return '*';
        if (typeof col.width === 'number') return col.width;
        const fixedWidth = getFixedWidth(col.key, columnCount);
        if (fixedWidth) return fixedWidth;
        if (col.align === 'right') return 55;
        if (col.align === 'center') return 35;
        return 'auto';
      });
    } else {
      return columns.map((col) => {
        if (col.key === 'name') return '*';
        if (!col.width || col.width === 'auto') return 'auto';
        return col.width;
      });
    }
  }

  /**
   * A4 items-table widths. The fixed responsive widths shrink as more columns are
   * enabled, but A4 headers are 11pt bold and cells 10pt, so labels/values such as
   * "S.No", "Discount %", "Numbers" or "₹11700.00" were broken mid-word. A column is
   * widened only when its longest header word or cell value does not fit; Description
   * ('*') gives up the space. Measured from ALL invoice items (not the current page's
   * chunk) so every page and the separate summary row get identical widths.
   */
  private getItemsTableColumnWidths(visibleColumns: any[], columnCount: number): any[] {
    const baseWidths = this.calculateResponsiveWidths(visibleColumns, columnCount);
    if (this.config.pageSettings.pageSize !== 'A4') return baseWidths;

    const tableConfig = this.config.itemTable;
    const currencySymbol = this.config.additionalSettings.currencySymbol;
    const items = this.invoiceData.items || [];
    const cellPadding = this.getResponsivePadding(columnCount);
    const HEADER_FONT_SIZE = 11; // bold, see buildItemsTable headerRow
    const BODY_FONT_SIZE = 10;

    const longestWordWidth = (text: string, fontSize: number, bold: boolean): number =>
      String(text || '')
        .split(/\s+/)
        .reduce((max, word) => Math.max(max, this.estimateTextWidth(word, fontSize, bold)), 0);

    const neededWidths = visibleColumns.map((col, colIndex) => {
      if (typeof baseWidths[colIndex] !== 'number') return baseWidths[colIndex]; // '*' / 'auto' already fit content
      let contentWidth = longestWordWidth(col.label, HEADER_FONT_SIZE, true);
      items.forEach((item, index) => {
        const text = this.getItemCellText(item, index, col, tableConfig, currencySymbol);
        contentWidth = Math.max(contentWidth, longestWordWidth(text, BODY_FONT_SIZE, false));
      });
      // pdfmake widths are content-only (cell padding is added outside), so no padding here
      return Math.max(baseWidths[colIndex], Math.ceil(contentWidth + 1));
    });

    // The table must stay inside the page border, which is drawn on the page margins
    // (see dynamic-pdf-template background). Real table width = content widths + each
    // column's left/right padding + vertical lines, so reserve that overhead too, and
    // never squeeze Description below a readable width: scale the extra space down if needed.
    const margins = this.config.pageSettings.margins;
    const pageContentWidth = 595.28 - (margins?.left || 0) - (margins?.right || 0);
    // Layout padding is cellPadding or max(cellPadding - 3, 2) per side; vertical lines are at most 0.5pt
    const tableOverhead = columnCount * Math.max(cellPadding, 2) * 2 + (columnCount + 1) * 0.5;
    const MIN_DESCRIPTION_WIDTH = 70; // still fits a full product word at 10pt; names wrap at spaces
    const fixedTotal = (widths: any[]) =>
      widths.reduce((sum: number, w: any) => sum + (typeof w === 'number' ? w : 0), 0);
    const available = pageContentWidth - tableOverhead - MIN_DESCRIPTION_WIDTH;
    const baseTotal = fixedTotal(baseWidths);
    const neededTotal = fixedTotal(neededWidths);
    if (neededTotal <= available || neededTotal <= baseTotal) return neededWidths;

    const ratio = Math.max(0, (available - baseTotal) / (neededTotal - baseTotal));
    return neededWidths.map((w, i) =>
      typeof w === 'number' ? Math.floor(baseWidths[i] + (w - baseWidths[i]) * ratio) : w,
    );
  }

  /** Approximate Roboto text width in points (slightly generous so text never breaks mid-word) */
  private estimateTextWidth(text: string, fontSize: number, bold: boolean): number {
    let em = 0;
    for (const ch of text) {
      if (/[0-9₹$€£]/.test(ch)) em += 0.57;
      else if (/[mwMW%@]/.test(ch)) em += 0.86;
      else if (/[A-Z]/.test(ch)) em += 0.66;
      else if (/[a-z]/.test(ch)) em += /[ijlft]/.test(ch) ? 0.3 : 0.55;
      else if (/[.,:;'|!]/.test(ch)) em += 0.27;
      else if (ch === ' ') em += 0.25;
      else em += 0.6; // other symbols / non-Latin scripts
    }
    return em * fontSize * (bold ? 1.08 : 1) * 1.04;
  }

  buildTotalsSection(): Content {
    const totalsConfig = this.config.totalsSection;
    // console.log(totalsConfig, 'totals config')
    const theme = this.config.themeSettings;
    const currencySymbol = this.config.additionalSettings.currencySymbol;
    const isThermalTem = this.config.pageSettings.pageSize === 'THERMAL';
    const rows: any[] = [];

    if (totalsConfig.showSubtotal) {
      // let subtotalValue = this.invoiceData.subtotal;
      // Exclude free items from subtotal (free item amount shows 0 in the table row)
      const freeItemsAmount = this.invoiceData.items.reduce(
        (sum, item: any) => Number(item.is_free) === 1
          ? sum + Number(item.amount || (item.price || 0) * (item.quantity || 0))
          : sum, 0);
      let subtotalValue = Math.max(0, Number(this.invoiceData.subtotal || 0) - freeItemsAmount);
      const isThermalInclusive = this.config.pageSettings.pageSize === 'THERMAL' && totalsConfig.showInclusiveTax;
      if (isThermalInclusive) {
        subtotalValue = this.invoiceData.items.reduce((sum, item) => {
          if (Number(item.is_free) === 1) return sum; // skip free items
          const qty = item.quantity || 1;
          const taxPerUnit = (item.taxamount || item.taxAmount || 0) / qty;
          const inclusiveRate = Number(item.price || 0) + taxPerUnit;
          return sum + (inclusiveRate * qty);
        }, 0);
      }
      // exact subtotal (paise kept, like the item rows) for thermal and A4; round-off is its own row
      rows.push([
        {
          text: 'Subtotal:',
          alignment: 'right',
          fontSize: theme.baseFontSize,
          bold: true,
        },
        {
          text: this.formatCurrency(subtotalValue, ''),
          alignment: 'right',
          fontSize: theme.baseFontSize,
        },
      ]);
    } if (totalsConfig.showDiscount) {
      if (
        this.invoiceData.itemDiscount !== undefined &&
        this.invoiceData.overallDiscount !== undefined
      ) {
        if (this.invoiceData.itemDiscount > 0) {
          rows.push([
            {
              text: 'Item Discount:',
              alignment: 'right',
              fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
              bold: true,
            },
            {
              text: this.formatCurrency(
                this.invoiceData.itemDiscount,
                '',
              ),
              alignment: 'right',
              fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
            },
          ]);
        }

        if (this.invoiceData.overallDiscount > 0) {
          rows.push([
            {
              text: 'Overall Discount:',
              alignment: 'right',
              fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
              bold: true,
            },
            {
              text: this.formatCurrency(
                this.invoiceData.overallDiscount,
                '',
              ),
              alignment: 'right',
              fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
            },
          ]);
        }

        if (
          this.invoiceData.itemDiscount > 0 ||
          this.invoiceData.overallDiscount > 0
        ) {
          const totalDiscount =
            (this.invoiceData.itemDiscount || 0) +
            (this.invoiceData.overallDiscount || 0);
          rows.push([
            {
              text: 'Total Discount:',
              alignment: 'right',
              fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
              bold: true,
            },
            {
              text: this.formatCurrency(totalDiscount, ''),
              alignment: 'right',
              fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
            },
          ]);
        }
      } else {
        if (this.invoiceData.discount && this.invoiceData.discount > 0) {
          rows.push([
            {
              text: 'Discount:',
              alignment: 'right',
              fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
              bold: true,
            },
            {
              text: this.formatCurrency(
                this.invoiceData.discount,
                currencySymbol,
              ),
              alignment: 'right',
              fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
            },
          ]);
        }
      }
    }
    const loyaltyAmount = Number(this.invoiceData.loyaltyAmount) || 0;

    if (loyaltyAmount > 0) {
      rows.push([
        {
          text: 'Loyalty Discount:',
          alignment: 'right',
          fontSize: theme.baseFontSize,
          bold: true,
        },
        {
          text: `- ${this.formatCurrency(loyaltyAmount, currencySymbol)}`,
          alignment: 'right',
          fontSize: theme.baseFontSize,
        },
      ]);
    }
    // console.log(this.invoiceData);

    const loyaltyPoints = Number(this.invoiceData.loyaltyPoints) || 0;

    if (totalsConfig.showLoyaltyPoints && loyaltyPoints > 0) {
      rows.push([
        {
          text: 'Loyalty Points Redeemed:',
          alignment: 'right',
          fontSize: theme.baseFontSize,
          bold: true,
        },
        {
          text: `${loyaltyPoints} pts`,
          alignment: 'right',
          fontSize: theme.baseFontSize,
        },
      ]);
    }
    if (totalsConfig.showTaxBreakup) {
      if (totalsConfig.showCGST && this.invoiceData.cgst) {
        rows.push([
          {
            text: 'CGST:',
            alignment: 'right',
            fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
            bold: true,
          },
          {
            text: this.formatCurrency(this.invoiceData.cgst, currencySymbol),
            alignment: 'right',
            fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
          },
        ]);
      }

      if (totalsConfig.showSGST && this.invoiceData.sgst) {
        rows.push([
          {
            text: 'SGST:',
            alignment: 'right',
            fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
            bold: true,
          },
          {
            text: this.formatCurrency(this.invoiceData.sgst, currencySymbol),
            alignment: 'right',
            fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
          },
        ]);
      }

      if (totalsConfig.showIGST && this.invoiceData.igst) {
        rows.push([
          {
            text: 'IGST:',
            alignment: 'right',
            fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
            bold: true,
          },
          {
            text: this.formatCurrency(this.invoiceData.igst, currencySymbol),
            alignment: 'right',
            fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
          },
        ]);
      }
    }
    if (totalsConfig.showRoundOff) {
      const isThermalInclusiveRoundOff = this.config.pageSettings.pageSize === 'THERMAL' && totalsConfig.showInclusiveTax;
      const roundOffValue = this.invoiceData.roundOffAmount || 0;
      if (isThermalInclusiveRoundOff || roundOffValue) {
        rows.push([
          {
            text: 'Rounded Off:',
            alignment: 'right',
            fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
            bold: true,
          },
          {
            text: this.formatCurrency(roundOffValue, ''),
            alignment: 'right',
            fontSize: isThermalTem ? theme.baseFontSize + 1 : theme.baseFontSize,
            bold: true,
          },
        ]);
      }
    }
    if (totalsConfig.showGrandTotal) {
      const grandTotal =
        this.invoiceData.totalAmount || this.invoiceData.grandTotal || 0;
      const finalTotal =
        grandTotal - (Number(this.invoiceData.loyaltyAmount) || 0);
      rows.push([
        {
          text: 'TOTAL:',
          alignment: 'right',
          fontSize: isThermalTem ? theme.baseFontSize + 3 : theme.baseFontSize + 2,
          bold: true,
          color: theme.primaryColor,
        },
        {
          text: this.formatCurrency(finalTotal, ''),
          alignment: 'right',
          fontSize: isThermalTem ? theme.baseFontSize + 3 : theme.baseFontSize + 2,
          bold: true,
          color: theme.primaryColor,
        },
      ]);
    }

    const tableLayout = totalsConfig.showBorder ? {
      hLineWidth: () => totalsConfig.borderWidth || 0.5,
      vLineWidth: () => totalsConfig.borderWidth || 0.5,
      hLineColor: () => totalsConfig.borderColor || theme.borderColor || '#000000',
      vLineColor: () => totalsConfig.borderColor || theme.borderColor || '#000000',
    } : 'noBorders';

    const hsnConfig = this.config.hsnBreakup;
    const isThermal = this.config.pageSettings.pageSize === 'THERMAL';
    let hsnAndTotalsComponent: any = null;

    if (isThermal) {
      const fontSize = theme.baseFontSize - 1;
      const lineColor = theme.textColor || '#000000';
      const dashedLine = {
        table: {
          widths: ['*'],
          body: [[{ text: '', border: [false, true, false, false] }]],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0,
          hLineColor: () => lineColor,
          vLineColor: () => lineColor,
          hLineStyle: () => ({ dash: { length: 3, space: 2 } }),
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 1,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 1],
      };

      const breakdownRows = rows.filter(r => r[0].text !== 'TOTAL:');
      const totalRow = rows.find(r => r[0].text === 'TOTAL:');

      const stack: any[] = [];

      stack.push(...breakdownRows.map(row => {
        let label = row[0].text.replace(':', '').trim();
        if (label === 'Subtotal') label = 'Total';
        if (label === 'Rounded Off') label = 'Round Off';

        const isTotal = label === 'Total';
        const fieldsToBold = ['Total', 'Item Discount', 'Overall Discount', 'Total Discount', 'Discount', 'CGST', 'SGST', 'IGST', 'Round Off'];
        const isBold = fieldsToBold.includes(label);

        return {
          columns: [
            { text: '', width: '*' },
            {
              text: label + ' :',
              fontSize: isTotal ? fontSize + 4 : fontSize + 2,
              bold: isBold,
              color: theme.textColor,
              width: 'auto',
              alignment: 'right',

            },
            {
              text: row[1].text,
              fontSize: isTotal ? fontSize + 4 : fontSize + 2,
              bold: isBold,
              color: theme.textColor,
              width: 70,
              alignment: 'right',
            },
          ],
          margin: [0, 2, 0, 1],
        };
      }));

      // NET AMOUNT (highlight from config)
      if (totalRow) {
        const netAmountBg = totalsConfig.grandTotalBackgroundColor || '#000000';
        const netAmountColor = totalsConfig.grandTotalTextColor || '#FFFFFF';
        stack.push({
          table: {
            widths: ['*', 'auto', 70],
            body: [[
              { text: '', border: [false, true, false, false] },
              { text: 'NET AMOUNT :', bold: true, color: netAmountColor, fontSize: isThermalTem ? fontSize + 6 : fontSize + 3, margin: [0, 5, -14, 4], border: [false, true, false, false], alignment: 'right' },
              { text: totalRow[1].text, bold: true, color: netAmountColor, fontSize: isThermalTem ? fontSize + 6 : fontSize + 3, alignment: 'right', margin: [0, 4, -4, 4], border: [false, true, false, false] }
            ]]
          },
          // layout: 'noBorders',
          fillColor: netAmountBg,
          margin: [0, 5, 0, 2]
        });
      }
      // console.log(this.invoiceData)
      // Payment details
      if (this.invoiceData.paidamount !== undefined) {
        stack.push({
          columns: [
            { text: '', width: '*' },
            { text: 'Received Amt. :', fontSize: fontSize + 4, color: theme.textColor, width: 'auto', alignment: 'right' },
            { text: this.formatCurrency(this.invoiceData.paidamount, ''), fontSize: fontSize + 4, color: theme.textColor, width: 70, alignment: 'right' }
          ],
          margin: [0, 1, 0, 1]
        });
        // stack.push(dashedLine);
      }
      // console.log(this.invoiceData, 'Invoice data for balance amount');
      if (this.invoiceData.balanceamount !== undefined && this.invoiceData.paymentMethod != 'CASH,CREDIT') {
        stack.push({
          columns: [
            { text: '', width: '*' },
            { text: 'Balance Amt. :', fontSize: fontSize + 4, color: theme.textColor, width: 'auto', alignment: 'right' },
            { text: this.formatCurrency(this.invoiceData.balanceamount, ''), fontSize: fontSize + 4, color: theme.textColor, width: 70, alignment: 'right' }
          ],
          margin: [0, 1, 0, 1]
        });
        // stack.push(dashedLine);
      }


      // Points details
      if (totalsConfig.showLoyaltyPoints && loyaltyPoints > 0) {
        stack.push({
          columns: [
            { text: '', width: '*' },
            { text: 'Loyalty Points :', fontSize, color: theme.textColor, width: 'auto', alignment: 'right' },
            { text: loyaltyPoints.toString(), fontSize, color: theme.textColor, width: 70, alignment: 'right' }
          ],
          margin: [0, 1, 0, 1]
        });
      }

      const consumedPoints = this.invoiceData.consumedPoints || 0;
      if (totalsConfig.showConsumedPoints && consumedPoints > 0) {
        stack.push({
          columns: [
            { text: '', width: '*' },
            { text: 'Consumed Points :', fontSize, color: theme.textColor, width: 'auto', alignment: 'right' },
            { text: consumedPoints.toString(), fontSize, color: theme.textColor, width: 70, alignment: 'right' }
          ],
          margin: [0, 1, 0, 1]
        });
      }

      const redeemedPoints = this.invoiceData.redeemedPoints || 0;
      if (totalsConfig.showRedeemedPoints && redeemedPoints > 0) {
        stack.push({
          columns: [
            { text: '', width: '*' },
            { text: 'Redeemed Points :', fontSize, color: theme.textColor, width: 'auto', alignment: 'right' },
            { text: redeemedPoints.toString(), fontSize, color: theme.textColor, width: 70, alignment: 'right' }
          ],
          margin: [0, 1, 0, 1]
        });
      }

      // Today's Savings (MRP - Rate for each item)
      // const totalDisc = this.invoiceData.items.reduce((sum, item) => {
      //   const mrp = Number(item.mrp) || 0;
      //   const price = Number(item.price) || 0;
      //   const qty = Number(item.quantity) || 0;
      //   return sum + ((mrp - price) * qty);
      // }, 0);
      // Today's Savings (MRP - Selling Price)
      const totalDiscRaw = this.invoiceData.items.reduce((sum, item) => {
        const mrp = Number(item.mrp) || 0;
        const price = Number(item.price) || 0;
        const qty = Number(item.quantity) || 0;

        let sellingPrice = price;

        // If inclusive tax enabled, add tax per unit
        if (totalsConfig.showInclusiveTax) {
          const taxAmount =
            Number(item.taxamount || item.taxAmount) || 0;

          const taxPerUnit = qty > 0 ? taxAmount / qty : 0;

          sellingPrice = price + taxPerUnit;
        }

        return sum + ((mrp - sellingPrice) * qty);
      }, 0);
      // paise kept like the other amounts; rounding to 2 decimals drops float noise
      const totalDisc = Math.round(totalDiscRaw * 100) / 100;
      // console.log(totalDisc, 'total discount/savings for the invoice');

      if (totalDisc > 0) {
        stack.push({
          table: {
            widths: ['*'],
            body: [[
              { text: `Today's Savings : ${this.formatCurrency(totalDisc, '')}`, bold: true, alignment: 'center', fontSize: fontSize + 4, margin: [0, 2, 0, 2] }
            ]]
          },
          layout: {
            hLineWidth: (i: number) => (i === 0 || i === 1 ? 1 : 0),
            vLineWidth: () => 0,
            hLineColor: () => '#000000',
            vLineColor: () => '#000000',
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
          },
          margin: [0, 5, 0, 2]
        });
      }

      hsnAndTotalsComponent = { stack, margin: [0, 2, 0, 2] } as any;
    } else if (
      hsnConfig &&
      hsnConfig.show &&
      this.invoiceData.hsnBreakup &&
      this.invoiceData.hsnBreakup.length > 0
    ) {
      this._hsnBuiltInsideTotals = true;
      hsnAndTotalsComponent = this.buildUnifiedHsnAndTotalsTable(rows, tableLayout);
    } else {
      hsnAndTotalsComponent = {
        columns: [
          { width: '*', text: '' },
          {
            width: 'auto',
            table: {
              widths: ['auto', 100],
              body: rows,
            },
            layout: tableLayout,
          },
        ],
        margin: [0, 0, 0, 0],
      };
    }

    const totalsTable = hsnAndTotalsComponent;

    const content: any[] = [];
    content.push(totalsTable);
    if (totalsConfig.showAmountInWords) {
      const grandTotal =
        this.invoiceData.totalAmount || this.invoiceData.grandTotal || 0;
      const finalTotal =
        grandTotal - (Number(this.invoiceData.loyaltyAmount) || 0);
      const amountInWords = this.numberToWords(finalTotal);

      const amountInWordsFullText = `${totalsConfig.amountInWordsPrefix}${amountInWords}${totalsConfig.amountInWordsSuffix}`;
      const amountInWordsText = {
        text: amountInWordsFullText,
        font: this.getFontForText(amountInWordsFullText),
        fontSize: totalsConfig.fontSize || theme.baseFontSize,
        italics: totalsConfig.amountInWordsItalic !== undefined ? totalsConfig.amountInWordsItalic : true,
        bold: totalsConfig.amountInWordsBold || false,
        margin: [0, 5, 0, 0],
        noWrap: false,
      };

      if (totalsConfig.amountInWordsShowBorder) {
        const borderStyle = totalsConfig.amountInWordsBorderStyle || 'solid';
        const layout = this.getBorderLayout(
          borderStyle,
          totalsConfig.amountInWordsBorderWidth || 0.5,
          totalsConfig.amountInWordsBorderColor || theme.borderColor || '#000000'
        );

        content.push({
          table: {
            widths: ['*'],
            body: [[amountInWordsText]],
          },
          layout: layout,
          margin: [0, 0, 0, 0],
        });
      } else {
        content.push(amountInWordsText);
      }
    }

    return this.applyFooterScaling({
      stack: content,
      margin: [0, 0, 0, 0], // Removed sectionSpacing to eliminate gaps
      unbreakable: true,  // Prevent totals section from breaking across pages
    });
  }

  buildInvoiceDetailsSection(inlineWithParty = false): Content {
    const invoiceConfig = this.config.invoiceDetails;
    const theme = this.config.themeSettings;
    const pageSettings = this.config.pageSettings;

    console.log(invoiceConfig, '--------------------------------------------')
    // THERMAL layout: simple text with dashed lines, no tables
    if (pageSettings.pageSize === 'THERMAL') {
      return this.buildThermalInvoiceDetails(invoiceConfig, theme);
    }

    // ═══ A5 PORTRAIT: Fixed-height invoice details matching screenshot ═══
    if (pageSettings.pageSize === 'A5' && pageSettings.orientation !== 'landscape') {
      return this.buildA5PortraitInvoiceDetails(invoiceConfig, theme);
    }
    const tableRows: any[][] = [];
    const fontSize = theme.baseFontSize - 1;
    // console.log(this.invoiceData, 'invoiceData')
    if (invoiceConfig.showInvoiceNumber) {
      let invoiceNumber = this.invoiceData.invoiceNumber || '-';
      if (typeof invoiceNumber === 'string' && invoiceNumber.startsWith('SI-')) {
        invoiceNumber = invoiceNumber.substring(3);
      }
      let invoiceDate = this.invoiceData.invoiceDate
        ? typeof this.invoiceData.invoiceDate === 'string'
          ? this.invoiceData.invoiceDate
          : this.invoiceData.invoiceDate.toLocaleDateString()
        : '-';

      const invoiceTime = this.invoiceData.invoiceTime || '';
      const dateLabel = invoiceConfig.showTime ? 'Date & Time' : 'Dated';
      const dateValue = invoiceConfig.showTime && invoiceTime
        ? `${invoiceDate} ${invoiceTime}`
        : invoiceDate;

      if (inlineWithParty) {
        console.log(inlineWithParty, 'inline with party')
        const tableBody: any[][] = [
          [
            { text: 'Invoice No. :', bold: true, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
            { text: invoiceNumber, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
          ],
          [
            { text: 'Date :', bold: true, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
            { text: invoiceDate, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
          ],
        ];

        if (invoiceConfig.showTime && invoiceTime) {
          tableBody.push([
            { text: 'Time :', bold: true, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
            { text: invoiceTime, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
          ]);
        }

        const paymentMethod = (this.invoiceData as any).paymentMethod || '';
        if (invoiceConfig.showModeOfPayment && paymentMethod) {
          tableBody.push([
            { text: 'Mode of Payment :', bold: true, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
            { text: paymentMethod, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
          ]);
        }
        console.log(this.invoiceData)
        const transportName = this.invoiceData.dispatchedThrough
        console.log(transportName)
        if (invoiceConfig.showTransportName && transportName) {
          tableBody.push([
            { text: 'Transport Name :', bold: true, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
            { text: transportName, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
          ]);
        }
        const vehicleNumber = this.invoiceData.vehicleNumber
        console.log(vehicleNumber)
        if (invoiceConfig.showVehicleNumber && vehicleNumber) {
          tableBody.push([
            { text: 'Vehicle Number :', bold: true, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
            { text: vehicleNumber, fontSize: fontSize, border: [false, false, false, false], margin: [4, 1, 4, 0] },
          ]);
        }

        return {
          table: {
            widths: ['auto', '*'],
            body: tableBody,
          },
          layout: 'noBorders',
          margin: [0, 0, 0, 0],
        };
      }
      if (invoiceConfig.showTime && this.invoiceData.invoiceTime) {
        invoiceDate = `${invoiceDate} ${this.invoiceData.invoiceTime}`;
      }

      const showDestination = invoiceConfig.showDestination !== false;
      const destination = showDestination ? (this.invoiceData.destination || '-') : ''
      const paymentMethod = (this.invoiceData as any).paymentMethod || '';
      const row1 = [
        {
          text: 'Invoice No.',
          fillColor: '#F8F9FA',
          bold: true,
          fontSize: fontSize,
        },
        { text: invoiceNumber, fontSize: fontSize },
        { text: dateLabel, fillColor: '#F8F9FA', bold: true, fontSize: fontSize },
        { text: invoiceDate, fontSize: fontSize },
      ];

      if (invoiceConfig.showModeOfPayment && paymentMethod) {
        row1.push(
          { text: 'Mode of Payment', fillColor: '#F8F9FA', bold: true, fontSize: fontSize },
          { text: paymentMethod, fontSize: fontSize }
        );
      } else if (showDestination) {
        row1.push(
          { text: 'Destination', fillColor: '#F8F9FA', bold: true, fontSize: fontSize },
          { text: destination, fontSize: fontSize }
        );
      }

      tableRows.push(row1);
    }
    if (invoiceConfig.showReferenceNo) {
      const referenceNo = this.invoiceData.referenceNo || '-';
      const referenceDate = this.invoiceData.referenceDate || '-';
      const showDispatchedThrough = invoiceConfig.showDispatchedThrough !== false;
      const dispatchedThrough = showDispatchedThrough ? (this.invoiceData.dispatchedThrough || '-') : '';
      const row2 = [
        {
          text: invoiceConfig.referenceNoLabel || 'Reference No.',
          fillColor: '#F8F9FA',
          bold: true,
          fontSize: fontSize,
        },
        { text: referenceNo, fontSize: fontSize },
        {
          text: invoiceConfig.referenceDateLabel || 'Reference Date',
          fillColor: '#F8F9FA',
          bold: true,
          fontSize: fontSize,
        },
        { text: referenceDate, fontSize: fontSize },
      ];

      if (showDispatchedThrough) {
        row2.push(
          { text: 'Dispatched Through', fillColor: '#F8F9FA', bold: true, fontSize: fontSize },
          { text: dispatchedThrough, fontSize: fontSize }
        );
      }

      tableRows.push(row2);
    }
    if (invoiceConfig.showSODCNo) {
      const sodcNo = this.invoiceData.sodcNo || '-';
      const sodcDate = this.invoiceData.sodcDate || '-';

      const row3 = [
        {
          text: invoiceConfig.sodcNoLabel || 'SO / DC No.',
          fillColor: '#F8F9FA',
          bold: true,
          fontSize: fontSize,
        },
        { text: sodcNo, fontSize: fontSize },
        {
          text: invoiceConfig.sodcDateLabel || 'SO / DC Date',
          fillColor: '#F8F9FA',
          bold: true,
          fontSize: fontSize,
        },
        { text: sodcDate, fontSize: fontSize },
      ];

      if (invoiceConfig.showEwayBillNumber) {
        const rightLabel = invoiceConfig.ewayBillLabel || 'e-Way Bill No. & Date';
        const rightValue = this.invoiceData.ewayBillNumber
          ? `${this.invoiceData.ewayBillNumber} / ${this.invoiceData.ewayBillDate || '-'}`
          : '-';
        row3.push(
          { text: rightLabel, fillColor: '#F8F9FA', bold: true, fontSize: fontSize },
          { text: rightValue, fontSize: fontSize }
        );
      }
      if (invoiceConfig.showVehicleNumber) {
        row3.push(
          { text: 'Vehicle Number', fillColor: '#F8F9FA', bold: true, fontSize: fontSize },
          { text: this.invoiceData.vehicleNumber || '-', fontSize: fontSize }
        );
      }

      tableRows.push(row3);
    }
    const transportName = this.invoiceData.dispatchedThrough
    const vehicleNumber = this.invoiceData.vehicleNumber
    let row4 = []
    if (invoiceConfig.showTransportName && transportName) {
      row4.push(
        // { text: '', bold: true, fillColor: '#F8F9FA', fontSize: fontSize },
        // { text: '', bold: true, fillColor: '#F8F9FA', fontSize: fontSize },
        { text: 'Transport Name :' },
        { text: transportName, fontSize: fontSize }
      )
    }
    if (invoiceConfig.showVehicleNumber && vehicleNumber) {
      row4.push(
        { text: 'Vehicle Number :' },
        { text: vehicleNumber, fontSize: fontSize }
      )
    }
    if (row4.length > 0) {
      tableRows.push(row4);
    }
    if (tableRows.length === 0) {
      return {
        text: '',
        margin: [0, 0, 0, this.config.layoutSettings.sectionSpacing],
      };
    }

    const maxColumns = Math.max(...tableRows.map(row => row.length));
    const columnWidths = [];
    for (let i = 0; i < maxColumns; i++) {
      columnWidths.push(i % 2 === 0 ? 'auto' : '*');
    }

    // Pad shorter rows with empty cells to match maxColumns (prevents pdfmake column mismatch)
    for (const row of tableRows) {
      while (row.length < maxColumns) {
        row.push({ text: '', fontSize: fontSize });
      }
    }

    if (invoiceConfig.showBorder) {
      return {
        table: {
          widths: columnWidths,
          body: tableRows,
        },
        layout: {
          hLineWidth: (i: number, node: any) => {
            if (inlineWithParty && i === 0) return 0;
            return invoiceConfig.borderWidth || 0.5;
          },
          vLineWidth: (i: number, node: any) => {
            if (inlineWithParty && (i === 0 || i === node.table.widths.length)) return 0;
            return invoiceConfig.borderWidth || 0.5;
          },
          hLineColor: () => invoiceConfig.borderColor || theme.borderColor || '#000000',
          vLineColor: () => invoiceConfig.borderColor || theme.borderColor || '#000000',
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 3,
          paddingBottom: () => 3,
        },
        margin: [0, 0, 0, 0],
      };
    }

    return {
      table: {
        widths: columnWidths,
        body: tableRows,
      },
      layout: 'noBorders',
      margin: [0, 0, 0, 0],
    };
  }

  private buildThermalInvoiceDetails(invoiceConfig: any, theme: any): Content {
    const fontSize = theme.baseFontSize + 1;
    const lineColor = theme.textColor || '#000000';
    const dashedLine = {
      table: {
        widths: ['*'],
        body: [[{ text: '', border: [false, true, false, true] }]],
      },
      layout: {
        hLineWidth: (i: number) => (i === 0 ? 0.5 : 0),
        vLineWidth: () => 0,
        hLineColor: () => lineColor,
        hLineStyle: () => ({ dash: { length: 3, space: 2 } }),
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
      margin: [0, 2, 0, 2] as number[],
    };

    const content: any[] = [];
    content.push(dashedLine);

    // Invoice name as the thermal bill title
    const invoiceName = getInvoiceName(this.config);
    if (invoiceName) {
      content.push({
        text: invoiceName,
        font: this.getFontForText(invoiceName),
        bold: true,
        fontSize: theme.baseFontSize + 3,
        alignment: 'center',
        color: theme.textColor,
        margin: [0, 1, 0, 3],
      });
    }

    let invoiceNumber = this.invoiceData.invoiceNumber || '-';
    // console.log(invoiceNumber, 'invoiceNumber')
    if (typeof invoiceNumber === 'string' && invoiceNumber.startsWith('SI-')) {
      invoiceNumber = invoiceNumber.substring(3);
    }
    const invoiceDate = this.invoiceData.invoiceDate
      ? typeof this.invoiceData.invoiceDate === 'string'
        ? this.invoiceData.invoiceDate
        : this.invoiceData.invoiceDate.toLocaleDateString()
      : '-';
    const invoiceTime = this.invoiceData.invoiceTime || '';
    const paymentMethod = (this.invoiceData as any).paymentMethod || '';

    // ── ROW 1: Inv No (left) | Date (right, only if showInvoiceDate) ──────────
    const showInvNo = invoiceConfig.showInvoiceNumber;
    const showDate = invoiceConfig.showInvoiceDate !== false;   // hide when explicitly false
    const showTime = invoiceConfig.showTime && !!invoiceTime;
    const showPay = invoiceConfig.showModeOfPayment && !!paymentMethod;

    // ── SINGLE TABLE: all invoice detail rows share column widths ──────────────
    // Col 0: left label (fixed width = widest label)
    // Col 1: left value (stretches)
    // Col 2: right label (fixed width)
    // Col 3: right value (fixed width)

    const detailRows: any[][] = [];

    const colon = (show: boolean) => ({
      text: show ? ':' : '',
      bold: true,
      fontSize,
      color: theme.textColor,
      noWrap: true,
    });

    // ROW 1: Inv No | Date
    if (showInvNo || showDate) {
      detailRows.push([
        showInvNo
          ? { text: 'Inv No', bold: true, fontSize, color: theme.textColor, noWrap: true }
          : { text: '' },
        colon(showInvNo),
        showInvNo
          ? { text: invoiceNumber, bold: true, fontSize, color: theme.textColor }
          : { text: '' },
        showDate
          ? { text: 'Date', bold: true, fontSize, color: theme.textColor, noWrap: true }
          : { text: '' },
        colon(showDate),
        showDate
          ? { text: invoiceDate, bold: true, fontSize, color: theme.textColor, noWrap: true }
          : { text: '' },
      ]);
    }

    // ROW 2: Time | Pay
    if (showTime || showPay) {
      detailRows.push([
        showTime
          ? { text: 'Time', bold: true, fontSize, color: theme.textColor, noWrap: true }
          : { text: '' },
        colon(showTime),
        showTime
          ? { text: invoiceTime, bold: true, fontSize, color: theme.textColor }
          : { text: '' },
        showPay
          ? { text: 'Pay', bold: true, fontSize, color: theme.textColor, noWrap: true }
          : { text: '' },
        colon(showPay),
        showPay
          ? { text: paymentMethod, bold: true, fontSize, color: theme.textColor, noWrap: true }
          : { text: '' },
      ]);
    }

    if (detailRows.length > 0) {
      content.push({
        table: {
          // left-label | : | left-value | right-label | : | right-value
          widths: ['auto', 'auto', '*', 'auto', 'auto', 'auto'],
          body: detailRows,
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: (i: number) => i === 0 ? 0 : 2,
          paddingRight: (i: number) => i === 1 || i === 4 ? 1 : 2, // tight around colons
          paddingTop: () => 1,
          paddingBottom: () => 1,
        },
        margin: [0, 1, 0, 1] as number[],
      });
    }

    // ── Party dashed separator ────────────────────────────────────────────────
    const partyConfig = this.config.partyDetails;
    const billToFields = partyConfig?.billToFields;
    const showPartyDetails = this.config.layoutSettings.showPartyDetails !== false;
    const showBillTo = partyConfig?.showBillTo !== false;
    const hasPartyName = billToFields?.showCustomerName !== false;
    const hasPartyPhone = billToFields?.showCustomerPhone !== false;

    const party = this.invoiceData.party || {
      name: this.invoiceData.customerName || '',
      phone: this.invoiceData.customerMobile || '',
      mobile: this.invoiceData.customerMobile || '',
    };

    const customerName = hasPartyName ? (party.name || '') : '';
    const customerPhone = hasPartyPhone ? (party.phone || party.mobile || '') : '';
    const willShowParty = showPartyDetails && showBillTo &&
      (!!customerName || (!!customerPhone && customerPhone !== '-'));

    if (willShowParty) {
      content.push(dashedLine);
    }

    return {
      stack: content,
      margin: [0, 0, 0, 0],
    };
  }

  buildTermsAndConditionsSection(): Content {
    const footerConfig = this.config.footer;
    const theme = this.config.themeSettings;
    // console.log('Building Terms and Conditions Section with config:', footerConfig);
    const content: any[] = [];

    if (footerConfig.showTermsAndConditions) {
      const termsText = footerConfig.termsText || '-';
      const termsContent: any[] = [];

      termsContent.push({
        text: 'Terms and Conditions',
        fontSize: theme.baseFontSize,
        bold: true,
        color: theme.primaryColor,
        margin: [0, 0, 0, 2],
      });

      const points = termsText.split('\n').filter((p: string) => p.trim() !== '');
      const isThermal = this.config.pageSettings.pageSize === 'THERMAL';

      if (isThermal) {
        return { text: '' };
      }

      termsContent.push({
        ol: points.map((p: string) => p.trim().replace(/^\d+\.\s*/, '')),
        fontSize: isThermal ? theme.baseFontSize : theme.baseFontSize - 3,
        color: theme.textColor,
        bold: footerConfig.termsBold || false,
        italics: footerConfig.termsItalic || false,
        margin: [5, 0, 0, 0],
      });

      if (!isThermal && footerConfig.showTermsBorder) {
        const borderStyle = footerConfig.termsBorderStyle || 'solid';
        const isA4 = this.config.pageSettings.pageSize === 'A4';
        const layout = isA4 ? {
          hLineWidth: () => footerConfig.borderWidth || 0.5,
          vLineWidth: () => 0, // No left/right borders for A4
          hLineColor: () => footerConfig.borderColor || theme.borderColor || '#000000',
          vLineColor: () => 'transparent',
        } : this.getBorderLayout(
          borderStyle,
          footerConfig.borderWidth || 0.5,
          footerConfig.borderColor || theme.borderColor || '#000000'
        );

        content.push({
          table: {
            widths: ['*'],
            body: [[{ stack: termsContent, margin: [5, 2, 5, 2] }]],
          },
          layout: layout,
          fillColor: footerConfig.termsBackgroundColor || null,
        });
      } else {
        content.push({ stack: termsContent });
      }
    }

    const isA4ForTerms = this.config.pageSettings.pageSize === 'A4';

    return this.applyFooterScaling({
      stack: content,
      margin: [0, 0, 0, isA4ForTerms ? 0 : this.config.layoutSettings.sectionSpacing],
      unbreakable: true,  // Prevent terms section from breaking across pages
    });
  }

  private renderThermalTermsAndConditions(): any[] {
    const footerConfig = this.config.footer;
    const theme = this.config.themeSettings;
    const termsContent: any[] = [];

    if (!footerConfig.showTermsAndConditions) return [];

    const termsText = footerConfig.termsText
      || this.invoiceData.termsAndConditions
      || this.invoiceData.terms
      || '';

    if (!termsText.trim()) return [];

    // Split by newline (not by '.') to preserve full sentences per point
    const points = termsText
      .split('.')
      .map((p: string) => p)
      .filter((p: string) => p !== '');

    const fontSize = theme.baseFontSize + 1;

    termsContent.push({
      text: 'Terms & Conditions',
      fontSize: theme.baseFontSize + 2,
      bold: true,
      decoration: 'underline',
      margin: [0, 3, 0, 5],
    });

    const listPoints = points.filter(p => !p.toLowerCase().includes('thank you'));
    const thankYouPoints = points.filter(p => p.toLowerCase().includes('thank you'));

    if (listPoints.length > 0) {
      const olItems = listPoints.map(p => p.trim().replace(/^\d+[\.\)]\s*/, ''));
      termsContent.push({
        ol: olItems,
        fontSize: fontSize,
        color: theme.textColor,
        margin: [0, 0, 0, 5],
        bold: true,
      });
    }

    if (thankYouPoints.length > 0) {
      termsContent.push({
        text: thankYouPoints[0].trim().replace(/^\d+[\.\)]\s*/, ''),
        alignment: 'center',
        bold: true,
        fontSize: theme.baseFontSize + 1,
        margin: [0, 10, 0, 5],
      });
    }

    // if (footerConfig.thermalFooterText) {
    //   termsContent.push({
    //     text: footerConfig.thermalFooterText,
    //     alignment: 'center',
    //     fontSize: theme.baseFontSize,
    //     margin: [0, 5, 0, 5],
    //   });
    // }

    return termsContent;
  }

  private _hsnBuiltInsideTotals = false;

  private buildThermalHsnBreakup(hsnConfig: any): Content {
    if (!this.invoiceData.hsnBreakup || this.invoiceData.hsnBreakup.length === 0) {
      return { text: '' };
    }

    const theme = this.config.themeSettings;
    const fontSize = 7;
    const headerFontSize = 8;
    const lineColor = theme.textColor || '#000000';

    // Pre-calculate totals to decide which columns to show
    const totalCgst = this.invoiceData.hsnBreakup.reduce((sum: number, h: any) => sum + (h.cgstAmount || 0), 0);
    const totalSgst = this.invoiceData.hsnBreakup.reduce((sum: number, h: any) => sum + (h.sgstAmount || 0), 0);
    const totalIgst = this.invoiceData.hsnBreakup.reduce((sum: number, h: any) => sum + (h.igstAmount || 0), 0);
    const totalTaxable = this.invoiceData.hsnBreakup.reduce((sum: number, h: any) => sum + (h.taxableValue || 0), 0);
    const totalTax = this.invoiceData.hsnBreakup.reduce((sum: number, h: any) => sum + (h.totalTax || 0), 0);

    // Determine which individual tax columns are active
    const showCGST = hsnConfig.showCGST !== false && totalCgst > 0;
    const showSGST = hsnConfig.showSGST !== false && totalSgst > 0;
    const showIGST = hsnConfig.showIGST !== false && totalIgst > 0;

    const header = {
      text: 'HSN Tax Breakup',
      bold: true,
      fontSize: theme.baseFontSize - 1,
      margin: [0, 2, 0, 2]
    };

    const tableBody: any[][] = [];
    const widths: any[] = [];
    const headerRow: any[] = [];

    // HSN/SAC
    if (hsnConfig.showHSNSAC !== false) {
      headerRow.push({ text: hsnConfig.hsnSACLabel || 'HSN/SAC', bold: true, fontSize: headerFontSize });
      widths.push(32);
    }

    // Combined GST% column (sum of cgst% + sgst% OR igst%)
    headerRow.push({ text: 'GST%', bold: true, fontSize: headerFontSize, alignment: 'center' });
    widths.push(16);

    // Taxable Value
    if (hsnConfig.showTaxableValue !== false) {
      headerRow.push({ text: 'Taxable Value', bold: true, fontSize: headerFontSize, alignment: 'right' });
      widths.push('*');
    }

    // CGST Amount only (no % column)
    if (showCGST) {
      headerRow.push({ text: 'CGST', bold: true, fontSize: headerFontSize, alignment: 'right' });
      widths.push('*');
    }

    // SGST Amount only
    if (showSGST) {
      headerRow.push({ text: 'SGST', bold: true, fontSize: headerFontSize, alignment: 'right' });
      widths.push('*');
    }

    // IGST Amount only
    if (showIGST) {
      headerRow.push({ text: 'IGST', bold: true, fontSize: headerFontSize, alignment: 'right' });
      widths.push('*');
    }

    // Total Tax
    if (hsnConfig.showTotalTax !== false) {
      headerRow.push({ text: hsnConfig.totalTaxLabel || 'Total', bold: true, fontSize: headerFontSize, alignment: 'right' });
      widths.push('*');
    }

    tableBody.push(headerRow);

    // Data rows
    this.invoiceData.hsnBreakup.forEach((hsn: any) => {
      const row: any[] = [];

      // Combined GST% = cgst% + sgst% for intra-state, or igst% for inter-state
      const gstPercent = (hsn.igstPercent || 0) > 0
        ? (hsn.igstPercent || 0)
        : (hsn.cgstPercent || 0) + (hsn.sgstPercent || 0);

      if (hsnConfig.showHSNSAC !== false) {
        row.push({ text: hsn.hsnCode || '-', fontSize });
      }

      row.push({ text: gstPercent > 0 ? `${gstPercent}%` : '-', fontSize, alignment: 'center' });

      if (hsnConfig.showTaxableValue !== false) {
        row.push({ text: (hsn.taxableValue || 0).toFixed(2), fontSize, alignment: 'right' });
      }
      if (showCGST) {
        row.push({ text: (hsn.cgstAmount || 0).toFixed(2), fontSize, alignment: 'right' });
      }
      if (showSGST) {
        row.push({ text: (hsn.sgstAmount || 0).toFixed(2), fontSize, alignment: 'right' });
      }
      if (showIGST) {
        row.push({ text: (hsn.igstAmount || 0).toFixed(2), fontSize, alignment: 'right' });
      }
      if (hsnConfig.showTotalTax !== false) {
        row.push({ text: (hsn.totalTax || 0).toFixed(2), fontSize, alignment: 'right' });
      }

      tableBody.push(row);
    });

    // Totals row
    const totalsRow: any[] = [];

    if (hsnConfig.showHSNSAC !== false) {
      totalsRow.push({ text: 'Total', bold: true, fontSize });
    }

    totalsRow.push({ text: '', fontSize }); // blank for GST% column

    if (hsnConfig.showTaxableValue !== false) {
      totalsRow.push({ text: totalTaxable.toFixed(2), bold: true, fontSize, alignment: 'right' });
    }
    if (showCGST) {
      totalsRow.push({ text: totalCgst.toFixed(2), bold: true, fontSize, alignment: 'right' });
    }
    if (showSGST) {
      totalsRow.push({ text: totalSgst.toFixed(2), bold: true, fontSize, alignment: 'right' });
    }
    if (showIGST) {
      totalsRow.push({ text: totalIgst.toFixed(2), bold: true, fontSize, alignment: 'right' });
    }
    if (hsnConfig.showTotalTax !== false) {
      totalsRow.push({ text: totalTax.toFixed(2), bold: true, fontSize, alignment: 'right' });
    }

    tableBody.push(totalsRow);

    return {
      stack: [
        header,
        {
          table: { widths, body: tableBody },
          layout: {
            hLineWidth: (i: number, node: any) =>
              (i === 0 || i === 1 || i === node.table.body.length - 1 || i === node.table.body.length ? 0.5 : 0),
            vLineWidth: () => 0,
            hLineColor: () => lineColor,
            vLineColor: () => lineColor,
            hLineStyle: () => ({ dash: { length: 2, space: 1 } }),
            paddingLeft: () => 1,
            paddingRight: () => 1,
            paddingTop: () => 1,
            paddingBottom: () => 1,
          }
        } as any
      ],
      margin: [0, 2, 0, 2]
    } as any;
  }

  buildHsnBreakupSection(forceFromTotals = false): Content {
    if (this._hsnBuiltInsideTotals && !forceFromTotals) {
      return { text: '' };
    }

    if (forceFromTotals) {
      this._hsnBuiltInsideTotals = true;
    }

    const hsnConfig = this.config.hsnBreakup;
    if (!hsnConfig || !hsnConfig.show) {
      return { text: '' };
    }

    const isThermal = this.config.pageSettings.pageSize === 'THERMAL';

    // In thermal mode, HSN Breakup is consolidated into buildFooterSection (below Declaration)
    if (isThermal) {
      return { text: '' };
    }

    if (
      !this.invoiceData.hsnBreakup ||
      this.invoiceData.hsnBreakup.length === 0
    ) {
      return { text: '' };
    }

    const theme = this.config.themeSettings;
    const currencySymbol = this.config.additionalSettings.currencySymbol;
    const content: any[] = [];
    const widths: any[] = [];
    const tableWidth = hsnConfig.tableWidth || 'auto';
    const columnWidths = hsnConfig.columnWidths || 'auto';

    const getColumnWidth = () => {
      if (tableWidth === 'full' || (typeof tableWidth === 'string' && tableWidth.includes('%'))) {
        if (columnWidths === 'equal') {
          return '*';
        } else {
          return '*';
        }
      }
      return 'auto';
    };

    const headerRow1: any[] = [];
    const headerRow2: any[] = [];

    if (hsnConfig.showHSNSAC !== false) {
      headerRow1.push({
        text: hsnConfig.hsnSACLabel || 'HSN/SAC',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
        rowSpan: 2,
      });
      headerRow2.push({});
      widths.push(getColumnWidth());
    }

    if (hsnConfig.showTaxableValue !== false) {
      headerRow1.push({
        text: hsnConfig.taxableValueLabel || 'Taxable Value',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
        rowSpan: 2,
      });
      headerRow2.push({});
      widths.push(getColumnWidth());
    }

    if (hsnConfig.showCGST !== false) {
      headerRow1.push({
        text: hsnConfig.cgstLabel || 'CGST',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
        colSpan: 2,
      });
      headerRow1.push({});
      headerRow2.push({
        text: '%',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
      });
      headerRow2.push({
        text: 'Amt',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
      });
      widths.push(getColumnWidth());
      widths.push(getColumnWidth());
    }

    if (hsnConfig.showSGST !== false) {
      headerRow1.push({
        text: hsnConfig.sgstLabel || 'SGST',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
        colSpan: 2,
      });
      headerRow1.push({});
      headerRow2.push({
        text: '%',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
      });
      headerRow2.push({
        text: 'Amt',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
      });
      widths.push(getColumnWidth());
      widths.push(getColumnWidth());
    }

    if (hsnConfig.showIGST !== false) {
      headerRow1.push({
        text: hsnConfig.igstLabel || 'IGST',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
        colSpan: 2,
      });
      headerRow1.push({});
      headerRow2.push({
        text: '%',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
      });
      headerRow2.push({
        text: 'Amt',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
      });
      widths.push(getColumnWidth());
      widths.push(getColumnWidth());
    }

    // Total Tax column
    if (hsnConfig.showTotalTax !== false) {
      headerRow1.push({
        text: hsnConfig.totalTaxLabel || 'Total Tax',
        fillColor: hsnConfig.headerStyle.backgroundColor,
        color: hsnConfig.headerStyle.textColor,
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: hsnConfig.headerStyle.bold,
        alignment: 'center',
        rowSpan: 2,
      });
      headerRow2.push({});
      widths.push(getColumnWidth());
    }
    // console.log(this.invoiceData, 'hsnBreakup------------------')
    const bodyRows = this.invoiceData.hsnBreakup.map((hsn: any) => {
      const row: any[] = [];

      if (hsnConfig.showHSNSAC !== false) {
        row.push({
          text: hsn.hsnCode || '',
          fontSize: 7, //hsnConfig.headerStyle.fontSize,
          alignment: 'center',
        });
      }
      if (hsnConfig.showTaxableValue !== false) {
        // row.push({
        //   text: this.formatCurrency(hsn.taxableValue || 0, currencySymbol),
        //   fontSize: 7, //hsnConfig.headerStyle.fontSize,
        //   alignment: 'right',
        // });
        const displayTaxable = this.invoiceData.discaftertax
          ? this.invoiceData.items.reduce((itemSum: number, item: any) => {
            const itemHsn = item.hsncode || item.hsn;
            if (itemHsn === hsn.hsnCode) {
              const itemPrice = item.price || 0;
              const itemQty = item.quantity || 0;
              return itemSum + itemPrice * itemQty;
            }
            return itemSum;
          }, 0)
          // price × qty before discount
          : hsn.taxableValue || 0;                     // price × qty minus discount
        row.push({
          text: this.formatCurrency(displayTaxable, currencySymbol),
          fontSize: 7,
          alignment: 'right',
        });
      }
      if (hsnConfig.showCGST !== false) {
        row.push({
          text: `${hsn.cgstPercent || 0}%`,
          fontSize: 7, //hsnConfig.headerStyle.fontSize,
          alignment: 'center',
        });
        row.push({
          text: this.formatCurrency(hsn.cgstAmount || 0, currencySymbol),
          fontSize: 7, //hsnConfig.headerStyle.fontSize,
          alignment: 'right',
        });
      }
      if (hsnConfig.showSGST !== false) {
        row.push({
          text: `${hsn.sgstPercent || 0}%`,
          fontSize: 7, //hsnConfig.headerStyle.fontSize,
          alignment: 'center',
        });
        row.push({
          text: this.formatCurrency(hsn.sgstAmount || 0, currencySymbol),
          fontSize: 7, //hsnConfig.headerStyle.fontSize,
          alignment: 'right',
        });
      }
      if (hsnConfig.showIGST !== false) {
        row.push({
          text: `${hsn.igstPercent || 0}%`,
          fontSize: 7, //hsnConfig.headerStyle.fontSize,
          alignment: 'center',
        });
        row.push({
          text: this.formatCurrency(hsn.igstAmount || 0, currencySymbol),
          fontSize: 7, //hsnConfig.headerStyle.fontSize,
          alignment: 'right',
        });
      }
      if (hsnConfig.showTotalTax !== false) {
        row.push({
          text: this.formatCurrency(hsn.totalTax || 0, currencySymbol),
          fontSize: 7, //hsnConfig.headerStyle.fontSize,
          alignment: 'right',
        });
      }

      return row;
    });

    const totalRow: any[] = [];
    const totalTaxableValue = this.invoiceData.hsnBreakup.reduce(
      (sum: number, hsn: any) => sum + (hsn.taxableValue || 0),
      0,
    );
    const totalCGST = this.invoiceData.hsnBreakup.reduce(
      (sum: number, hsn: any) => sum + (hsn.cgstAmount || 0),
      0,
    );
    const totalSGST = this.invoiceData.hsnBreakup.reduce(
      (sum: number, hsn: any) => sum + (hsn.sgstAmount || 0),
      0,
    );
    const totalIGST = this.invoiceData.hsnBreakup.reduce(
      (sum: number, hsn: any) => sum + (hsn.igstAmount || 0),
      0,
    );
    const totalTax = this.invoiceData.hsnBreakup.reduce(
      (sum: number, hsn: any) => sum + (hsn.totalTax || 0),
      0,
    );

    if (hsnConfig.showHSNSAC !== false) {
      totalRow.push({
        text: 'Total',
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: true,
        alignment: 'center',
      });
    }
    if (hsnConfig.showTaxableValue !== false) {
      // totalRow.push({
      //   text: this.formatCurrency(totalTaxableValue, currencySymbol),
      //   fontSize: 7, //hsnConfig.headerStyle.fontSize,
      //   bold: true,
      //   alignment: 'right',
      // });
      const displayTotal = this.invoiceData.hsnBreakup.reduce(
        (sum: number, hsn: any) => sum + (
          hsn.discaftertax
            ? this.invoiceData.items.reduce((itemSum: number, item: any) => {
              const itemHsn = item.hsncode || item.hsn;
              if (itemHsn === hsn.hsnCode) {
                const itemPrice = item.price || 0;
                const itemQty = item.quantity || 0;
                return itemSum + itemPrice * itemQty;
              }
              return itemSum;
            }, 0)
            : (hsn.taxableValue || 0)
        ), 0
      );
      totalRow.push({
        text: this.formatCurrency(displayTotal, currencySymbol),
        fontSize: 7, bold: true, alignment: 'right',
      });
    }
    if (hsnConfig.showCGST !== false) {
      totalRow.push({ text: '', fontSize: hsnConfig.bodyStyle.fontSize });
      totalRow.push({
        text: this.formatCurrency(totalCGST, currencySymbol),
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: true,
        alignment: 'right',
      });
    }
    if (hsnConfig.showSGST !== false) {
      totalRow.push({ text: '', fontSize: hsnConfig.bodyStyle.fontSize });
      totalRow.push({
        text: this.formatCurrency(totalSGST, currencySymbol),
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: true,
        alignment: 'right',
      });
    }
    if (hsnConfig.showIGST !== false) {
      totalRow.push({ text: '', fontSize: hsnConfig.bodyStyle.fontSize });
      totalRow.push({
        text: this.formatCurrency(totalIGST, currencySymbol),
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: true,
        alignment: 'right',
      });
    }
    if (hsnConfig.showTotalTax !== false) {
      totalRow.push({
        text: this.formatCurrency(totalTax, currencySymbol),
        fontSize: 7, //hsnConfig.headerStyle.fontSize,
        bold: true,
        alignment: 'right',
      });
    }

    const tableDefinition: any = {
      table: {
        headerRows: 2,
        widths: widths,
        body: [headerRow1, headerRow2, ...bodyRows, totalRow],
      },
      layout: hsnConfig.showBorder ? {
        hLineWidth: () => hsnConfig.borderWidth || 0.5,
        vLineWidth: () => hsnConfig.borderWidth || 0.5,
        hLineColor: () => hsnConfig.borderColor || theme.borderColor || '#000000',
        vLineColor: () => hsnConfig.borderColor || theme.borderColor || '#000000',
      } : 'noBorders'
    };

    if (tableWidth === 'full' || (typeof tableWidth === 'string' && tableWidth.includes('%'))) {
      tableDefinition.width = '*';
    }

    content.push(tableDefinition);

    return this.applyFooterScaling({
      stack: content,
      margin: [0, 0, 0, this.config.layoutSettings.sectionSpacing],
      unbreakable: true,
    });
  }

  buildFooterSection(): Content {
    const footerConfig = this.config.footer;
    const theme = this.config.themeSettings;
    const businessName = this.invoiceData.business?.name || this.invoiceData.businessName || '';

    const content: any[] = [];

    if (footerConfig.showBankDetails) {
      const bankDetailsContent: any[] = [];
      bankDetailsContent.push({
        text: 'Bank Details',
        fontSize: theme.baseFontSize,
        bold: true,
        color: theme.primaryColor,
        margin: [0, 0, 0, 0],
      });

      const bankDetails = this.invoiceData.bankDetails;
      const bankDetailsLines: string[] = [];
      if (bankDetails) {
        if (bankDetails.bankName) bankDetailsLines.push(`Bank Name: ${bankDetails.bankName}`);
        if (bankDetails.accountNumber) bankDetailsLines.push(`Account Number: ${bankDetails.accountNumber}`);
        if (bankDetails.ifscCode) bankDetailsLines.push(`IFSC Code: ${bankDetails.ifscCode}`);
        if (bankDetails.accountHolderName) bankDetailsLines.push(`Account Holder: ${bankDetails.accountHolderName}`);
        if (bankDetails.upiId) bankDetailsLines.push(`UPI ID: ${bankDetails.upiId}`);
      }

      bankDetailsContent.push({
        text: bankDetailsLines.join('\n') || '',
        fontSize: theme.baseFontSize - 1,
        color: theme.textColor,
        margin: [0, 0, 0, 0],
      });

      let qrElement: any;
      const qrcodeData = this.invoiceData.qrcode || (footerConfig as any).qrcodeBase64 || (footerConfig as any).qrcode;
      if (qrcodeData && footerConfig.showQRCode) {
        qrElement = {
          image: qrcodeData,
          fit: [40, 40],
          alignment: 'right',
          margin: [0, 0, 0, 0]
        };
      } else {
        qrElement = {
          table: {
            widths: [40],
            body: [[{ text: ' ', margin: [0, 20, 0, 20] }]]
          },
          layout: 'noBorders',
          fillColor: '#F8F9FA',
          alignment: 'right'
        };
      }

      content.push({
        columns: [
          { width: '*', stack: bankDetailsContent },
          { width: 50, stack: [qrElement], alignment: 'right' }
        ],
        unbreakable: true,
        margin: [0, 2, 0, 0]
      });
    }

    const isThermal = this.config.pageSettings.pageSize === 'THERMAL';
    const fontSize = theme.baseFontSize - 1;
    const lineColor = theme.textColor || '#000000';

    const dashedLine = {
      table: {
        widths: ['*'],
        body: [[{ text: '', border: [false, true, false, false] }]],
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0,
        hLineColor: () => lineColor,
        vLineColor: () => lineColor,
        hLineStyle: () => ({ dash: { length: 3, space: 2 } }),
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 1,
        paddingBottom: () => 0,
      },
      margin: [0, 2, 0, 2],
    };

    if (footerConfig.showDeclaration && footerConfig.declarationText && this.config.layoutSettings.showFooter) {
      const declarationText = footerConfig.declarationText;
      const points = declarationText.split('\n').filter(p => p.trim() !== '');
      // console.log(this.invoiceData)
      if (isThermal) {
        // 2. Declaration (BELOW HSN Table)
        const thermalDeclStack: any[] = [
          {
            text: footerConfig.declarationLabel || 'Declaration',
            fontSize: theme.baseFontSize + 2,
            bold: true,
            margin: [0, 2, 0, 0],
          },
          {
            text: points.join(' ').replace(/^\d+\.\s*/, ''),
            fontSize: fontSize + 1,
            alignment: 'center',
            italics: true,
            margin: [0, 2, 0, 5],
            color: theme.textColor
          }
        ];
        content.push({ stack: thermalDeclStack });
        content.push(dashedLine);
      }
      else {
        const declarationStack: any[] = [
          {
            text: footerConfig.declarationLabel || 'Declaration:',
            fontSize: theme.baseFontSize,
            bold: true,
            margin: [0, 0, 0, 0],
          }
        ];
        declarationStack.push({
          ol: points.map(p => p.trim().replace(/^\d+\.\s*/, '')),
          fontSize: theme.baseFontSize - 1,
          margin: [5, 0, 0, 0],
          color: theme.textColor
        });

        const isA4 = this.config.pageSettings.pageSize === 'A4';
        content.push({
          table: {
            widths: ['*'],
            body: [[{ stack: declarationStack, margin: [5, 5, 5, 5] }]]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: (i: number) => (isA4 ? 0 : 0.5), // No left/right borders for A4
            hLineColor: () => '#000000',
            vLineColor: () => '#000000'
          },
          unbreakable: true
        });
      }
    }

    if (isThermal) {
      const hsnConfig = this.config.hsnBreakup;
      if (hsnConfig && hsnConfig.show && this.invoiceData.hsnBreakup && this.invoiceData.hsnBreakup.length > 0) {
        content.push(this.buildThermalHsnBreakup(hsnConfig));
      }

      if (this.invoiceData.qrcode && footerConfig.showQRCode) {
        content.push({
          text: 'Inclusive of All Taxes',
          fontSize: isThermal ? fontSize + 1 : fontSize,
          alignment: 'center',
          margin: [0, 10, 0, 5]
        });

        const qrData = this.invoiceData.qrcode;
        if (qrData.startsWith('data:image')) {
          content.push({
            image: qrData,
            width: 60,
            alignment: 'center',
            margin: [0, 5, 0, 10]
          });
        } else {
          content.push({
            qr: qrData,
            fit: 60,
            alignment: 'center',
            margin: [0, 5, 0, 10]
          });
        }
      }

      const termsStack = this.renderThermalTermsAndConditions();
      if (termsStack.length > 0) {
        content.push({ stack: termsStack, fontSize: theme.baseFontSize + 1, margin: [0, 5, 0, 5] });
      }
      // console.log(footerConfig)
      if (footerConfig.showNotes && footerConfig.notesText) {
        content.push({
          text: footerConfig.notesText,
          fontSize: isThermal ? theme.baseFontSize + 2 : theme.baseFontSize - 1,
          alignment: 'center',
          margin: [0, 5, 0, 5],
          color: theme.textColor,
          bold: true
        })
      }
      if (footerConfig.thermalFooterText) {
        content.push({
          text: footerConfig.thermalFooterText,
          alignment: 'center',
          fontSize: theme.baseFontSize + 1,
          margin: [0, 5, 0, 5],
          bold: true
        });
      }
    }

    if (this.config.pageSettings.pageSize === 'A4' && footerConfig.showSignature) {
      const isDual = this.config.layoutSettings.signatureLayout === 'dual';
      const businessName = this.invoiceData.business?.name || this.invoiceData.businessName || '';

      const row1: any[] = [{ text: '', margin: [0, 6, 0, 2] }];
      if (footerConfig.showForBusinessName && businessName) {
        row1.push({
          text: `For ${businessName}`,
          fontSize: theme.baseFontSize,
          bold: true,
          alignment: 'center',
          margin: [0, 6, 0, 2]
        });
      } else {
        row1.push({ text: '', margin: [0, 6, 0, 2] });
      }

      const row2: any[] = [{ text: '', margin: [0, 1, 0, 0] }];
      const signatureData = this.invoiceData.signature || (this.invoiceData.business as any)?.signature || footerConfig.signatureImage || (footerConfig as any).signature;
      if (signatureData) {
        row2.push({
          image: signatureData,
          fit: [100, 40],
          alignment: 'center',
          margin: [0, 1, 0, 0]
        });
      } else {
        row2.push({ text: '', margin: [0, 22, 0, 0] });
      }

      const row3: any[] = [];
      if (isDual) {
        row3.push({
          text: footerConfig.customerSealLabel || 'Customer Signature',
          fontSize: theme.baseFontSize - 1,
          alignment: 'center',
          margin: [0, 2, 0, 6]
        });
      } else {
        row3.push({ text: '', margin: [0, 2, 0, 6] });
      }

      if (footerConfig.signatureLabel) {
        row3.push({
          text: footerConfig.signatureLabel,
          fontSize: theme.baseFontSize - 1,
          alignment: 'center',
          margin: [0, 2, 0, 6]
        });
      } else {
        row3.push({ text: '', margin: [0, 2, 0, 6] });
      }

      content.push({
        table: {
          widths: ['*', '*'],
          body: [row1, row2, row3]
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0.5 : 0),
          vLineWidth: (i: number) => (i === 1 ? 0.5 : 0),
          hLineColor: () => '#000000',
          vLineColor: () => '#000000',
          paddingTop: () => 0,
          paddingBottom: () => 0,
          paddingLeft: () => 4,
          paddingRight: () => 4,
        },
        unbreakable: true
      });
    }
    return this.applyFooterScaling({
      stack: content,
      margin: [0, 0, 0, 0],
      unbreakable: true
    });
  }


  private getBorderLayout(style: 'solid' | 'dotted' | 'dashed', width: number, color: string): any {
    if (style === 'dotted') {
      return {
        hLineWidth: () => width,
        vLineWidth: () => width,
        hLineColor: () => color,
        vLineColor: () => color,
        hLineStyle: () => ({ dash: { length: 2, space: 2 } }),
        vLineStyle: () => ({ dash: { length: 2, space: 2 } }),
      };
    } else if (style === 'dashed') {
      return {
        hLineWidth: () => width,
        vLineWidth: () => width,
        hLineColor: () => color,
        vLineColor: () => color,
        hLineStyle: () => ({ dash: { length: 4, space: 2 } }),
        vLineStyle: () => ({ dash: { length: 4, space: 2 } }),
      };
    } else {
      // solid
      return {
        hLineWidth: () => width,
        vLineWidth: () => width,
        hLineColor: () => color,
        vLineColor: () => color,
      };
    }
  }

  /**
   * Helper: Format currency
   */
  private formatCurrency(amount: number, symbol: string): string {
    const formatted = amount.toFixed(
      this.config.additionalSettings.decimalPlaces,
    );
    return this.config.additionalSettings.currencyPosition === 'before'
      ? `${symbol}${formatted}`
      : `${formatted}${symbol}`;
  }

  /**
   * Helper: Convert number to words (Indian numbering system)
   */
  private numberToWords(num: number): string {
    if (!isFinite(num)) return '';
    const ones = [
      '',
      'One',
      'Two',
      'Three',
      'Four',
      'Five',
      'Six',
      'Seven',
      'Eight',
      'Nine',
    ];
    const tens = [
      '',
      '',
      'Twenty',
      'Thirty',
      'Forty',
      'Fifty',
      'Sixty',
      'Seventy',
      'Eighty',
      'Ninety',
    ];
    const teens = [
      'Ten',
      'Eleven',
      'Twelve',
      'Thirteen',
      'Fourteen',
      'Fifteen',
      'Sixteen',
      'Seventeen',
      'Eighteen',
      'Nineteen',
    ];

    const convertLessThanThousand = (n: number): string => {
      n = Math.floor(n);
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100)
        return (
          tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '')
        );
      const h = Math.floor(n / 100);
      const rem = n % 100;
      return (
        ones[h] +
        ' Hundred' +
        (rem !== 0 ? ' ' + convertLessThanThousand(rem) : '')
      );
    };

    let n = Math.floor(Math.abs(num));
    if (n === 0) return 'Zero';

    const parts: string[] = [];

    const crore = Math.floor(n / 10000000);
    if (crore) {
      parts.push(convertLessThanThousand(crore) + ' Crore');
      n = n % 10000000;
    }

    const lakh = Math.floor(n / 100000);
    if (lakh) {
      parts.push(convertLessThanThousand(lakh) + ' Lakh');
      n = n % 100000;
    }

    const thousand = Math.floor(n / 1000);
    if (thousand) {
      parts.push(convertLessThanThousand(thousand) + ' Thousand');
      n = n % 1000;
    }

    if (n) {
      parts.push(convertLessThanThousand(n));
    }

    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }
}
